import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Single source of truth for locale normalization. Canonical values: "fr" | "en".
// Accepts fr/FR/fr-FR, en/EN/en-US/en-GB, display labels, etc. Returns null when unresolvable.
export function normalizeLanguage(value) {
  if (typeof value !== "string") return null;
  const v = value.trim().toLowerCase();
  if (!v) return null;
  if (v === "fr" || v.startsWith("fr-") || v.startsWith("fr_") || v === "français" || v === "francais" || v === "french") return "fr";
  if (v === "en" || v.startsWith("en-") || v.startsWith("en_") || v === "english" || v === "anglais") return "en";
  return null;
}

function initialLanguage() {
  try {
    const saved = normalizeLanguage(localStorage.getItem("athlynx_lang"));
    if (saved) return saved;
  } catch { /* private mode */ }
  try {
    const nav = normalizeLanguage(typeof navigator !== "undefined" ? navigator.language : null);
    if (nav) return nav;
  } catch { /* no navigator */ }
  return "fr";
}

// Centralized FR/EN dictionary. Interface labels only — never user content.
const MESSAGES = {
  fr: {
    "nav.dashboard": "Dashboard",
    "nav.pipeline": "Pipeline",
    "nav.clubs": "Clubs",
    "nav.calendar": "Calendrier",
    "nav.relances": "À relancer",
    "nav.discussions": "Discussions",
    "nav.performance": "Performance",
    "nav.intelligence": "Athlynx Intelligence",
    "nav.contracts": "Contrats",
    "nav.documents": "Documents",
    "nav.import": "Import",
    "sidebar.agent": "Agent",
    "sidebar.allAthletes": "Tous les athlètes",
    "sidebar.quickView": "Vue rapide",
    "common.save": "Enregistrer",
    "common.cancel": "Annuler",
    "common.close": "Fermer",
    "common.back": "Retour",
    "common.continue": "Continuer",
    "common.search": "Rechercher…",
    "common.loading": "Chargement…",
    "settings.language": "Langue",
    "settings.subtitle": "Gérez votre compte, votre sécurité, vos préférences et vos données.",
    "settings.languageDesc": "Langue de l'interface Athlynx",
    "settings.french": "Français",
    "settings.english": "English",
    "common.add": "Ajouter",
    "common.edit": "Modifier",
    "common.delete": "Supprimer",
    "common.all": "Tous",
    "common.none": "Aucun",
    "common.today": "Aujourd'hui",
    "common.total": "Total",
    "common.import": "Importer",
    "dash.hello": "Bonjour, {name}.",
    "dash.subtitle": "Voici l'état de votre portefeuille et les actions à suivre.",
    "dash.addAthlete": "Ajouter un athlète",
    "dash.emptyPipeline": "Ton pipeline est vide. Ajoute un athlète ou charge la démo pour explorer l'outil.",
    "dash.loadDemo": "Charger la démo",
    "dash.loadingDemo": "Chargement…",
    "dash.confirmSeed": "Charger les données de démo ? Cela remplace les données existantes.",
    "dash.demoLoaded": "Démo chargée",
    "dash.seedError": "Erreur seed",
    "dash.kpiError": "Impossible de charger les indicateurs.",
    "dash.dashError": "Impossible de charger les modules du tableau de bord.",
    "dash.kpi.relancer": "À relancer",
    "dash.kpi.places": "Placés",
    "dash.kpi.discussion": "En discussion",
    "dash.kpi.contacte": "Contacté",
    "dash.kpi.todayCount": "{n} aujourd'hui",
    "dash.kpi.overdueCount": "{n} en retard",
    "dash.kpi.historyBuilding": "Historique en construction",
    "dash.kpi.noSignature": "Pas de signature {range}",
    "dash.kpi.vsPrev": "{delta} vs période précédente",
    "dash.kpi.newExchanges": "{n} nouveaux échanges cette semaine",
    "dash.kpi.interactionsWeek": "{n} interactions cette semaine",
    "dash.kpi.interactionsRange": "{n} interactions {range}",
    "dash.kpi.awaitingReply": "En attente de réponse",
    "dash.range.today": "aujourd'hui", "dash.range.yesterday": "hier",
    "dash.range.7d": "ces 7 jours", "dash.range.30d": "ces 30 jours",
    "dash.range.month": "ce mois-ci", "dash.range.custom": "sur la période",
    "clubs.title": "Clubs",
    "clubs.count": "{n} clubs",
    "clubs.search": "Rechercher un club…",
    "clubs.add": "Club",
    "clubs.empty": "Aucun club.",
    "clubs.contact": "Contact :",
    "clubs.playersSent": "Joueurs envoyés ({n})",
    "clubs.noPlayersSent": "Aucun joueur envoyé.",
    "contracts.title": "Contrats",
    "contracts.subtitle": "Gérez vos contrats, signatures et documents en un seul espace sécurisé.",
    "contracts.new": "Nouveau contrat",
    "contracts.tab.contracts": "Contrats",
    "contracts.tab.renewals": "Renouvellements",
    "contracts.kpi.inSignature": "En signature",
    "contracts.kpi.signed": "Signés",
    "contracts.kpi.drafts": "Brouillons",
    "contracts.search": "Rechercher un contrat, un athlète, un club…",
    "contracts.allStatuses": "Tous les statuts",
    "contracts.emptyNone": "Aucun contrat pour le moment",
    "contracts.emptyNoMatch": "Aucun contrat ne correspond à votre recherche",
    "contracts.emptyHint": "Créez votre premier dossier de contrat : documents, signataires et suivi complet.",
    "contracts.signatures": "Signatures",
    "pipeline.athleteCount": "{n} athlète{s}",
    "pipeline.of": "sur {n}",
    "pipeline.search": "Rechercher un athlète…",
    "pipeline.allClubs": "Tous les clubs",
    "pipeline.filters": "Filtres",
    "pipeline.athlete": "Athlète",
    "pipeline.stageError": "Impossible de modifier l'étape.",
    "pipeline.movedTo": "Déplacé vers",
    "pipeline.confirmPlacement": "Confirmer le placement",
    "pipeline.openDiscussion": "Ouvrir la discussion",
    "pipeline.placementDone": "placement réalisé",
    "pipeline.placementsDone": "placements réalisés",
    "pipeline.seePlacements": "Voir les placements",
    "docs.title": "Documents",
    "docs.subtitle": "Gérez tous les documents de votre portefeuille au même endroit.",
    "docs.add": "Ajouter un document",
    "docs.search": "Rechercher un document, un joueur…",
    "docs.kpi.total": "Total documents",
    "docs.kpi.expired": "Expirés",
    "docs.kpi.expiring": "Expirent bientôt",
    "docs.kpi.valid": "À jour",
    "docs.activeFilter": "Filtre actif",
    "docs.clickToFilter": "Cliquer pour filtrer",
    "status.Repéré": "Repéré", "status.Contacté": "Contacté", "status.En discussion": "En discussion",
    "status.Mandat signé": "Mandat signé", "status.Placé": "Placé",
    "nav.trackedAthletes": "Athlètes suivis",
    "nav.search": "Rechercher…",
    "nav.settings": "Paramètres",
    "nav.logout": "Déconnexion",
    "relances.kicker": "À RELANCER",
    "relances.subtitle": "Relances en attente sur la période sélectionnée.",
    "calendar.subtitle": "Visualisez et gérez tous vos événements et activités.",
    "athlete.tab.resume": "Résumé",
    "athlete.tab.info": "Informations",
    "athlete.tab.career": "Carrière",
    "athlete.tab.notes": "Notes",
    "athlete.tab.docs": "Documents",
    "athlete.tab.activity": "Activité",
    "common.tomorrow": "Demain",
    "common.event": "Événement",
    "range.today": "Aujourd'hui", "range.yesterday": "Hier", "range.7d": "7 derniers jours",
    "range.30d": "30 derniers jours", "range.month": "Mois en cours", "range.custom": "Personnalisé",
    "range.period": "Période",
    "dash.card.activity": "Activité récente",
    "dash.card.seeAllActivity": "Voir toute l'activité",
    "dash.card.seeFullCalendar": "Voir le calendrier complet",
    "dash.card.noEventsDay": "Aucun événement ce jour-là.",
    "dash.card.createEvent": "Créer un événement",
    "dash.card.statusDist": "Répartition des statuts",
    "dash.card.seeAllAthletes": "Voir tous les athlètes",
    "dash.card.athleteUnit": "athlète{s}",
    "dash.card.funnel": "Pipeline par étape",
    "dash.card.funnelEmpty": "Aucun joueur dans le pipeline.",
    "dash.card.upcoming": "Prochains événements",
    "dash.card.seeAllEvents": "Voir tous les événements",
    "dash.card.noEvents": "Aucun événement planifié.",
    "dash.card.expiringDocs": "Documents expirant bientôt",
    "dash.card.seeAllDocs": "Voir tous les documents",
    "dash.card.noExpiringDocs": "Aucun document n'expire dans les 30 prochains jours.",
    "event.call": "Appel", "event.meeting": "Rendez-vous", "event.rdv-club": "RDV club",
    "event.match": "Match", "event.video": "Visio", "event.follow-up": "Relance",
    "event.relance": "Relance", "event.deadline": "Deadline", "event.training": "Entraînement",
    "activity.NEGOTIATION_PROFILE_GENERATED": "Fiche de négociation générée",
    "activity.CAREER_PLAN_EXPORTED": "Plan de carrière exporté en PDF",
    "pipeline.overdueFollowup": "Relance en retard",
    "pipeline.overdueFollowups": "{n} relances en retard",
    "pipeline.noActivity": "Sans activité depuis {n}j",
    "pipeline.followedUp": "Relancé",
  },
  en: {
    "nav.dashboard": "Dashboard",
    "nav.pipeline": "Pipeline",
    "nav.clubs": "Clubs",
    "nav.calendar": "Calendar",
    "nav.relances": "Follow-ups",
    "nav.discussions": "Discussions",
    "nav.performance": "Performance",
    "nav.intelligence": "Athlynx Intelligence",
    "nav.contracts": "Contracts",
    "nav.documents": "Documents",
    "nav.import": "Import",
    "sidebar.agent": "Agent",
    "sidebar.allAthletes": "All athletes",
    "sidebar.quickView": "Quick view",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.back": "Back",
    "common.continue": "Continue",
    "common.search": "Search…",
    "common.loading": "Loading…",
    "settings.language": "Language",
    "settings.subtitle": "Manage your account, security, preferences and data.",
    "settings.languageDesc": "Athlynx interface language",
    "settings.french": "Français",
    "settings.english": "English",
    "common.add": "Add",
    "common.edit": "Edit",
    "common.delete": "Delete",
    "common.all": "All",
    "common.none": "None",
    "common.today": "Today",
    "common.total": "Total",
    "common.import": "Import",
    "dash.hello": "Hello, {name}.",
    "dash.subtitle": "Here's your portfolio status and the actions to follow.",
    "dash.addAthlete": "Add an athlete",
    "dash.emptyPipeline": "Your pipeline is empty. Add an athlete or load the demo to explore the tool.",
    "dash.loadDemo": "Load demo",
    "dash.loadingDemo": "Loading…",
    "dash.confirmSeed": "Load demo data? This replaces existing data.",
    "dash.demoLoaded": "Demo loaded",
    "dash.seedError": "Seed error",
    "dash.kpiError": "Unable to load the indicators.",
    "dash.dashError": "Unable to load the dashboard modules.",
    "dash.kpi.relancer": "Follow-ups",
    "dash.kpi.places": "Placed",
    "dash.kpi.discussion": "In discussion",
    "dash.kpi.contacte": "Contacted",
    "dash.kpi.todayCount": "{n} today",
    "dash.kpi.overdueCount": "{n} overdue",
    "dash.kpi.historyBuilding": "History being built",
    "dash.kpi.noSignature": "No signing {range}",
    "dash.kpi.vsPrev": "{delta} vs previous period",
    "dash.kpi.newExchanges": "{n} new exchanges this week",
    "dash.kpi.interactionsWeek": "{n} interactions this week",
    "dash.kpi.interactionsRange": "{n} interactions {range}",
    "dash.kpi.awaitingReply": "Awaiting reply",
    "dash.range.today": "today", "dash.range.yesterday": "yesterday",
    "dash.range.7d": "these 7 days", "dash.range.30d": "these 30 days",
    "dash.range.month": "this month", "dash.range.custom": "over the period",
    "clubs.title": "Clubs",
    "clubs.count": "{n} clubs",
    "clubs.search": "Search a club…",
    "clubs.add": "Club",
    "clubs.empty": "No clubs.",
    "clubs.contact": "Contact:",
    "clubs.playersSent": "Players sent ({n})",
    "clubs.noPlayersSent": "No players sent.",
    "contracts.title": "Contracts",
    "contracts.subtitle": "Manage your contracts, signatures and documents in one secure workspace.",
    "contracts.new": "New contract",
    "contracts.tab.contracts": "Contracts",
    "contracts.tab.renewals": "Renewals",
    "contracts.kpi.inSignature": "In signature",
    "contracts.kpi.signed": "Signed",
    "contracts.kpi.drafts": "Drafts",
    "contracts.search": "Search a contract, an athlete, a club…",
    "contracts.allStatuses": "All statuses",
    "contracts.emptyNone": "No contracts yet",
    "contracts.emptyNoMatch": "No contract matches your search",
    "contracts.emptyHint": "Create your first contract file: documents, signatories and full tracking.",
    "contracts.signatures": "Signatures",
    "pipeline.athleteCount": "{n} athlete{s}",
    "pipeline.of": "of {n}",
    "pipeline.search": "Search an athlete…",
    "pipeline.allClubs": "All clubs",
    "pipeline.filters": "Filters",
    "pipeline.athlete": "Athlete",
    "pipeline.stageError": "Unable to change the stage.",
    "pipeline.movedTo": "Moved to",
    "pipeline.confirmPlacement": "Confirm placement",
    "pipeline.openDiscussion": "Open discussion",
    "pipeline.placementDone": "placement completed",
    "pipeline.placementsDone": "placements completed",
    "pipeline.seePlacements": "View placements",
    "docs.title": "Documents",
    "docs.subtitle": "Manage all your portfolio documents in one place.",
    "docs.add": "Add a document",
    "docs.search": "Search a document, a player…",
    "docs.kpi.total": "Total documents",
    "docs.kpi.expired": "Expired",
    "docs.kpi.expiring": "Expiring soon",
    "docs.kpi.valid": "Up to date",
    "docs.activeFilter": "Active filter",
    "docs.clickToFilter": "Click to filter",
    "status.Repéré": "Scouted", "status.Contacté": "Contacted", "status.En discussion": "In discussion",
    "status.Mandat signé": "Mandate signed", "status.Placé": "Placed",
    "nav.trackedAthletes": "Tracked athletes",
    "nav.search": "Search…",
    "nav.settings": "Settings",
    "nav.logout": "Log out",
    "relances.kicker": "FOLLOW-UPS",
    "relances.subtitle": "Pending follow-ups over the selected period.",
    "calendar.subtitle": "View and manage all your events and activities.",
    "athlete.tab.resume": "Summary",
    "athlete.tab.info": "Information",
    "athlete.tab.career": "Career",
    "athlete.tab.notes": "Notes",
    "athlete.tab.docs": "Documents",
    "athlete.tab.activity": "Activity",
    "common.tomorrow": "Tomorrow",
    "common.event": "Event",
    "range.today": "Today", "range.yesterday": "Yesterday", "range.7d": "Last 7 days",
    "range.30d": "Last 30 days", "range.month": "Current month", "range.custom": "Custom",
    "range.period": "Period",
    "dash.card.activity": "Recent activity",
    "dash.card.seeAllActivity": "See all activity",
    "dash.card.seeFullCalendar": "See full calendar",
    "dash.card.noEventsDay": "No events on this day.",
    "dash.card.createEvent": "Create an event",
    "dash.card.statusDist": "Status distribution",
    "dash.card.seeAllAthletes": "See all athletes",
    "dash.card.athleteUnit": "athlete{s}",
    "dash.card.funnel": "Pipeline by stage",
    "dash.card.funnelEmpty": "No players in the pipeline.",
    "dash.card.upcoming": "Upcoming events",
    "dash.card.seeAllEvents": "See all events",
    "dash.card.noEvents": "No scheduled events.",
    "dash.card.expiringDocs": "Documents expiring soon",
    "dash.card.seeAllDocs": "See all documents",
    "dash.card.noExpiringDocs": "No documents expire in the next 30 days.",
    "event.call": "Call", "event.meeting": "Meeting", "event.rdv-club": "Club meeting",
    "event.match": "Match", "event.video": "Video call", "event.follow-up": "Follow-up",
    "event.relance": "Follow-up", "event.deadline": "Deadline", "event.training": "Training",
    "activity.NEGOTIATION_PROFILE_GENERATED": "Negotiation sheet generated",
    "activity.CAREER_PLAN_EXPORTED": "Career plan exported as PDF",
    "pipeline.overdueFollowup": "Follow-up overdue",
    "pipeline.overdueFollowups": "{n} follow-ups overdue",
    "pipeline.noActivity": "No activity for {n}d",
    "pipeline.followedUp": "Followed up",
  },
};

const LanguageContext = createContext({ lang: "fr", setLang: () => {}, t: (k) => k });

// Centralized source-string map (gettext-style) for dense surfaces: dialogs, settings bodies, wizards.
const FR_EN = {
  // Stage + Follow-up unification
  "Passer à": "Move to", "Ouvrir le suivi": "Open follow-up",
  "Aucun athlète placé pour le moment.": "No placed athlete yet.",
  "Historique en construction": "History building",
  "Fréquence de suivi": "Follow-up frequency", "Personnalisée": "Custom",
  "Utiliser le délai par défaut": "Use the default delay",
  "Délai par défaut pour": "Default delay for",
  "Fréquence de suivi mise à jour": "Follow-up frequency updated",
  "Délai par défaut rétabli": "Default delay restored",
  "Relance planifiée (fréquence de suivi)": "Scheduled follow-up (frequency)",
  "À jour": "Up to date", "À relancer aujourd'hui": "Follow up today",
  "vs période précédente": "vs previous period",
  "Athlètes entrés dans l'étape": "Athletes who entered the stage",
  "sur la période sélectionnée (historique réel des transitions). Le total actuel de l'étape est visible dans le Pipeline.":
    "over the selected period (real transition history). The current stage total is visible in the Pipeline.",
  "Annuler": "Cancel", "Créer": "Create", "Création…": "Creating…", "Enregistrer": "Save",
  "Enregistrement…": "Saving…", "Supprimer": "Delete", "Modifier": "Edit", "Fermer": "Close",
  "Continuer": "Continue", "Retour": "Back", "Suivant": "Next", "Précédent": "Previous",
  "Erreur": "Error", "Notes": "Notes",  "Type": "Type", "Joueur": "Player",
  "Début": "Start",  "Appel": "Call", "Rendez-vous": "Meeting", "Relance": "Follow-up",
  "(optionnel)": "(optional)", "Sélectionner": "Select", "Sport": "Sport", "Statut": "Status",
  "Nom complet": "Full name", "Âge": "Age", "Téléphone": "Phone", "Email": "Email",
  "Nouvel athlète": "New athlete", "Nouvel événement": "New event",
  "Choisissez un sport puis complétez les champs adaptés.": "Choose a sport, then fill in the adapted fields.",
  "Rechercher un sport…": "Search a sport…", "Rechercher un sport": "Search a sport",
  "Effacer la recherche": "Clear search", "Aucun sport trouvé": "No sport found",
  "Sports précédents": "Previous sports", "Sports suivants": "Next sports", "Choisir un sport": "Choose a sport",
  "Ce qu'il faut selon le sport": "What each sport requires",
  "Les champs clés requis pour chaque sport.": "The key fields required for each sport.",
  "Les champs marqués d'un": "Fields marked with",
  "sont obligatoires pour le sport sélectionné.": "are required for the selected sport.",
  "Les champs du formulaire s'adaptent au sport sélectionné.": "The form fields adapt to the selected sport.",
  "Les ligues se mettent à jour selon le sport sélectionné.": "Leagues update based on the selected sport.",
  "Impossible de charger le catalogue des sports": "Unable to load the sports catalog",
  "Changer de sport effacera les champs spécifiques déjà saisis. Continuer ?": "Changing sport will clear the sport-specific fields already entered. Continue?",
  "Athlète ajouté": "Athlete added", "Erreur lors de l'ajout": "Error while adding",
  "Sélectionner une ligue": "Select a league", "Sélectionner un statut": "Select a status",
  "Champs requis :": "Required fields:",
  "Titre et date requis": "Title and date required", "Événement créé": "Event created",
  "Fichier trop volumineux (max 20 Mo)": "File too large (max 20 MB)",
  "Sélectionnez un joueur": "Select a player", "Sélectionnez un fichier": "Select a file",
  "Document ajouté": "Document added", "Échec de l'upload": "Upload failed",
  "Ajouter un document": "Add a document",
  "Le document sera rattaché au joueur sélectionné.": "The document will be attached to the selected player.",
  "Joueur *": "Player *", "Fichier *": "File *", "Sélectionner un joueur": "Select a player",
  "Retirer le fichier": "Remove file",
  "Cliquer pour choisir un fichier (max 20 Mo)": "Click to choose a file (max 20 MB)",
  "Catégorie": "Category", "Expiration (optionnel)": "Expiration (optional)",
  "Libellé (optionnel)": "Label (optional)", "Ajouter le document": "Add document",
  "En signature": "In signature", "Actifs": "Active", "Expirent bientôt": "Expiring soon",
  "Archivé": "Archived", "En attente de signature": "Pending signature",
  "Partiellement signé": "Partially signed", "Signés": "Signed", "Critiques": "Critical",
  "Échéance ≤ 90 j": "Due ≤ 90 d", "Dossiers ouverts": "Open cases", "Réessayer": "Retry",
  "Impossible de charger les renouvellements": "Unable to load renewals",
  "Vérifiez votre connexion puis réessayez.": "Check your connection and try again.",
  "Feuille de route des échéances": "Deadline roadmap", "Tous les dossiers": "All cases",
  "Rechercher un athlète, un contrat, un club…": "Search an athlete, a contract, a club…",
  "Expirés": "Expired", "À qualifier": "To qualify", "Préparation": "Preparing",
  "Accord de principe": "Agreement in principle", "Non renouvelé": "Not renewed", "Perdu": "Lost",
  "Aucun renouvellement détecté": "No renewal detected",
  "Fin :": "End:",  "Élevée": "High",
  "Document final": "Final document", "À sélectionner": "To select", "Verrouillé": "Locked",
  "configuré(s)": "configured", "Envoi": "Sending", "Invitations envoyées": "Invitations sent",
  "signés": "signed", "Terminé": "Completed",
  "Modifier le signataire": "Edit signer", "Ajouter un signataire": "Add a signer",
  "Chaque partie devant signer le document final.": "Each party required to sign the final document.",
  "Ajouter": "Add", "Signataire ajouté": "Signer added",
  "Signataire mis à jour": "Signer updated",
  "Envoyer pour signature": "Send for signature",
  "Vérifiez les éléments avant l'envoi — le document sera verrouillé.": "Review everything before sending — the document will be locked.",
  "signataire(s)": "signer(s)", "Signature séquentielle": "Sequential signing",
  "Signature parallèle": "Parallel signing", "Expire le": "Expires on",
  "Mode test : aucune invitation réelle ne sera envoyée.": "Test mode: no real invitation will be sent.",
  "Je confirme avoir vérifié la concordance du document final avec les termes négociés (montants, dates, parties, clauses).": "I confirm I verified that the final document matches the negotiated terms (amounts, dates, parties, clauses).",
  "Envoyer les invitations": "Send invitations", "Invitations envoyées.": "Invitations sent.",
  "Impossible de charger la signature": "Unable to load the signature workspace",
  "Aucune procédure de signature": "No signature procedure",
  "Préparez le document final, les signataires, puis envoyez les invitations de signature électronique.": "Prepare the final document and signers, then send the e-signature invitations.",
  "Démarrer la signature": "Start signature",
  "Signature électronique non configurée": "Electronic signature not configured",
  "Vous pouvez préparer le document et les signataires. L'envoi des invitations nécessite la clé API Yousign.": "You can prepare the document and signers. Sending invitations requires the Yousign API key.",
  "Mode test actif — les signatures sont simulées, aucune invitation réelle n'est envoyée.": "Test mode active — signatures are simulated, no real invitation is sent.",
  "Statut de signature": "Signature status", "sur": "of", "Envoyé le": "Sent on",
  "Critique": "Critical", "Nouvelle procédure": "New procedure",
  "En attente": "Pending", "Mode test": "Test mode", "Non configuré": "Not configured",
  "Suivi opérationnel": "Operational tracking", "Sous contrôle": "On track",
  "Attention requise": "Attention required", "Analyser le contrat": "Analyze the contract",
  "Temps restant :": "Time remaining:", "an(s)": "yr", "mois": "mo",
  "Échéances à venir": "Upcoming deadlines",
  "Obligations ouvertes": "Open obligations", "Renouvellement": "Renewal",
  "Préparation en cours": "Preparation in progress", 
  "Obligations": "Obligations", "Options": "Options", "Avenants": "Amendments",
  "Aucune échéance contractuelle à venir.": "No upcoming contractual deadline.",
  "Aucune obligation suivie pour ce contrat.": "No obligation tracked for this contract.",
  "Aucune option contractuelle suivie.": "No contractual option tracked.",
  "Aucun avenant enregistré.": "No amendment recorded.",
  "En retard": "Overdue", "Aujourd'hui": "Today", "Décision requise": "Decision required", "Disponible": "Available", "Complétée": "Completed", "Exercée": "Exercised", "Non exercée": "Not exercised",
  "Levée": "Waived", "Annulée": "Cancelled", "Contestée": "Disputed",
  "Source :": "Source:", "Ajouter au calendrier": "Add to calendar", "Ajouté au calendrier": "Added to calendar",
  "Marquer complétée": "Mark completed", "Échéance complétée": "Deadline completed",
  "Obligation complétée": "Obligation completed", "Détenteur :": "Holder:",
  "Exercer": "Exercise", "Ne pas exercer": "Do not exercise",
  "Option exercée": "Option exercised", "Option non exercée": "Option not exercised",
  "Valider l'avenant": "Validate amendment", "Avenant validé — termes opérationnels mis à jour": "Amendment validated — operational terms updated",
  "Nouvelle échéance": "New deadline", "Nouvelle obligation": "New obligation",
  "Nouvelle option": "New option", "Nouvel avenant": "New amendment",
  "Donnée contractuelle validée par vous.": "Contract data validated by you.",
  "Partie obligée": "Obligated party",
  "Détenteur": "Holder", "Terme modifié": "Modified term", "Nouvelle valeur": "New value",
  "Date d'effet": "Effective date", "Clause source (optionnel)": "Source clause (optional)", "Créé": "Created",
  "Analyse Athlynx Intelligence": "Athlynx Intelligence analysis",
  "Suggestions extraites du document — rien n'est créé sans votre validation.": "Suggestions extracted from the document — nothing is created without your validation.",
  "Valider la sélection": "Validate selection",
  "Sélectionnez au moins un élément": "Select at least one item",
  "élément(s) créé(s)": "item(s) created", "Analyse indisponible.": "Analysis unavailable.",
  "Option à exercer": "Option to exercise", "Prime": "Bonus", "Paiement": "Payment",
  "Enregistrement": "Registration", "Revue": "Review", 
  
  "Terminé le": "Completed on", "Fournisseur": "Provider",
  "Sélectionnez le document final et ajoutez les signataires (email valide) pour pouvoir envoyer.": "Select the final document and add signers (valid email) before sending.",
  "Télécharger le contrat signé": "Download signed contract",
  "Confirmer l'annulation": "Confirm cancellation", "Annuler la procédure": "Cancel procedure", "Procédure annulée": "Procedure cancelled",
  "Validée": "Validated", "À compléter": "To complete", "Termes vs document": "Terms vs document",
  "Concordance": "Match", "Vérification requise": "Review required",
  "Athlynx Intelligence assiste la comparaison — la validation humaine reste requise.": "Athlynx Intelligence assists the comparison — human validation remains required.",
  "Historique récent": "Recent history", "Sélectionné": "Selected",
  "Sélectionné le": "Selected on", "Voir": "View", "Le document soumis est immuable pour cette procédure. Toute modification du contrat nécessite d'annuler et de relancer une procédure.": "The submitted document is immutable for this procedure. Any contract change requires cancelling and restarting a procedure.",
  "Sélectionnez la version finale à faire signer :": "Select the final version to be signed:",
  "Aucun document — ajoutez le contrat final dans l'onglet Documents.": "No document — add the final contract in the Documents tab.",
  "Configuration de l'envoi": "Sending configuration",
  "Chaque signataire signe à son tour, dans l'ordre.": "Each signer signs in turn, in order.",
  "Date limite de signature (optionnel)": "Signature deadline (optional)",
  "Message d'invitation (optionnel)": "Invitation message (optional)",
  "Message affiché aux signataires…": "Message shown to signers…",
  "Aucun signataire. Ajoutez les parties devant signer (email requis).": "No signer yet. Add the parties required to sign (email required).",
  "Signé le": "Signed on", "Refusé le": "Declined on", "Invitation envoyée": "Invitation sent",
  "Dernière relance": "Last reminder", "Relancer": "Remind", "Relance envoyée à": "Reminder sent to",
  "Simuler : signé": "Simulate: signed", "refusé": "declined",
  "Document final sélectionné": "Final document selected",
  "Prêt à envoyer": "Ready to send", 
  "Document consulté": "Document viewed",
  "Fédération": "Federation", "Autre partie": "Other party", "Avocat": "Lawyer",
  "Représentant légal": "Legal representative", "Club": "Club", "Sponsor": "Sponsor",
  "personne(s) sur": "of", "ont signé.": "have signed.",
  "Prend effet le": "Effective on", "Voir le contrat signé": "View signed contract",
  "Toutes les signatures ont été obtenues.": "All signatures have been collected.",
  "Signature en préparation": "Signature in preparation",
  "Prêt à envoyer pour signature.": "Ready to send for signature.",
  "Document final et signataires à configurer.": "Final document and signers to configure.",
  "Finaliser": "Finalise", "Voir la signature": "View signature",
  "Relancer un signataire": "Remind a signer",
  "Signature interrompue": "Signature interrupted",
  "La dernière procédure n'a pas abouti.": "The last procedure did not complete.",
  "Aucun dossier ne correspond à vos filtres": "No case matches your filters",
  "Les contrats signés ou actifs arrivant à échéance dans les 365 prochains jours sont détectés automatiquement et apparaissent ici.": "Signed or active contracts expiring within the next 365 days are detected automatically and appear here.",
  // Common sport catalog labels (dynamic from backend)
  "Poste": "Position", "Pied fort": "Strong foot", "Nationalité": "Nationality",
  "Taille (cm)": "Height (cm)", "Poids (kg)": "Weight (kg)", "Club actuel": "Current club",
  "Ligue": "League", "Main forte": "Strong hand", "Position": "Position",
  "Gauche": "Left", "Droit": "Right", "Droite": "Right", "Ambidextre": "Ambidextrous",
  "CV sportif": "Sports CV", "Statistiques": "Statistics", "Pièce d'identité": "ID document",
  "Contrat": "Contract", "Rapport scout": "Scout report", "Fiche de négociation": "Negotiation sheet",
  "Plan de carrière": "Career plan", 
  "Document supprimé": "Document deleted", "Erreur lors de la suppression": "Error while deleting",
  "Supprimer le document": "Delete document", "Supprimer ce document ?": "Delete this document?",
  "sera définitivement supprimé. Cette action est irréversible.": "will be permanently deleted. This action is irreversible.",
  // Settings bodies
   "Actif": "Active", "Paiement en retard": "Payment overdue",
  "Annulé": "Canceled", "Expiré": "Expired", "Agent sportif": "Sports agent",
  "Non renseigné": "Not provided",  
   "Connexion": "Sign-in", "Email / mot de passe": "Email / password",
  "Sécurité": "Security", "Mot de passe, double authentification": "Password, two-factor authentication",
   "modifié le": "changed on", "défini": "set", "connexion Google": "Google sign-in",
  "Sessions & appareils": "Sessions & devices", "Appareils connectés à votre compte": "Devices connected to your account",
  "session": "session", "active": "active", "Abonnement": "Subscription",
  "Plan, facturation et paiement": "Plan, billing and payment",
  "Données & confidentialité": "Data & privacy", "Export, confidentialité, suppression": "Export, privacy, deletion",
  "Téléchargez vos données · gérez votre compte": "Download your data · manage your account",
  "Activité de sécurité récente": "Recent security activity",
  "Impossible de charger les sessions": "Unable to load sessions", "Appareil déconnecté": "Device signed out",
  "appareil(s) déconnecté(s)": "device(s) signed out",
  "Consultez et gérez les appareils connectés à votre compte.": "View and manage the devices connected to your account.",
  "Déconnecter tous les autres": "Sign out all others", "Aucune session active.": "No active sessions.",
  "Appareil": "Device", "Session actuelle": "Current session", "maintenant": "now", "Déconnecter": "Sign out",
  "Les informations d'appareil et de navigateur sont détectées à la connexion.": "Device and browser information is detected at sign-in.",
  "Les mots de passe ne correspondent pas": "Passwords do not match",
  "Le nouveau mot de passe est trop faible": "The new password is too weak",
  "Mot de passe modifié.": "Password changed.", "autre(s) session(s) déconnectée(s).": "other session(s) signed out.",
  "Protégez votre compte et gérez vos méthodes d'authentification.": "Protect your account and manage your authentication methods.",
  "Sécurité du compte": "Account security", "Dernière modification :": "Last changed:", "Défini": "Set",
  "Connexion Google (aucun mot de passe local)": "Google sign-in (no local password)",
  "Authentification à deux facteurs": "Two-factor authentication", "Activée": "Enabled", "Non activée": "Not enabled",
  "À configurer": "To configure", "Sessions actives": "Active sessions", "appareil": "device", "Gérer": "Manage",
  "Changer le mot de passe": "Change password",
  "Après modification, tous vos autres appareils seront déconnectés.": "After the change, all your other devices will be signed out.",
   "Nouveau mot de passe": "New password",
  "8 caractères min.": "8 characters min.", "Majuscule + minuscule": "Uppercase + lowercase", 
  "Confirmer le nouveau mot de passe": "Confirm the new password",
  "Modification…": "Changing…", "Modifier le mot de passe": "Change password",
  "Votre compte utilise la connexion Google. Aucun mot de passe Athlynx n'est défini — votre sécurité est gérée par Google.": "Your account uses Google sign-in. No Athlynx password is set — your security is managed by Google.",
  // Career builder chrome + labels
  "Identité": "Identity", "Vision": "Vision", "Feuille de route": "Roadmap", "Objectifs": "Objectives",
  "Readiness": "Readiness", "Page finale": "Final page",
  "Complétion du document": "Document completion",
  "Erreur d'enregistrement": "Save error", "Enregistré automatiquement": "Saved automatically",
  "Encore recommandé :": "Still recommended:",
  "Athlète": "Athlete",  "Moyenne": "Medium", 
  "Nom": "Name", "Championnat / niveau": "League / level", "Pied / main fort(e)": "Foot / strong hand",
  "Taille": "Height", "Poids": "Weight", "Langues": "Languages",
  "Profil athlète": "Athlete profile", "URL personnalisée": "Custom URL",
  "Atteint": "Achieved", "En cours": "In progress", "À venir": "Upcoming",
  "À risque": "At risk", "Retardé": "Delayed",
  // Onboarding
  "Mode démo activé": "Demo mode enabled", "Bienvenue sur Athlynx": "Welcome to Athlynx",
  "Erreur onboarding": "Onboarding error",
  "Votre nouveau cockpit pour suivre joueurs, clubs et relances en un seul endroit.": "Your new cockpit to track players, clubs and follow-ups in one place.",
  "Profitez de": "Enjoy a", "14 jours d'essai": "14-day trial", "sans carte bancaire.": "with no credit card.",
  "Langue de l'interface": "Interface language",
  "Découvrir Athlynx avec des données de démo ?": "Discover Athlynx with demo data?",
  "Vos vraies données restent protégées. Vous pouvez désactiver le mode démo à tout moment.": "Your real data stays protected. You can disable demo mode at any time.",
  "Activer le mode démo": "Enable demo mode", "RECOMMANDÉ": "RECOMMENDED",
  "13 joueurs, 7 clubs, interactions et rappels prêts à explorer": "13 players, 7 clubs, interactions and reminders ready to explore",
  "Démarrer avec un compte vide": "Start with an empty account",
  "Importez vos joueurs ou ajoutez-les manuellement": "Import your players or add them manually",
  "Tout est prêt !": "All set!",
  "On charge vos données de démo et on vous redirige vers le dashboard.": "We're loading your demo data and redirecting you to the dashboard.",
  "Vous arrivez sur un dashboard vierge. Importez vos joueurs ou ajoutez-en un pour commencer.": "You'll land on an empty dashboard. Import your players or add one to get started.",
  "Commencer": "Get started",
  "Bientôt": "Coming soon", "Offre à activer": "Plan to activate",
  "Voir mon offre": "View my plan", "Aucune activité récente.": "No recent activity.",
  "Pied dominant": "Dominant foot", "Main dominante": "Dominant hand", "Date de naissance": "Date of birth",
  "Matchs joués": "Matches played", "Buts": "Goals", "Passes décisives": "Assists",
  "Minutes jouées": "Minutes played", "Titularisations": "Starts", "Cartons": "Cards",
  "Arrêts": "Saves", "Points": "Points", "Rebonds": "Rebounds", "Interceptions": "Interceptions",
  "Essais": "Tries", "Plaquages": "Tackles", "Mètres gagnés": "Meters gained",
  "Vitesse max (km/h)": "Top speed (km/h)", "Titres": "Titles", "Classement": "Ranking",
  "Aces": "Aces", "Blocs": "Blocks", "Sélections": "Caps",
  // Account panel
  "Compte": "Account", "Gérez vos informations personnelles et vos préférences de connexion.": "Manage your personal information and sign-in preferences.",
  "Informations personnelles": "Personal information", "Prénom": "First name",
  "Email professionnel": "Professional email", "Changer": "Change", "Langue préférée": "Preferred language",
  "Prénom et nom requis": "First and last name required", "Profil mis à jour": "Profile updated",
  "Code envoyé à la nouvelle adresse": "Code sent to the new address",
  "Adresse email mise à jour": "Email address updated", "Changer l'adresse email": "Change email address",
  "Un code de vérification à 6 chiffres sera envoyé à la nouvelle adresse. Votre email actuel reste actif tant que la vérification n'est pas terminée.": "A 6-digit verification code will be sent to the new address. Your current email stays active until verification is complete.",
  "Saisissez le code envoyé à": "Enter the code sent to",
  "Nouvelle adresse email": "New email address", "Envoyer le code de vérification": "Send verification code",
  "Vérification…": "Verifying…", "Vérifier et mettre à jour": "Verify and update",
  "Renvoyer le code dans": "Resend the code in", "Renvoyer le code": "Resend the code",
  // Data & privacy / billing / integrations / preferences
  "Compte supprimé.": "Account deleted.", "Compte désactivé.": "Account deactivated.",
  "Supprimer définitivement le compte": "Permanently delete the account", "Désactiver le compte": "Deactivate the account",
  "Cette action entraînera la suppression définitive de votre compte Athlynx et des données associées, conformément aux règles de conservation applicables. Vos sessions seront immédiatement révoquées.": "This action will permanently delete your Athlynx account and associated data, in accordance with the applicable retention rules. Your sessions will be revoked immediately.",
  "Votre accès sera suspendu et tous vos appareils déconnectés. Vos données sont conservées : reconnectez-vous à tout moment pour réactiver votre compte.": "Your access will be suspended and all your devices signed out. Your data is kept: sign back in at any time to reactivate your account.",
  "Saisissez «": "Type “", "» pour confirmer": "” to confirm",
  "En cours…": "In progress…", "Supprimer mon compte": "Delete my account", "Désactiver mon compte": "Deactivate my account",
  "Export téléchargé": "Export downloaded", "Export impossible": "Export failed",
  "Contrôlez vos données et gérez votre compte, dans une logique RGPD.": "Control your data and manage your account, GDPR-style.",
  "Mes données": "My data",
  "Téléchargez une copie de vos données Athlynx : profil, athlètes, contrats, interactions, journal d'activité. Aucun mot de passe ni secret n'est inclus.": "Download a copy of your Athlynx data: profile, athletes, contracts, interactions, activity log. No passwords or secrets are included.",
  "Export…": "Exporting…", "Exporter en JSON": "Export as JSON", "Exporter en CSV (ZIP)": "Export as CSV (ZIP)",
  "Confidentialité": "Privacy",
  "Vos données sont utilisées uniquement pour le fonctionnement de votre espace Athlynx. Elles ne sont ni revendues ni utilisées pour entraîner des modèles d'IA publics.": "Your data is used only to run your Athlynx workspace. It is never sold or used to train public AI models.",
  "Zone sensible": "Danger zone",
  "Accès suspendu, données conservées. Réactivation en vous reconnectant.": "Access suspended, data kept. Reactivate by signing back in.",
  "Désactiver": "Deactivate", "Supprimer le compte": "Delete the account",
  "Suppression définitive avec purge différée conforme aux obligations légales.": "Permanent deletion with deferred purge in line with legal obligations.",
  "Abonnement & facturation": "Subscription & billing",
  "Votre plan, votre statut et la gestion de votre paiement.": "Your plan, status and payment management.",
  "Abonnement actuel": "Current subscription", "Fin de l'essai": "Trial ends", "Prochaine échéance": "Next renewal",
  "Résiliation": "Cancellation", "Programmée à la fin de la période": "Scheduled at the end of the period",
   "Enregistré via Stripe": "Saved via Stripe", "Aucun": "None",
   "Gérer mon abonnement": "Manage my subscription", "Voir les plans": "View plans",
  "Intégrations": "Integrations", "Services connectés à votre espace Athlynx.": "Services connected to your Athlynx workspace.",
  "Connexion à votre compte Athlynx via Google.": "Sign in to your Athlynx account with Google.",
  "Paiement sécurisé de votre abonnement Athlynx.": "Secure payment for your Athlynx subscription.",
  "Alertes de sécurité et codes de vérification par email.": "Security alerts and verification codes by email.",
  "Synchronisation de votre calendrier Athlynx.": "Sync of your Athlynx calendar.",
  "Signature électronique certifiée de vos contrats.": "Certified electronic signature for your contracts.",
  "Bientôt disponible": "Coming soon", "Connecté": "Connected", "Non connecté": "Not connected",
  "Importez et exportez vos données Athlynx.": "Import and export your Athlynx data.",
  "Import de données": "Data import",
  "Importez vos athlètes par CSV ou grâce à l'import assisté par IA.": "Import your athletes via CSV or with AI-assisted import.",
  "Ouvrir l'import": "Open import", "Export de données": "Data export",
  "Téléchargez l'ensemble de vos données depuis Données & confidentialité.": "Download all your data from Data & privacy.",
  "Exporter mes données": "Export my data",
  "Préférence enregistrée": "Preference saved", "Préférences": "Preferences",
  "Personnalisez l'apparence et le comportement d'Athlynx.": "Customize the look and behavior of Athlynx.",
  "Apparence": "Appearance", "Thème": "Theme", "Actuellement :": "Currently:", "Sombre": "Dark", "Clair": "Light",
  "Passer en mode": "Switch to", "clair": "light mode", "sombre": "dark mode",
  "Formats & navigation": "Formats & navigation", "Format de date": "Date format", "Format d'heure": "Time format",
  "Page d'accueil": "Landing page", "Athlètes": "Athletes", "Calendrier": "Calendar", "Contrats": "Contracts",
  "JJ/MM/AAAA": "DD/MM/YYYY", "MM/JJ/AAAA": "MM/DD/YYYY",
  // Demo mode card
  "Mode démo désactivé — vraies données préservées": "Demo mode disabled — real data preserved",
  "Données démo réinitialisées": "Demo data reset", "Une erreur est survenue": "An error occurred",
  "Données de démo": "Demo data", "Activé": "Enabled", "Désactivé": "Disabled", "Mode démo actif": "Demo mode active",
  "Reset démo": "Reset demo", "Recharger les données démo": "Reload the demo data",
  "Quitter le mode démo": "Exit demo mode", "Supprimer toutes les données démo": "Delete all demo data",
  "Mode démo désactivé": "Demo mode disabled",
  "Vos vraies données et les données de démo coexistent.": "Your real data and the demo data coexist.",
  "Vous pouvez tester librement sans impacter vos données réelles.": "You can test freely without affecting your real data.",
  "Activez pour remplir votre compte avec ~13 joueurs, 7 clubs et un agenda réaliste. Idéal pour tester la plateforme ou présenter à un prospect.": "Enable to fill your account with ~13 players, 7 clubs and a realistic agenda. Ideal for testing the platform or demoing to a prospect.",
  "Seules les données de démo seront supprimées.": "Only the demo data will be deleted.",
  "Vos vraies données sont 100 % sécurisées.": "Your real data is 100% safe.",
  "Activer le mode démo ?": "Enable demo mode?", "Quitter le mode démo ?": "Exit demo mode?",
  "Réinitialiser les données démo ?": "Reset the demo data?",
  "Cela ajoutera ~13 joueurs, 7 clubs et un agenda fictif à votre compte. Vos vraies données ne seront pas modifiées.": "This will add ~13 players, 7 clubs and a fictional agenda to your account. Your real data will not be modified.",
  "Toutes les données démo seront supprimées. Vos vraies données restent intactes.": "All demo data will be deleted. Your real data remains intact.",
  "Les données démo actuelles seront remplacées par un nouveau jeu propre. Vos vraies données restent intactes.": "The current demo data will be replaced with a fresh clean set. Your real data remains intact.",
  "Activer": "Enable", "Quitter": "Exit", "Réinitialiser": "Reset",
  // Public auth pages (Login / Register / Forgot / Reset)
  "Plateforme de gestion sportive": "Sports management platform",
  // Sidebar IA refactor
   "Ressources": "Resources", "Analyse": "Analytics",
  "Vue pipeline": "Pipeline view", "Rapports": "Reports",
  "Repérés": "Scouted", "Prises de contact": "Outreach", "Mandats signés": "Signed mandates",
   "À relancer": "Follow-ups",
  "Réduire la barre latérale": "Collapse the sidebar", "Développer la barre latérale": "Expand the sidebar",
  "Ajouter manuellement": "Add manually", "Importer des athlètes": "Import athletes",
  "Espace contrats": "Contracts workspace",
  // Phase 1 localization audit — prospecting / discussions / mandates / shared
  "1er contact": "1st contact", "Accord mutuel": "Mutual agreement", "Action planifiée": "Action scheduled", "Action réalisée": "Action completed",
  "Actions de la discussion": "Discussion actions", "Ajout manuel": "Added manually", "Ajouter un club cible": "Add a target club",
  "Ajouter un interlocuteur": "Add a contact", "Ajouter un lien": "Add a link", "Ajouter un représentant légal": "Add a legal guardian",
  "Ajouter un échange": "Log an interaction", "Ajouter une note": "Add a note", "Ajouter une note sur ce client…": "Add a note about this client…",
  "Ajouter une note sur ce prospect…": "Add a note about this prospect…", "Ajouter une note sur cet athlète…": "Add a note about this athlete…",
  "Ajoutez d'abord un club cible.": "Add a target club first.", "Analyse des relances": "Follow-up analytics",
  "Attentes de l'athlète": "Athlete expectations", "Au calendrier": "On the calendar", "Aucun athlète dans cet état.": "No athletes in this state.",
  "Aucun autre interlocuteur ajouté.": "No other contacts added.", "Aucun client ne correspond à ces filtres.": "No clients match these filters.",
  "Aucun client sous mandat pour le moment.": "No clients under mandate yet.", "Aucun club ciblé pour le moment.": "No target clubs yet.",
  "Aucun interlocuteur renseigné.": "No contacts recorded.", "Aucun lien ajouté.": "No links added.",
  "Aucun mandat actif. Créez un contrat de type « Mandat de représentation » depuis Contrats.": "No active mandate. Create a “Representation agreement” contract from Contracts.",
  "Aucun mandat terminé.": "No ended mandates.", "Aucun point d'attention enregistré.": "No concerns recorded.",
  "Aucun prochain match renseigné": "No upcoming match recorded", "Aucun prospect ne correspond à ces critères.": "No prospects match these criteria.",
  "Aucun représentant légal renseigné.": "No legal guardian recorded.", "Aucun résumé rédigé pour cette discussion.": "No summary written for this discussion.",
  "Aucun échange": "No interactions", "Aucun échange enregistré": "No interactions logged", "Aucun échange enregistré.": "No interactions logged.",
  "Aucun événement dans l'historique.": "No events in the history.", "Aucune action en attente.": "No pending actions.",
  "Aucune attente renseignée.": "No expectations recorded.", "Aucune condition renseignée.": "No terms recorded.",
  "Aucune coordonnée disponible pour ce destinataire.": "No contact details available for this recipient.",
  "Aucune discussion dans cette vue.": "No discussions in this view.", "Aucune note pour cette discussion.": "No notes for this discussion.",
  "Aucune note pour le moment.": "No notes yet.", "Aucune offre reçue.": "No offers received.",
  "Aucune prochaine action planifiée.": "No next action scheduled.", "Aucune relance complétée sur cette période.": "No follow-ups completed in this period.",
  "Automatique : J+5": "Automatic: D+5", "Autres contraintes": "Other constraints", "Autres primes": "Other bonuses", "Avis": "Feedback",
  "Avis athlète": "Athlete feedback", "Avis enregistré": "Feedback saved", "Avis sur l'offre": "Feedback on the offer",
  "Blocage levé": "Blocker cleared", "Canal": "Channel", "Canal : tous": "Channel: all", "Canal non défini": "Channel not set",
  "Centralisez vos prospects et préparez le bon premier contact.": "Centralize your prospects and prepare the right first contact.",
  "Ces conditions restent des éléments de discussion — aucun contrat n'est créé.": "These terms remain discussion points — no contract is created.",
  "Choisir…": "Choose…", "Choisissez une issue": "Choose an outcome", "Clauses": "Clauses", "Client": "Client",
  "Club / Athlète…": "Club / Athlete…", "Club ajouté aux cibles": "Club added to targets", "Club retiré": "Club removed",
  "Clubs approchés": "Clubs approached", "Clôturée": "Closed", "Commission": "Commission", "Commission payée par": "Commission paid by",
  "Comparaison factuelle des offres actives — aucune recommandation automatique.": "Factual comparison of active offers — no automatic recommendation.",
  "Comparer les offres": "Compare offers", "Compétitions": "Competitions", "Compétitions visées": "Target competitions",
  "Conditions en discussion — distinctes du contrat final.": "Terms under discussion — separate from the final contract.",
  "Conditions enregistrées": "Terms saved", "Conditions envisagées": "Proposed terms", "Confirmer l'annulation ?": "Confirm cancellation?",
  "Confirmer la fin du mandat": "Confirm end of mandate", "Confirmer la relance": "Confirm follow-up", "Confirmer le mandat": "Confirm mandate",
  "Contact réactivé": "Contact reactivated", "Contexte": "Context", "Contexte (pourquoi ce joueur ?)": "Context (why this athlete?)",
  "Contraintes": "Constraints", "Contraintes familiales": "Family constraints", "Contrat expiré": "Contract expired",
  "Copier le message": "Copy message", "Court terme": "Short term",  "Dashboard": "Dashboard", "Date": "Date",
  "Date d'effet (optionnel)": "Effective date (optional)", "Date d'effet envisagée": "Planned effective date", "Date de l'offre": "Offer date",
  "Date requise": "Date required", "Demain": "Tomorrow", "Depuis le": "Since", "Dernier club": "Previous club",
  "Discussion depuis": "In discussion since", "Discussion introuvable": "Discussion not found", "Discussion marquée bloquée": "Discussion marked blocked",
  "Discussions actives": "Active discussions", "Document du mandat non ajouté.": "Mandate document not added.",
  "Données insuffisantes": "Insufficient data", "Dossier de renouvellement prêt": "Renewal case ready", "Durée (mois)": "Duration (months)",
  "Durée de contrat": "Contract duration", "Durée de contrat souhaitée": "Desired contract duration", "Début de la discussion": "Discussion started",
  "Définir le projet de carrière": "Define the career plan", "Déjà au calendrier": "Already on the calendar", "Démarche enregistrée": "Outreach logged",
  "Détail": "Details", "Détail de l'athlète sélectionné": "Selected athlete details", "Détail de la source": "Source details",
  "Email (optionnel)": "Email (optional)", "En veille jusqu'au": "Snoozed until",
  "Enregistrement manuel — aucun message n'est envoyé automatiquement par Athlynx.": "Manual log — Athlynx does not send any message automatically.",
  "Enregistrer la démarche": "Save outreach", "Enregistrer la note": "Save note", 
  "Enregistrer une réponse": "Log a reply", "Erreur de chargement": "Loading error", "Erreur lors de la confirmation": "Confirmation failed",
  "Espace de travail du client sélectionné": "Selected client workspace", "Ex. : 2 ans (à confirmer)": "E.g. 2 years (to confirm)",
  "Ex. : Préparer le rendez-vous avec la famille": "E.g. Prepare the meeting with the family", "Ex. : Représentation": "E.g. Representation",
  "Ex. : Représentation complète": "E.g. Full representation", "Ex. : Souhaite comparer plusieurs propositions": "E.g. Wants to compare several offers",
  "Ex. : À discuter": "E.g. To discuss", "Ex. : Évoluer en Ligue 2 d'ici 2 saisons": "E.g. Play in Ligue 2 within 2 seasons",
  "Exclusivité": "Exclusivity", "Exclusivité (optionnel)": "Exclusivity (optional)", "Expiration": "Expiry",
  "Exporter la sélection": "Export selection", "Famille": "Family",  "Fin de contrat : tous": "Contract end: all",
  "Fin de contrat la plus proche": "Earliest contract end", "Fin de mandat": "Mandate end", "Fin du mandat": "End of mandate",
  "Grand nombre = athlètes actuellement": "Large number = athletes currently", "Heure": "Time", "Hier": "Yesterday",
  "Import": "Import", "Import / Export": "Import / Export", "Importer": "Import",
  "Impossible de charger les discussions": "Unable to load discussions", "Interlocuteur ajouté": "Contact added",
  "Interlocuteur déjà présent": "Contact already added", "Interlocuteur supprimé": "Contact removed", "Interlocuteurs": "Contacts",
  "Intéressé": "Interested", "Issue": "Outcome", "Jalon franchi": "Milestone reached", "Jalons franchis": "Milestones reached",
  "L'athlète n'est pas supprimé : notes, historique et interlocuteurs sont conservés.": "The athlete is not deleted: notes, history and contacts are kept.",
  "L'athlète passera de « En discussion » à « Mandat signé » dans la Pipeline. La discussion sera clôturée et son historique conservé.": "The athlete will move from “In discussion” to “Signed mandate” in the Pipeline. The discussion will be closed and its history kept.",
  "L'athlète reste « Sans réponse » pendant la veille, puis repasse automatiquement en attente avec un compteur réinitialisé.": "The athlete stays “No reply” while snoozed, then automatically returns to pending with a reset counter.",
  "La prochaine relance sera replanifiée automatiquement (J+5).": "The next follow-up will be rescheduled automatically (D+5).",
  "La période ne s'applique qu'aux statistiques ci-dessous — jamais aux listes opérationnelles.": "The period only applies to the statistics below — never to operational lists.",
  "La réactivation démarre un nouveau mandat — l'athlète redeviendra actif une fois le nouveau mandat signé.": "Reactivation starts a new mandate — the athlete becomes active again once the new mandate is signed.",
  "La séquence est épuisée. Cette relance réinitialise un nouveau cycle de contact.": "The sequence is exhausted. This follow-up resets a new contact cycle.",
  "Le mandat sera conservé dans l'historique. L'athlète quittera le pipeline actif et restera visible dans l'onglet « Terminés ».": "The mandate will be kept in the history. The athlete will leave the active pipeline and remain visible in the “Ended” tab.",
  "Le projet de carrière n'a pas encore été défini.": "The career plan has not been defined yet.",
  "Les athlètes passés « En discussion » dans la Pipeline apparaîtront ici.": "Athletes moved to “In discussion” in the Pipeline will appear here.",
  "Lever le blocage": "Clear the blocker", "Libellé (ex. : Vidéo highlights)": "Label (e.g. Highlight video)",
  "Libellé et échéance requis": "Label and due date required", "Lien ajouté": "Link added", "Lien supprimé": "Link removed",
  "Liens utiles": "Useful links", "Lieu / stade": "Venue / stadium",
  "Lignes secondaires et courbe = entrées dans l'étape sur la période sélectionnée.": "Secondary lines and curve = entries into the stage over the selected period.",
  "Ligue 2 ou National": "Ligue 2 or National", "Majeurs": "Adults", "Mandat": "Mandate", "Mandat confirmé": "Mandate confirmed",
  "Mandat confirmé — l'athlète passe en « Mandat signé »": "Mandate confirmed — the athlete moves to “Signed mandate”",
  "Mandat déjà confirmé": "Mandate already confirmed", "Mandat exclusif": "Exclusive mandate", "Mandat mis à jour": "Mandate updated",
  "Mandat terminé": "Mandate ended", "Mandat terminé — client archivé dans « Terminés »": "Mandate ended — client archived under “Ended”",
  "Mandats précédents": "Previous mandates", "Marquer comme bloquée": "Mark as blocked", "Marquer comme faite": "Mark as done",
  "Marquer comme franchi": "Mark as reached", "Marquer comme réalisée": "Mark as completed", "Marquer comme traité": "Mark as handled",
  "Masquer": "Hide", "Masquer les écartés": "Hide dismissed", "Match / événement (optionnel)": "Match / event (optional)",
  "Message / résumé de la démarche": "Message / outreach summary", "Message copié": "Message copied", "Message envoyé": "Message sent",
  "Mettre en veille": "Snooze", "Mineur": "Minor", "Mineur — le représentant légal est le destinataire par défaut": "Minor — the legal guardian is the default recipient",
  "Mineurs": "Minors", "Mis en veille — un nouveau cycle s'ouvrira à l'échéance": "Snoozed — a new cycle will start when due",
  "Mis à jour": "Updated", "Mobilité": "Mobility", "Mobilité / logement": "Mobility / housing", "Modifier la date": "Edit date",
  "Modifier la situation": "Edit situation", "Modifier la source": "Edit source", "Modifier le mandat": "Edit mandate", "Motif": "Reason",
  "Motif (requis)": "Reason (required)", "Motif du blocage (optionnel)": "Blocker reason (optional)", "Motif non renseigné": "No reason given",
  "Moyen terme": "Mid term", "Niveau : tous": "Level: all", "Niveau visé": "Target level", "Nom complet (requis)": "Full name (required)",
  "Nom du club": "Club name", "Nom du club…": "Club name…", "Nombre d'offres": "Number of offers", "Non renseignée": "Not provided",
  "Note": "Note", "Note (optionnel)": "Note (optional)", "Note ajoutée": "Note added", "Notes (optionnel)": "Notes (optional)",
  "Notes récentes": "Recent notes", "Nouveau mandat en préparation": "New mandate in preparation", "OK": "OK",
  "Objectif court terme": "Short-term objective", "Objectif moyen terme": "Mid-term objective", "Objectif principal": "Main objective",
  "Objet": "Subject", "Observation annulée": "Scouting session cancelled", "Observation planifiée et ajoutée au calendrier": "Scouting session scheduled and added to the calendar",
  "Offres reçues": "Offers received", "Origine du contact": "Contact origin",
  "Ou créer un nouveau club (référencé dans Clubs)": "Or create a new club (listed in Clubs)", "Oui, contact effectué": "Yes, contact made",
  "Ouvrir Contrats": "Open Contracts", "Ouvrir WhatsApp": "Open WhatsApp", "Ouvrir l'application email": "Open email app",
  "Ouvrir le calendrier": "Open calendar", "Ouvrir le contrat / signature": "Open contract / signature",
  "Ouvrir le dossier de renouvellement": "Open renewal case",
  "Ouvrir un canal ne change pas l'étape. Confirmez uniquement si le message a réellement été envoyé.": "Opening a channel does not change the stage. Confirm only if the message was actually sent.",
  "Pas encore": "Not yet", "Pas maintenant": "Not now", "Passer en discussion": "Move to discussion", "Pays": "Country", "Payée par": "Paid by",
  "Planifier": "Schedule", "Planifier une action": "Schedule an action", "Planifier une observation": "Schedule scouting",
  "Point client": "Client check-in", "Point programmé — visible dans À relancer": "Check-in scheduled — visible in Follow-ups",
  "Point téléphonique": "Phone check-in", "Points d'attention / objections": "Concerns / objections", "Points à observer…": "Points to watch…",
  "Pourquoi ce joueur ?": "Why this athlete?", "Premier contact": "First contact", "Premier contact effectué ?": "First contact made?",
  "Prime à la signature (€)": "Signing bonus (€)", "Prochain match / Observation": "Next match / Scouting", "Prochain événement": "Next event",
  "Prochaine relance": "Next follow-up", "Prochaine relance programmée": "Next follow-up scheduled", "Prochaine étape": "Next step",
  "Profil complet": "Full profile", "Programmer": "Schedule", "Programmer la prochaine relance ?": "Schedule the next follow-up?",
  "Programmer un point": "Schedule a check-in", "Programmer un point client": "Schedule a client check-in", "Projet de carrière": "Career plan",
  "Proposer": "Propose", "Proposer à un club": "Propose to a club", "Prospect restauré": "Prospect restored",
  "Prospect(s) écarté(s) — restauration possible via « Voir les écartés »": "Prospect(s) dismissed — restore via “View dismissed”",
  "Provenance des coordonnées (ex. : fourni par…)": "Source of contact details (e.g. provided by…)", "Préférence n°1": "Preference #1",
  "Prévu le": "Scheduled for", "Périmètre": "Scope", "Période des statistiques": "Statistics period",
  "Qui m'a répondu, et qui dois-je relancer ?": "Who replied, and who should I follow up with?",
  "Qui vaut la peine d'être approché ?": "Who is worth approaching?",
  "Rechercher un athlète, un club, un décideur…": "Search athletes, clubs, decision-makers…",
  "Rechercher un athlète, un club, un motif…": "Search athletes, clubs, reasons…", "Rechercher un client…": "Search clients…",
  "Rechercher un club existant": "Search an existing club", "Recommandation": "Recommendation", "Recommandé par": "Recommended by",
  "Recommandé par…": "Recommended by…", "Recontacter dans": "Contact again in", "Recontacter le": "Contact again on", "Refus": "Rejection",
  "Refus enregistré": "Rejection logged", "Relance effectuée": "Follow-up done", "Relance marquée comme faite.": "Follow-up marked as done.",
  "Relance prévue (optionnel)": "Planned follow-up (optional)", "Relance replanifiée": "Follow-up rescheduled",
  "Relancer exceptionnellement ?": "Follow up anyway?", "Relances": "Follow-ups", "Relances complétées": "Completed follow-ups",
  "Remettre en veille": "Snooze again", "Renouveler le mandat": "Renew mandate", "Renouvellement (Renewal Intelligence)": "Renewal (Renewal Intelligence)",
  "Repéré depuis le plus longtemps": "Scouted the longest", "Repéré le": "Scouted on", "Repéré récemment": "Recently scouted",
  "Restaurer": "Restore", "Rester en prise de contact": "Stay in outreach", "Réactiver (nouveau mandat)": "Reactivate (new mandate)",
  "Réactiver ce contact ?": "Reactivate this contact?", "Rédiger": "Compose", "Répartition par statut dans le pipeline": "Breakdown by pipeline status",
  "Réponse enregistrée": "Reply logged", "Réponse positive enregistrée. Passer cet athlète en discussion ?": "Positive reply logged. Move this athlete to discussion?",
  "Résiliation par l'agent": "Terminated by agent", "Résiliation par l'athlète": "Terminated by athlete", "Résilier": "Terminate",
  "Résilier le mandat": "Terminate mandate", "Résumé": "Summary", "Résumé de la réponse…": "Reply summary…",
  "Résumé rédigé par l'agent : où en est la discussion, ce qui est prévu…": "Summary written by the agent: where the discussion stands, what is planned…",
  "Salaire (€ / mois)": "Salary (€ / month)", "Salaire max (€ / mois)": "Max salary (€ / month)", "Salaire min (€ / mois)": "Min salary (€ / month)",
  "Salaire visé": "Target salary", "Sans club": "No club", "Services": "Services", "Signature": "Signature",
  "Situation contractuelle": "Contract situation", "Situation mise à jour": "Situation updated", "Source : toutes": "Source: all",
  "Source mise à jour": "Source updated", "Source non renseignée": "Source not provided", "Stable vs": "Stable vs",
  "Stable vs période précédente": "Stable vs previous period", "Statut Pipeline": "Pipeline status", "Statut du contrat": "Contract status",
  "Suivez leurs projets, vos démarches et transformez les opportunités en placements.": "Track their plans, your outreach, and turn opportunities into placements.",
  "Suivez vos discussions en cours et identifiez ce qui permet de faire avancer chaque dossier vers un mandat.": "Track your ongoing discussions and identify what moves each case toward a mandate.",
  "Suivez vos prises de contact et relancez au bon moment.": "Track your outreach and follow up at the right time.",
  "Suivez vos relances et gardez chaque échange au bon moment.": "Track your follow-ups and keep every exchange on time.",
  "Sélectionnez un athlète pour ouvrir l'espace de discussion.": "Select an athlete to open the discussion workspace.",
  "Sélectionnez un client": "Select a client", "Séquence épuisée": "Sequence exhausted", "Temps de jeu / rôle": "Playing time / role",
  "Territoire": "Territory", "Territoire (optionnel)": "Territory (optional)", "Tout désélectionner": "Deselect all",
  "Toutes les étapes": "All stages", "Type de club": "Club type", "Type de mandat": "Mandate type", "Téléphone (optionnel)": "Phone (optional)",
  "URL requise": "URL required", "Un motif est requis": "A reason is required",
  "Un refus a été enregistré. La réactivation ouvre un nouveau cycle de prise de contact.": "A rejection was logged. Reactivating opens a new outreach cycle.",
  "Version": "Version", "Voir le calendrier": "View calendar", "Voir le contrat": "View contract", "Voir le profil": "View profile",
  "Voir les écartés": "View dismissed", "Voir tout": "View all", "Voir toutes les notes": "View all notes",
  "Vos clients sous mandat, en recherche de club.": "Your clients under mandate, looking for a club.", "Zones": "Areas",
  "Zones acceptées (séparées par des virgules)": "Accepted areas (comma-separated)", "Zones recherchées": "Target areas",
  "athlètes au total": "athletes in total", "athlètes en prise de contact": "athletes in outreach", "athlètes repérés": "athletes scouted",
  "aujourd'hui": "today", "autre(s) relance(s)": "other follow-up(s)", "autre(s) échéance(s) critique(s)": "other critical deadline(s)",
  "ces 7 jours": "last 7 days", "clients sous mandat": "clients under mandate", "clubs": "clubs", "dans": "in",
  "démarche(s)": "outreach action(s)", "en retard de": "overdue by", "enregistrée": "saved",
  "est passé de Repéré à Contacté": "moved from Scouted to Contacted", "est passé en discussion": "moved to discussion",
  "identique au Pipeline, indépendant de la période": "same as the Pipeline, independent of the period", "intéressé(s)": "interested",
  "modifiable": "editable", "non renseignée": "not provided", "offre(s)": "offer(s)", "par": "by", "payée par": "paid by",
  "point précédent": "previous data point", "prospect(s) exporté(s)": "prospect(s) exported", "relance complétée": "follow-up completed",
  "relances complétées": "follow-ups completed", "représentant légal": "legal guardian", "suggestion": "suggestion",
  "sur la période": "over the period", "sélectionné(s)": "selected", "À discuter": "To discuss", "À recontacter plus tard": "Contact again later",
  "Âge : tous": "Age: all", "Écarter": "Dismiss", "Écarter ce prospect ?": "Dismiss this prospect?", "Écarté": "Dismissed",
  "Échange": "Interaction", "Échange enregistré": "Interaction logged", "Échéance de réponse": "Reply deadline", "Études": "Education",
  "évolution sur": "trend over", "≤ 12 mois": "≤ 12 months", "≤ 6 mois": "≤ 6 months",
  "Que souhaitez-vous analyser ?": "What would you like to analyze?",
  "Interrogez votre portefeuille, vos contrats, vos relations et vos opportunités.": "Query your portfolio, contracts, relationships and opportunities.",
  "Quels athlètes dois-je relancer ?": "Which athletes should I follow up with?",
  "Quels contrats arrivent à échéance ?": "Which contracts are expiring?",
  "Quelles opportunités dois-je faire avancer ?": "Which opportunities should I move forward?",
  "Quelles relations nécessitent mon attention ?": "Which relationships need my attention?",
  "Prépare mon prochain rendez-vous": "Prepare my next meeting",
  "Posez une question sur votre portefeuille...": "Ask a question about your portfolio...",
  "Tout le portefeuille": "Whole portfolio",
  "Opportunités": "Opportunities", "Relations": "Relationships",
  "Analyser": "Analyze", "Prioriser": "Prioritize", "Résumer": "Summarize", "Préparer un rendez-vous": "Prepare a meeting",
  "Générer un plan d'action": "Generate an action plan",
  "Analyses récentes": "Recent analyses", "Nouvelle analyse": "New analysis", "Rechercher une analyse…": "Search analyses…",
  "Aucune analyse pour le moment.": "No analyses yet.", "Aucune analyse ne correspond.": "No analyses match.",
  "Analyse du portefeuille…": "Analyzing your portfolio…",
  "Impossible de générer l'analyse pour le moment.": "Unable to generate the analysis right now.",
  "Ajouter un athlète au contexte": "Add an athlete to the context", 
  "Aucun athlète trouvé.": "No athletes found.", "Chargement…": "Loading…", "Contexte :": "Context:",
  "Les réponses sont centrées sur cet athlète.": "Answers are focused on this athlete.", 
  "Renommer l'analyse :": "Rename analysis:", "Renommer": "Rename", "Action recommandée :": "Recommended action:", 
   "À suivre": "To watch", 
  "Athlynx Intelligence s'appuie sur les données de votre espace de travail.": "Athlynx Intelligence relies on the data in your workspace.",

  "Choisir la langue": "Choose language",
  "Vos athlètes.": "Your athletes.",
  "Athlynx centralise vos opportunités, vos échanges et la gestion de vos athlètes. Pipeline, calendrier, relations et dossiers réunis dans un seul outil.": "Athlynx centralizes your opportunities, conversations and athlete management. Pipeline, calendar, relationships and athlete profiles, all in one workspace.",
  "Plateforme d'agent football": "Football agent platform",
  "Votre réseau.": "Your network.", "Vos joueurs.": "Your players.", "Votre temps.": "Your time.",
  "Athlynx centralise vos opportunités, vos échanges et votre carrière d'agent. Pipeline, calendrier, relances et fiches joueurs — réunis dans un seul outil.": "Athlynx centralizes your opportunities, your exchanges and your agent career. Pipeline, calendar, follow-ups and player profiles — together in one tool.",
  "Sécurisé. Fiable. Conçu pour les agents.": "Secure. Reliable. Built for sports agents.",
  "Connectez-vous à votre compte ou créez-en un nouveau.": "Sign in to your account or create a new one.",
  "Continuer avec Google": "Continue with Google", "Continuer avec iCloud": "Continue with iCloud",
  "ou": "or", "Mot de passe oublié ?": "Forgot password?", "vous@exemple.com": "you@example.com",
  "Se souvenir de moi": "Remember me", "Se connecter": "Sign in", "Connexion…": "Signing in…",
  "Pas encore de compte ?": "Don't have an account yet?", "Créer un compte": "Create an account",
  "Configurer": "Set up",
  "Vérifiez votre identité": "Verify your identity",
  "Saisissez l'un de vos codes de récupération (format XXXX-XXXX).": "Enter one of your recovery codes (format XXXX-XXXX).",
  "Saisissez le code à 6 chiffres de votre application d'authentification.": "Enter the 6-digit code from your authenticator app.",
  "Nous avons envoyé un code à 6 chiffres à votre adresse email.": "We sent a 6-digit code to your email address.",
  "Vérifier": "Verify",
  "Utiliser un code d'authentification": "Use an authenticator code",
  "Utiliser un code de récupération": "Use a recovery code",
  "Connexion impossible": "Unable to sign in", "Code invalide": "Invalid code",
  "Cacher": "Hide", "Afficher": "Show", "Bienvenue !": "Welcome!",
  "Rejoignez Athlynx en quelques secondes.": "Join Athlynx in seconds.",
  "Prénom requis": "First name required", "Nom requis": "Last name required",
  "Email invalide": "Invalid email", "8 caractères minimum": "8 characters minimum",
  "Compte créé — bienvenue !": "Account created — welcome!", "Création impossible": "Unable to create the account",
  "Confirmer le mot de passe": "Confirm password", "Créer mon compte": "Create my account",
  "Déjà un compte ?": "Already have an account?",
  "S'abonner": "Subscribe", "Redirection…": "Redirecting…", "/an": "/year",
  "Note vocale rapide": "Quick voice note",
  "Enregistrer une note vocale": "Record a voice note",
  "Enregistrez une note et associez-la à un athlète.": "Record a note and attach it to an athlete.",
  "Démarrer l'enregistrement": "Start recording",
  "Enregistrement…": "Recording…",
  "En pause": "Paused",
  "Reprendre": "Resume",
  "Arrêter l'enregistrement": "Stop recording",
  "Transcription en cours": "Transcribing",
  "Transcription (modifiable)": "Transcription (editable)",
  "Associer à un athlète": "Attach to an athlete",
  "Choisir un athlète": "Choose an athlete",
  "Athlète": "Athlete",
  "Enregistrer dans les notes du joueur": "Save to player notes",
  "Abandonner": "Discard",
  "Accès au microphone refusé": "Microphone access denied",
  "Aucun microphone détecté": "No microphone detected",
  "Enregistrement non pris en charge par ce navigateur": "Recording is not supported by this browser",
  "Enregistrement vide — aucun son capté": "Empty recording — no sound captured",
  "Erreur d'enregistrement": "Recording error",
  "Service de transcription indisponible — réessayez": "Transcription service unavailable — please retry",
  "Saisissez votre note manuellement…": "Type your note manually…",
  "Titre de la note": "Note title",
  "Note vocale": "Voice note",
  "Nouvelle note vocale": "New voice note",
  "Note enregistrée": "Note saved",
  "Échec de l'enregistrement de la note": "Failed to save the note",
  "Note supprimée": "Note deleted",
  "Modifier la note": "Edit note",
  "Votre note…": "Your note…",
  "Informations clés, réunions et observations.": "Key information, meetings and observations.",
  "Aucune note pour le moment.": "No notes yet.",
  "Vocal": "Voice",
  "Contenu requis": "Content required",
  "Enregistrez votre note. Elle sera transcrite puis ajoutée au profil de": "Record your note. It will be transcribed and added to the profile of",
  "Veillez à respecter les règles applicables si vous enregistrez une conversation impliquant d'autres personnes.": "Make sure you respect applicable rules when recording conversations involving other people.",
  "Limite de transcriptions atteinte — réessayez plus tard": "Transcription limit reached — try again later",
  "Format audio non pris en charge": "Unsupported audio format",
  "Enregistrement vide ou trop court": "Empty or too short recording",
  "Fichier audio trop volumineux (20 Mo max)": "Audio file too large (20 MB max)",
  "Aucune parole détectée dans l'enregistrement": "No speech detected in the recording",
  "Réunion": "Meeting", "Appel": "Call", "Médical": "Medical", "Pause": "Pause",
  "Rechercher dans les notes…": "Search notes…",
  "Toutes catégories": "All categories",
  "Plus récentes": "Most recent",
  "Plus anciennes": "Oldest",
  "Aucune note ne correspond à votre recherche.": "No notes match your search.",
  "Du": "From", "Au": "To",
  "Conserver l'enregistrement audio": "Keep original audio recording",
  "La note sera enregistrée en texte + audio.": "The note will be saved as text + audio.",
  "La note sera enregistrée en texte seul (audio supprimé).": "The note will be saved as text only (audio discarded).",
  "Seule la transcription sera conservée. L'enregistrement original sera supprimé.": "Only the transcription will be saved. The original recording will be deleted.",
  "Votre enregistrement original sera supprimé en toute sécurité après la période sélectionnée. Votre transcription restera disponible.": "Your original recording will be securely deleted after the selected period. Your saved transcription will remain available.",
  "Supprimer automatiquement après": "Automatically delete after",
  "30 jours": "30 days", "90 jours": "90 days", "365 jours": "365 days",
  "Supprimer l'audio maintenant": "Delete audio now",
  "Suppression programmée": "Scheduled deletion",
  "Sécurité : vous devez changer votre mot de passe initial.": "Security: you must change your initial password.",
  "Changer maintenant": "Change now",
  "Veuillez changer votre mot de passe initial.": "Please change your initial password.",
  "Statut Fondateur": "Founder status",
  "Accès permanent": "Permanent access",
  "Santé de la plateforme": "Platform health",
  "Base de données": "Database",
  "Opérationnelle": "Operational",
  "Heure serveur": "Server time",
  "Exonération interne — aucune expiration": "Internal entitlement — never expires",
  "fonctionnalités débloquées": "features unlocked",
  "activée": "enabled",
  "disponible (non activée)": "available (not enabled)",
  "Mode démo": "Demo mode",
  "Actif — sans limite de durée": "Active — no time limit",
  "Inactif — activable à tout moment": "Inactive — can be enabled anytime",
  "joueurs démo": "demo athletes",
  "Derniers événements d'audit": "Latest audit events",
  "jours restants": "days left",
  "Rétention": "Retention",
  "Enregistrement original": "Original recording",
  "Enregistrement original supprimé. Transcription conservée.": "Original recording deleted. Transcription preserved.",
  "Période de rétention mise à jour": "Retention period updated",
  "Période déjà expirée — audio supprimé.": "Period already expired — audio deleted.",
  "Période de rétention invalide": "Invalid retention period",
  "Audio expiré": "Audio expired",
  "Durée maximale atteinte (3 minutes) — l'enregistrement a été arrêté automatiquement.": "Maximum duration reached (3 minutes) — recording stopped automatically.",
  "Note enregistrée, mais l'audio n'a pas pu être conservé.": "Note saved, but the audio could not be kept.",
  "Supprimer l'audio": "Delete audio",
  "Audio supprimé": "Audio deleted",
  "Enregistrement trop long (3 minutes maximum)": "Recording too long (3 minutes maximum)",
  "Taille audio invalide": "Invalid audio size",
  "Audio introuvable": "Audio not found",
  "Modifier l'étape de la discussion": "Change discussion stage",
  "Modifier l'étape": "Change stage",
  "Souhaitez-vous faire passer cette discussion à l'étape": "Do you want to move this discussion to the stage",
  "Date de progression": "Progress date",
  "Note interne (optionnel)": "Internal note (optional)",
  "Prochaine action (optionnel)": "Next action (optional)",
  "Étape de la discussion modifiée": "Discussion stage updated",
  "Cette discussion est déjà à cette étape.": "This discussion is already at this stage.",
  "Choisir une étape": "Choose a stage",
  "Étape": "Stage",
  "Étape modifiée": "Stage changed",
  "Jalon réinitialisé": "Milestone reset",
  "L'athlète a un mandat signé mais cette discussion est toujours ouverte. Vous pouvez la clôturer pour aligner le suivi.": "This athlete has a signed mandate but this discussion is still open. You can close it to keep the tracking aligned.",
  "Clôturer la discussion": "Close the discussion",
  "Actives": "Active",
  "Vérification de votre abonnement…": "Verifying your subscription…",
  "Votre abonnement Athlynx est en cours d'activation. Merci de patienter quelques instants.": "Your Athlynx subscription is being activated. Please wait a few moments.",
  "Paiement confirmé": "Payment confirmed",
  "Votre abonnement est actif.": "Your subscription is active.",
  "Voir ma facturation": "Go to billing",
  "Activation en cours": "Activation in progress",
  "L'activation prend plus de temps que prévu. Votre paiement est traité par Stripe et n'est pas perdu — revenez dans quelques minutes.": "Activation is taking longer than expected. Your payment is being processed by Stripe and is not lost — check back in a few minutes.",
  "Réessayer la vérification": "Retry verification",
  "Paiement indisponible — configuration Stripe": "Payment unavailable — Stripe configuration",
  "Configuration Stripe incomplète : les liens de paiement ne correspondent pas au mode de la clé Stripe (LIVE/TEST). Les paiements sont désactivés par sécurité jusqu'à correction.": "Incomplete Stripe configuration: the payment links do not match the Stripe key mode (LIVE/TEST). Payments are disabled for safety until this is fixed.",
  "Toutes les fonctionnalités Pro. Il vous reste": "All Pro features. You have",
  "Votre période d'essai est expirée. Passez à Athlynx Pro pour continuer.": "Your trial has expired. Upgrade to Athlynx Pro to continue.",
  "Ex. John": "e.g. John", "Ex. Smith": "e.g. Smith",
  "votre@email.com": "your@email.com",
  "Créez un mot de passe": "Create a password",
  "Confirmez votre mot de passe": "Confirm your password",
  "En créant un compte, vous acceptez nos conditions d'utilisation et notre politique de confidentialité.": "By creating an account, you agree to our terms of use and privacy policy.",
  "Entrez votre email pour recevoir un lien de réinitialisation.": "Enter your email to receive a reset link.",
  "Envoyer le lien": "Send the link", "Demande envoyée": "Request sent",
  "Si un compte existe avec cet email, un lien de réinitialisation sera envoyé.": "If an account exists with this email, a reset link will be sent.",
  "Retour à la connexion": "Back to sign in",
  "Choisissez un nouveau mot de passe pour votre compte.": "Choose a new password for your account.",
  "Lien invalide. Demandez un nouveau lien depuis la page « Mot de passe oublié ».": "Invalid link. Request a new one from the “Forgot password” page.",
  "Confirmer": "Confirm", "Mise à jour…": "Updating…", "Mot de passe réinitialisé": "Password reset",
  // Contract negotiation workspace
  "Détails": "Details",
  "Signataires": "Signatories",
  "Historique": "History",
  "Négociation": "Negotiation",
  "Brouillon": "Draft",
  "Offre reçue": "Offer received",
  "En analyse": "Under review",
  "Contre-offre préparée": "Counter-offer prepared",
  "Contre-offre envoyée": "Counter-offer sent",
  "En attente de réponse": "Awaiting response",
  "Nouvelle offre reçue": "Revised offer received",
  "En négociation": "Negotiating",
  "Accord verbal": "Verbal agreement",
  "Contrat final reçu": "Final contract received",
  "Prêt à signer": "Ready for signature",
  "Signé": "Signed",
  "Refusé": "Rejected",
  "Retiré": "Withdrawn",
  "Clôturé": "Closed",
  "Non abordé": "Not discussed",
  "Ouvert": "Open",
  "Accepté": "Accepted",
  "Partiellement accepté": "Partially accepted",
  "À clarifier": "Needs clarification",
  "Offre initiale": "Initial offer",
  "Contre-offre": "Counter-offer",
  "Offre révisée": "Revised offer",
  "Accord final": "Final agreement",
  "Agent": "Agent",
  "Aucune négociation active pour ce contrat.": "No active negotiation for this contract.",
  "Ouvrez une négociation pour suivre offres, contre-offres et points d'accord.": "Open a negotiation to track offers, counter-offers and agreed points.",
  "Démarrer la négociation": "Start negotiation",
  "Négociation ouverte": "Negotiation opened",
  "Enregistrer l'offre reçue": "Record received offer",
  "Enregistrer une offre révisée": "Record revised offer",
  "Créer une contre-offre": "Create counter-offer",
  "Brouillon de contre-offre créé à partir de la dernière offre": "Counter-offer draft created from the latest offer",
  "Comparaison des offres": "Offer comparison",
  "Ajoutez une offre ou un document de contrat pour commencer.": "Add an offer or contract document to begin.",
  "Élément": "Term",
  "Notre contre-offre": "Our counter-offer",
  "Dernière offre": "Latest offer",
  "Objectif": "Target",
  "Écart": "Delta",
  "Modifiée": "Changed",
  "Points ouverts": "Open points",
  "Points acquis": "Agreed points",
  "Points refusés": "Rejected points",
  "Tous les points de négociation sont actuellement résolus.": "All negotiation points are currently resolved.",
  "Aucun point acquis pour le moment.": "No agreed points yet.",
  "Aucun point refusé.": "No rejected points.",
  "Historique des échanges": "Negotiation timeline",
  "Aucune activité de négociation pour le moment.": "No negotiation activity yet.",
  "Synthèse contractuelle": "Contract summary",
  "Version actuelle": "Current version",
  "Durée": "Duration",
  "Proposition actuelle": "Current proposal",
  "Notre position": "Our position",
  "Agent responsable": "Responsible agent",
  "acceptés": "accepted",
  "à négocier": "negotiating",
  "refusés": "rejected",
  "non abordés": "not discussed",
  "Prochaine action": "Next action",
  "Ex. : Relancer le directeur sportif": "E.g.: Follow up with sporting director",
  "Prochaine action enregistrée": "Next action saved",
  "Finalisation": "Finalization",
  "Marquer l'accord verbal": "Mark verbal agreement",
  "Accord verbal enregistré": "Verbal agreement recorded",
  "Revue finale": "Final review",
  "Marquer signé": "Mark signed",
  "Contrat marqué signé": "Contract marked signed",
  "Convertir en contrat actif": "Convert to active contract",
  "Contrat actif créé à partir des conditions validées": "Active contract created from validated terms",
  "Contrat activé — le suivi de renouvellement utilise les dates finales.": "Contract activated — renewal tracking uses the final dates.",
  "Un accord verbal n'est pas un contrat signé.": "A verbal agreement is not a signed contract.",
  "Revue finale avant signature": "Final review before signature",
  "Vérifiez chaque point avant de passer le contrat en « Prêt à signer ».": "Check every point before moving the contract to “Ready for signature”.",
  "Conditions financières vérifiées": "Financial terms reviewed",
  "Durée vérifiée": "Duration reviewed",
  "Options vérifiées": "Options reviewed",
  "Commission vérifiée": "Commission reviewed",
  "Clauses spécifiques vérifiées": "Custom clauses reviewed",
  "Document final joint": "Final document attached",
  "Différences vérifiées": "Differences reviewed",
  "Revue finale enregistrée": "Final review saved",
  "Version finalisée": "Version finalized",
  "Brouillon enregistré": "Draft saved",
  "Brouillon supprimé": "Draft deleted",
  "Ajouter un élément": "Add a term",
  "Oui": "Yes",
  "Non": "No",
  "ans": "years",
  "Salaire annuel": "Annual salary",
  "Salaire mensuel": "Monthly salary",
  "Prime de signature": "Signing bonus",
  "Bonus performance": "Performance bonus",
  "Prime de présence": "Appearance bonus",
  "Prime de victoire": "Win bonus",
  "Prime de fidélité": "Loyalty bonus",
  "Commission agent": "Agent commission",
  "Durée (années)": "Duration (years)",
  "Date de début": "Start date",
  "Date de fin": "End date",
  "Option de renouvellement": "Renewal option",
  "Option de prolongation": "Extension option",
  "Clause libératoire": "Release clause",
  "Clause de rachat": "Buyout clause",
  "Préavis": "Notice period",
  "Droits à l'image": "Image rights",
  "Droits commerciaux": "Commercial rights",
  "Logement": "Housing",
  "Véhicule": "Vehicle",
  "Déplacements": "Travel",
  "Aide à l'installation": "Relocation support",
  "Assurance": "Insurance",
  "Soutien scolaire / formation": "Education / training support",
  "Offre initiale reçue": "Initial offer received",
  "Accord final enregistré": "Final agreement recorded",
  "Contrat signé (négociation)": "Contract signed (negotiation)",
  "Revue finale complétée — prêt à signer": "Final review completed — ready for signature",
  "Statut d'élément mis à jour": "Term status updated",
  // Athlete profile / multi-sport / avatar
  "Athlétisme": "Athletics",
  "Boxe": "Boxing",
  "Cyclisme": "Cycling",
  "Football américain": "American football",
  "Hockey sur glace": "Ice hockey",
  "Natation": "Swimming",
  "Formule 1 / Motorsport": "Formula 1 / Motorsport",
  "Envergure (cm)": "Wingspan (cm)",
  "Championnat": "Championship",
  "Circuit": "Circuit",
  "Circuit / Ligue": "Circuit / League",
  "Circuit / Niveau": "Circuit / Level",
  "Ligue / Niveau": "League / Level",
  "Fédération / Niveau": "Federation / Level",
  "Niveau / Organisation": "Level / Organization",
  "Organisation": "Organization",
  "Catégorie de poids": "Weight class",
  "Discipline": "Discipline",
  "Jeu": "Game",
  "Rôle": "Role",
  "Profil": "Profile",
  "Numéro de course": "Race number",
  "Équipe / Club": "Team / Club",
  "Équipe actuelle": "Current team",
  "Club / Salle": "Club / Gym",
  "Revers": "Backhand",
  "Bras de lancer": "Throwing arm",
  "Camp d'entraînement": "Training camp",
  "Côté de frappe": "Hitting side",
  "Côté de jeu": "Playing side",
  "Côté de tir": "Shooting side",
  "Épreuve": "Event",
  "Garde": "Stance",
  "Nage": "Stroke",
  "Palmarès": "Record",
  "Portée d'attaque (cm)": "Attack reach (cm)",
  "Spécialité": "Specialty",
  "Temps au 40 yards (s)": "40-yard dash (s)",
  "Université / College": "University / College",
  "Gardien": "Goalkeeper",
  "Défenseur": "Defender",
  "Défenseur central": "Centre-back",
  "Milieu central": "Central midfielder",
  "Milieu défensif": "Defensive midfielder",
  "Milieu offensif": "Attacking midfielder",
  "Avant-centre": "Striker",
  "Ailier": "Winger",
  "Meneur": "Point guard",
  "Arrière": "Back",
  "Ailier fort": "Power forward",
  "Pivot": "Pivot",
  "Pilier": "Prop",
  "Talonneur": "Hooker",
  "Deuxième ligne": "Lock",
  "Troisième ligne": "Back row",
  "Demi de mêlée": "Scrum-half",
  "Demi d'ouverture": "Fly-half",
  "Centre": "Centre",
  "Ailier gauche": "Left wing",
  "Ailier droit": "Right wing",
  "Arrière gauche": "Left back",
  "Arrière droit": "Right back",
  "Demi-centre": "Centre back",
  "Une main": "One-handed",
  "Deux mains": "Two-handed",
  "Simple": "Singles",
  "Double": "Doubles",
  "Double mixte": "Mixed doubles",
  "Passeur": "Setter",
  "Réceptionneur-attaquant": "Outside hitter",
  "Central": "Middle blocker",
  "Pointu": "Opposite",
  "Libéro": "Libero",
  "Haies": "Hurdles",
  "Sauts": "Jumps",
  "Lancers": "Throws",
  "Marche": "Race walking",
  "Épreuves combinées": "Combined events",
  "Demi-fond": "Middle distance",
  "Fond": "Long distance",
  "Nage libre": "Freestyle",
  "Dos": "Backstroke",
  "Brasse": "Breaststroke",
  "Papillon": "Butterfly",
  "4 nages": "Medley",
  "Route": "Road",
  "Piste": "Track",
  "Sprinteur": "Sprinter",
  "Grimpeur": "Climber",
  "Polyvalent": "All-rounder",
  "Orthodoxe": "Orthodox",
  "Mouche": "Flyweight",
  "Coq": "Bantamweight",
  "Plume": "Featherweight",
  "Léger": "Lightweight",
  "Super-léger": "Super lightweight",
  "Welter": "Welterweight",
  "Super-welter": "Super welterweight",
  "Moyen": "Middleweight",
  "Mi-lourd": "Light heavyweight",
  "Lourd": "Heavyweight",
  "Lanceur": "Pitcher",
  "Receveur": "Catcher",
  "Première base": "First base",
  "Deuxième base": "Second base",
  "Troisième base": "Third base",
  "Arrêt-court": "Shortstop",
  "Champ gauche": "Left field",
  "Champ centre": "Center field",
  "Champ droit": "Right field",
  "Frappeur désigné": "Designated hitter",
  "Informations professionnelles": "Professional information",
  "Profil sportif": "Sport profile",
  "Informations": "Information",
  "Modifier les informations": "Edit information",
  "Modifications enregistrées": "Changes saved",
  "Annuler la modification": "Cancel editing",
  "Modifier le profil": "Edit profile",
  "Relancer par téléphone": "Follow up by phone",
  "Relancer par email": "Follow up by email",
  "Aucun contact renseigné": "No contact details",
  "Créer un contrat": "Create a contract",
  "Plus d'actions": "More actions",
  "Niveau": "Level",
  "Niveau actuel": "Current level",
  "À propos": "About",
  "Poste principal": "Primary position",
  "Postes secondaires": "Secondary positions",
  "Description, points forts, contexte…": "Description, strengths, context…",
  "Informations clés": "Key information",
  "Ajouter un tag": "Add a tag",
  "Aucun document. Ajoutez CV, vidéos, statistiques.": "No documents. Add CV, videos, statistics.",
  "Retirer": "Remove",
  "Aucun tag. Cliquez ✎ pour en ajouter.": "No tags. Click ✎ to add one.",
  "Statistiques saison": "Season statistics",
  "Importer des statistiques": "Import statistics",
  "Importer un fichier stats": "Import a stats file",
  "Voir tous les documents": "See all documents",
  "Voir l'historique": "See history",
  "Analyser le fichier": "Analyze the file",
  "Statistiques importées dans Documents": "Statistics imported into Documents",
  "Échec de l'import": "Import failed",
  "Format non supporté. Utilisez CSV, XLSX ou PDF.": "Unsupported format. Use CSV, XLSX or PDF.",
  "Fichier trop volumineux (max 20 Mo).": "File too large (max 20 MB).",
  "Aucun rappel.": "No reminders.",
  "Aucune interaction enregistrée.": "No interactions recorded.",
  "Ajouter une date d'expiration": "Add an expiration date",
  "Pas d'expiration": "No expiration",
  "Tous les types": "All types",
  "Télécharger": "Download",
  "Optionnel — utile pour passeports, contrats, certificats.": "Optional — useful for passports, contracts, certificates.",
  "Aucun email renseigné": "No email on file",
  "Aucun numéro renseigné": "No phone number on file",
  "Erreur téléchargement": "Download error",
  "Parcours": "Career path",
  "Ajouté à votre suivi": "Added to your tracking",
  "Club actuel non renseigné": "Current club not provided",
  "Le fichier doit être une image": "The file must be an image",
  "Image trop volumineuse (max 8 Mo)": "Image too large (max 8 MB)",
  "Photo mise à jour": "Photo updated",
  "Échec de la mise à jour": "Update failed",
  "Photo supprimée": "Photo removed",
  "Suppression impossible": "Unable to delete",
  "Photo du joueur": "Player photo",
  "Prendre une photo": "Take a photo",
  "Caméra de l'appareil": "Device camera",
  "Choisir depuis la galerie": "Choose from gallery",
  "Image existante (jpg, png, webp)": "Existing image (jpg, png, webp)",
  "Supprimer la photo": "Remove photo",
  "Reviendra aux initiales": "Falls back to initials",
  "Astuce : glissez-déposez une image directement sur l'avatar.": "Tip: drag and drop an image directly onto the avatar.",
  "Modifier la photo de profil": "Change profile photo",
  "Choisir une nouvelle photo de profil ou supprimer celle existante.": "Choose a new profile photo or remove the current one.",

  // ---- Negotiation Session 2: documents, extraction, reconciliation, briefs ----
  "Offre": "Offer",
  "Contrat final": "Final contract",
  "Annexe": "Annex",
  "Documents de négociation": "Negotiation documents",
  "Créez d'abord une version pour y joindre des documents.": "Create a version first to attach documents to it.",
  "brouillon": "draft",
  "Joindre un document": "Attach a document",
  "Aucun document joint. Joignez une offre ou un contrat pour lancer l'analyse.": "No document attached. Attach an offer or a contract to run the analysis.",
  "Document analysé": "Document analysed",
  "termes détectés": "terms detected",
  "Document joint à la version": "Document attached to the version",
  "Échec de l'envoi": "Upload failed",
  "Document retiré": "Document removed",
  "Analyser le document": "Analyse document",
  "à vérifier": "to review",
  "validés": "validated",
  "Les valeurs extraites par Athlynx Intelligence restent des suggestions tant que vous ne les validez pas.": "Values extracted by Athlynx Intelligence remain suggestions until you validate them.",
  "Fichier trop volumineux (max 15 Mo)": "File too large (max 15 MB)",
  "À vérifier": "Needs review",
  "Non trouvé": "Not found",
  "Validé": "Validated",
  "Corrigé et validé": "Edited and validated",
  "Ignoré": "Ignored",
  "Tous": "All",
  "Écarts": "Differences",
  "Validés": "Validated",
  "Ignorés": "Ignored",
  "Non trouvés": "Not found",
  "Conditions extraites": "Extracted terms",
  "non trouvés": "not found",
  "Chaque valeur extraite doit être validée par vous avant d'entrer dans la négociation.": "Every extracted value must be validated by you before it enters the negotiation.",
  "Valider les termes identiques": "Accept all unchanged terms",
  "Créez d'abord un brouillon de version pour recevoir les valeurs validées.": "Create a draft version first to receive validated values.",
  "Aucun terme dans ce filtre.": "No terms in this filter.",
  "Suggestion ignorée": "Suggestion ignored",
  "Valeur validée dans le brouillon": "Value validated into the draft",
  "Aucun terme identique à valider en lot.": "No identical terms to bulk-validate.",
  "termes identiques validés": "identical terms validated",
  "Valeur extraite": "Extracted value",
  "Valeur actuelle": "Current value",
  "Différence": "Difference",
  "Source": "Source",
  "Page": "Page",
  "Ignorer": "Ignore",
  "Accepter": "Accept",
  "Valider la valeur corrigée": "Validate corrected value",
  "Non résolus": "Unresolved",
  "Manquants": "Missing",
  "Inattendus": "Unexpected",
  "Conformes": "Matches",
  "Conforme": "Match",
  "Manquant": "Missing",
  "Inattendu": "Unexpected",
  "Confirmé conforme": "Confirmed as match",
  "Modification acceptée": "Change accepted",
  "Correction demandée": "Correction requested",
  "Réconciliation du contrat final": "Final contract reconciliation",
  "Réconcilié": "Reconciled",
  "Revue requise": "Review required",
  "Relancer la réconciliation": "Re-run reconciliation",
  "Lancer la réconciliation": "Run reconciliation",
  "Analysez d'abord le contrat final pour extraire ses termes.": "Analyse the final contract first to extract its terms.",
  "comparé à la version convenue": "compared against agreed version",
  "Aucun point non résolu — la réconciliation est complète.": "No unresolved items — reconciliation is complete.",
  "Réconciliation effectuée": "Reconciliation completed",
  "Réconciliation complète — prêt pour la signature": "Reconciliation complete — ready for signature",
  "Résolution enregistrée": "Resolution saved",
  "Vérifiez les différences contractuelles avant de continuer.": "Review contractual differences before continuing.",
  "Vérifiez que le contrat final correspond à la dernière version convenue avant la signature.": "Verify that the final contract matches the last agreed version before signing.",
  "Accepter la modification finale ?": "Accept the final change?",
  "Cette valeur du contrat final remplacera la valeur convenue. La version convenue reste conservée dans l'historique.": "This final-contract value will replace the agreed value. The agreed version remains preserved in the history.",
  "Note (facultatif)": "Note (optional)",
  "Accepter la modification": "Accept change",
  "Convenu": "Agreed",
  "Absent de la version convenue": "Absent from the agreed version",
  "Non détecté": "Not detected",
  "Rouvrir": "Reopen",
  "Confirmer conforme": "Confirm match",
  "Demander une correction": "Request correction",
  "Synthèse de négociation": "Negotiation summary",
  "Préparer la réunion": "Prepare meeting",
  "Généré uniquement à partir des données validées de la négociation.": "Generated only from validated negotiation data.",
  "Préparation de réunion": "Meeting prep brief",
  "Aucune version finalisée": "No finalized version",
  "Athlynx Intelligence est momentanément indisponible — synthèse basée uniquement sur les données structurées.": "Athlynx Intelligence is temporarily unavailable — summary based on structured data only.",
  "Points clés": "Key points",
  "Priorités": "Priorities",
  "Questions à clarifier": "Questions to clarify",
  "Prochaines actions": "Next actions",
  "Écart restant vs objectifs": "Remaining gap vs targets",
  "Derniers mouvements": "Latest movements",
  "Échéances": "Deadlines",
  "Arguments pour la réunion": "Talking points for the meeting",
  "Généré par": "Generated by",
  "Copier": "Copy",
  "Copié dans le presse-papiers": "Copied to clipboard",
  "Aucun point ouvert.": "No open points.",

  // ---- Session 3: counter-offer PDF, analytics, timeline i18n, brief PDF ----
  "Statut de négociation": "Negotiation status",
  "finalisée": "finalized",
  "Contrat activé depuis la négociation": "Contract activated from the negotiation",
  "Document": "Document",
  "joint à": "attached to",
  "retiré": "removed",
  "termes extraits de": "terms extracted from",
  "Valeur validée": "Value validated",
  "Valeur corrigée puis validée": "Value corrected then validated",
  "Réconciliation finale": "Final reconciliation",
  "conformes": "matches",
  "écarts": "differences",
  "manquants": "missing",
  "inattendus": "unexpected",
  "Écart confirmé conforme": "Difference confirmed as match",
  "Modification finale acceptée": "Final change accepted",
  "Résolution réouverte": "Resolution reopened",
  "Synthèse de négociation générée": "Negotiation summary generated",
  "Préparation de réunion générée": "Meeting prep brief generated",
  "exportée en PDF": "exported as PDF",
  "Synthèse de négociation exportée en PDF": "Negotiation summary exported as PDF",
  "Préparation de réunion exportée en PDF": "Meeting prep brief exported as PDF",
  "Exporter la contre-offre en PDF": "Export counter-offer as PDF",
  "Seules les informations sélectionnées ci-dessous apparaîtront dans le document. Les objectifs internes, statuts et notes internes ne sont jamais inclus.": "Only the information selected below will appear in the document. Internal targets, statuses and internal notes are never included.",
  "Conditions à inclure": "Terms to include",
  "Valable jusqu'au": "Valid until",
  "optionnel": "optional",
  "Note partageable": "Shareable note",
  "Visible par la contrepartie — n'y mettez aucune information interne.": "Visible to the counterparty — do not include any internal information.",
  "Aperçu du contenu": "Content preview",
  "Conditions": "Terms",
  "Contact agent": "Agent contact",
  "Télécharger le PDF": "Download PDF",
  "PDF de contre-offre téléchargé": "Counter-offer PDF downloaded",
  "Aucun élément": "No items",
  "Exporter la contre-offre": "Export counter-offer",
  "Analytique de négociation": "Negotiation analytics",
  "versions finalisées": "finalized versions",
  "en hausse": "improved",
  "en baisse": "decreased",
  "inchangés": "unchanged",
  "Finalisez au moins une version pour voir l'évolution.": "Finalize at least one version to see the evolution.",
  "Écart restant": "Remaining gap",
  "négociations": "negotiations",
  "évolution salariale cumulée": "cumulative salary evolution",
  "Calculs déterministes basés sur les versions finalisées — aucune note ni score arbitraire.": "Deterministic calculations based on finalized versions — no arbitrary score or rating.",
  "Exporter en PDF": "Export as PDF",
  "PDF téléchargé": "PDF downloaded",
  "Recommandations Athlynx Intelligence": "Athlynx Intelligence recommendations",
  "à confirmer par l'agent": "to be confirmed by the agent",
  "Faits vérifiés": "Verified facts",
  "Signaux calculés": "Calculated signals",

  // ---- Session 4: validity deadlines + counter-offer email delivery ----
  "Valide": "Active",
  "Expire bientôt": "Expiring soon",
  "Expire aujourd'hui": "Expires today",
  "Expire demain": "Expires tomorrow",
  "Valable encore": "Valid for another",
  "Expirée": "Expired",
  "Expirée depuis": "Expired for",
  "jour": "day",
  "jours": "days",
  "j": "d",
  "Remplacée": "Superseded",
  "Offre expirée — la contre-offre V": "Offer expired — counter-offer V",
  "est arrivée à échéance.": "has reached its deadline.",
  "est arrivée à échéance": "has reached its deadline",
  "Créer une nouvelle version": "Create new version",
  "Envois": "Deliveries",
  "Envoyé": "Sent",
  "Échec": "Failed",
  "Envoi…": "Sending…",
  "Envoyer la contre-offre": "Send counter-offer",
  "Seul le PDF de contre-offre approuvé et votre message seront envoyés — aucune donnée interne.": "Only the approved counter-offer PDF and your message will be sent — no internal data.",
  "Destinataire": "Recipient",
  "Utiliser le contact du club": "Use the club contact",
  "Sujet": "Subject",
  "Message": "Message",
  "Pièce jointe": "Attachment",
  "conditions incluses": "terms included",
  "Réponse à": "Reply-to",
  "Cette version a déjà été envoyée à ce destinataire le": "This version was already sent to this recipient on",
  "Envoyer à nouveau": "Send again",
  "Envoyer": "Send",
  "envoyée à": "sent to",
  "Échec d'envoi de la contre-offre": "Failed to send counter-offer",
  "Échéance": "Deadline",
  "la contre-offre pour": "the counter-offer for",
  "La contre-offre pour": "The counter-offer for",
  "expire dans 7 jours": "expires in 7 days",
  "expire dans 3 jours": "expires in 3 days",
  "expire demain": "expires tomorrow",
  "Action requise": "Action required",
  "Offre expirée": "Offer expired",
  "Offres — échéances": "Offers — deadlines",
  "Offre expire bientôt": "Offer expiring soon",
  "Offre expire dans": "Offer expires in",
  "Négociations et offres": "Negotiations & offers",
  "Alertes d'échéance des offres (J-7, J-3, J-1, expiration) et envois de contre-offres.": "Offer deadline alerts (D-7, D-3, D-1, expiration) and counter-offer deliveries.",
  "Notifications dans l'application": "In-app notifications",
  "Affichées dans la cloche de notifications.": "Shown in the notification bell.",
  "Notifications par email": "Email notifications",
  "Le suivi des échéances reste actif même si les notifications sont désactivées.": "Deadline tracking stays active even when notifications are disabled.",
  "Préférences enregistrées": "Preferences saved",
  "Service email non configuré — ajoutez la clé Resend pour activer l'envoi.": "Email service not configured — add the Resend key to enable sending.",
  "L'envoi de l'email a échoué. Vérifiez l'adresse et réessayez.": "The email could not be sent. Check the address and try again.",
  "Limite d'envoi atteinte — réessayez dans une heure.": "Sending limit reached — try again in an hour.",
  "Finalisez la version avant de l'envoyer.": "Finalize the version before sending it.",
  "Seules vos contre-offres peuvent être envoyées.": "Only your counter-offers can be sent.",
  "Adresse email du destinataire invalide.": "Invalid recipient email address.",

  // ---- Phase A: contract lifecycle production refactor ----
  "Comment souhaitez-vous créer ce contrat ?": "How would you like to create this contract?",
  "Importer un contrat": "Import a contract",
  "Ajoutez un document. Athlynx extrait les informations et prépare le dossier.": "Add a document. Athlynx extracts the information and prepares the file.",
  "Créer manuellement": "Create manually",
  "Renseignez vous-même les informations du contrat.": "Enter the contract information yourself.",
  "Analyse du document…": "Analysing document…",
  "Choisir un document (PDF)": "Choose a document (PDF)",
  "Informations détectées": "Detected information",
  "Athlynx Intelligence a extrait": "Athlynx Intelligence extracted",
  "informations": "items",
  "confirmées": "confirmed",
  "Vérifiez et corrigez les informations avant de créer le contrat.": "Review and correct the information before creating the contract.",
  "Confirmé": "Confirmed",
  "Confirmer et créer le contrat": "Confirm and create the contract",
  "Titre du contrat": "Contract title",
  "Athlète (nom)": "Athlete (name)",
  "Montant annuel": "Annual amount",
  "Autres informations détectées": "Other detected information",
  "Contrat créé depuis le document importé": "Contract created from the imported document",
  "Analyse terminée": "Analysis complete",
  "Contrat activé automatiquement (date d'effet atteinte)": "Contract automatically activated (effective date reached)",
  "Contrat expiré (date de fin dépassée)": "Contract expired (end date passed)",
  "Signé · Prend effet le": "Signed · Takes effect on",
  "Nouveau contrat": "New contract",
  "Modifier le contrat": "Edit contract",
  "Créez un contrat manuellement ou à partir d'un document.": "Create a contract manually or from a document.",
  "Mettez à jour les informations du contrat.": "Update the contract information.",
  "Informations générales": "General information",
  "Détails du contrat": "Contract details",
  "Validation": "Validation",
  "Vérifiez les informations détectées": "Review the detected information",
  "Informations du contrat": "Contract information",
  "Ces informations seront pré-remplies et peuvent être modifiées.": "This information is pre-filled and can be edited.",
  "Informations détectées par": "Information detected by",
  "informations du document.": "items from the document.",
  "Vérifiez et corrigez si nécessaire avant de créer le contrat.": "Review and correct if needed before creating the contract.",
  "autres informations détectées": "other detected items",
  "Réduire la liste": "Collapse list",
  "Voir la clause": "View clause",
  "Masquer la clause": "Hide clause",
  "Valeur saisie": "Entered value",
  "Le titre est requis pour créer le contrat.": "A title is required to create the contract.",
  "Dossier complet": "File complete",
  "Analyse du contrat en cours…": "Contract analysis in progress…",
  "Lecture du document": "Reading the document",
  "Identification des parties": "Identifying the parties",
  "Extraction des conditions": "Extracting the terms",
  "Préparation des informations": "Preparing the information",
  "L'analyse du document n'a pas pu être terminée.": "The document analysis could not be completed.",
  "Vous pouvez réessayer ou compléter les informations manuellement.": "You can retry or fill in the information manually.",
  "Continuer manuellement": "Continue manually",
  "Importez un document pour pré-remplir automatiquement les informations du contrat.": "Import a document to automatically pre-fill the contract information.",
  "Importer un document": "Import a document",
  "Utiliser": "Use",
  "comme contrepartie externe": "as external counterparty",
  "Rechercher un club…": "Search for a club…",
  "Aucun club trouvé": "No club found",
  "Externe": "External",
  "Aucune contrepartie": "No counterparty",
  "Type de contrat": "Contract type",
  "Commission (%)": "Commission (%)",
  "Devise": "Currency",
  "Créer le contrat": "Create the contract",
  "Contrat créé": "Contract created",
  "Contrat mis à jour": "Contract updated",
  "Extraction d'origine": "Original extraction",
  "Complétude du dossier": "File completeness",
  "Les éléments signés et les procédures de signature restent immuables et gérés par le cycle de vie du contrat.": "Signed items and signature procedures remain immutable and governed by the contract lifecycle.",
  "Le document importé sera joint au contrat.": "The imported document will be attached to the contract.",
  "Le document n'a pas pu être joint au contrat": "The document could not be attached to the contract",
  "Analyser à nouveau": "Analyze again",
  "Choisir un autre document": "Choose another document",
  "(externe)": "(external)",
  "Créé le": "Created on",
  "Dernière mise à jour": "Last updated",
  "Dates": "Dates",
  "Type de document": "Document type",
  "Ligue / Championnat": "League / Competition",
  "Prime à la signature": "Signing bonus",
  "Autres clauses": "Other clauses",
  "Ex. Mandat de représentation — Kylian Martin": "e.g. Representation mandate — Kylian Martin",
  "Mandat de représentation": "Representation mandate",
  "Contrat de transfert": "Transfer contract",
  "Contrat de travail": "Employment contract",
  "Contrat de sponsoring": "Sponsorship contract",
  "Avenant": "Amendment",
  "Contexte, clauses clés, points de vigilance…": "Context, key clauses, points of attention…",
  "Ouvrir le pipeline": "Open the pipeline",
  "Aucun document à vérifier": "No document to review",
  "Aucune échéance à venir": "No upcoming deadline",
  "Voir la fiche": "View profile",
  "Rechercher un athlète…": "Search for an athlete…",
  "Aucun athlète trouvé": "No athlete found",
  "Activité de": "Activity of",
  "Depuis": "For",
  "expirés": "expired",
  "expiré": "expired",
  "Événement": "Event",
  "Retour au portefeuille": "Back to portfolio",
  "Dashboard filtré sur": "Dashboard filtered on",
  "Aucune relance en attente": "No pending follow-up",
  "à vérifier dans les 30 jours": "to review within 30 days",
  "étape complétée": "step completed",
  "étape actuelle": "current step",
  "Statut hors pipeline": "Status outside pipeline",
  "Que souhaitez-vous savoir ?": "What would you like to know?",
  "Interrogez votre portefeuille à partir des données présentes dans Athlynx.": "Query your portfolio from the data available in Athlynx.",
  "Interrogez les données de": "Query the data of",
  "présentes dans Athlynx.": "available in Athlynx.",
  "Qui dois-je relancer aujourd'hui ?": "Who should I follow up with today?",
  "Quels athlètes demandent mon attention ?": "Which athletes need my attention?",
  "Quels contrats arrivent bientôt à échéance ?": "Which contracts are expiring soon?",
  "Que dois-je prioriser cette semaine ?": "What should I prioritize this week?",
  "Que dois-je faire pour": "What should I do for",
  "aujourd'hui ?": "today?",
  "Quelle est ma prochaine échéance avec": "What is my next deadline with",
  "Quels documents dois-je vérifier ?": "Which documents should I review?",
  "Où en est sa situation contractuelle ?": "What is their contract situation?",
  "Demander à Athlynx Intelligence…": "Ask Athlynx Intelligence…",
  "Demander à Athlynx…": "Ask Athlynx…",
  "Vous": "You",
  "Analyse de votre portefeuille…": "Analyzing your portfolio…",
  "Analyse des données de": "Analyzing the data of",
  "Voir dans Athlynx Intelligence": "View in Athlynx Intelligence",
  "Nouvelle conversation": "New conversation",
  "Nouveau message": "New message",
  "Athlynx Intelligence est momentanément indisponible.": "Athlynx Intelligence is temporarily unavailable.",
  "Ouvrir": "Open",
  "Action recommandée": "Recommended action",
  "Une partie de l'analyse n'a pas pu être générée. Vos données Athlynx restent disponibles.": "Part of the analysis could not be generated. Your Athlynx data remains available.",
  // --- Placements workspace ---
  "Placements": "Placements",
  "Suivez vos athlètes après leur arrivée dans leur nouvelle structure.": "Track your athletes after they arrive at their new organization.",
  "À surveiller": "Watch",
  "Rechercher un athlète placé…": "Search a placed athlete…",
  "Tous les sports": "All sports",
  "Statut de suivi": "Follow-up status",
  "Exporter": "Export",
  "Confirmer un placement": "Confirm a placement",
  "Confirmer le placement": "Confirm placement",
  "Placement": "Placement",
  "Suivi normal": "On track",
  "Attention": "Attention",
  "Résilié": "Terminated",
  "Placé le": "Placed on",
  "Contrat jusqu'en": "Contract until",
  "Contrat jusqu'au": "Contract until",
  "Date de placement à compléter": "Placement date to complete",
  "Date du placement": "Placement date",
  "Dernier échange": "Last exchange",
  "Prochain suivi": "Next follow-up",
  "mois restants": "months remaining",
  "En retard de": "Overdue by",
  "Timeline de suivi": "Follow-up timeline",
  "Ajouter un suivi": "Add a follow-up",
  "Aucun suivi planifié pour ce placement.": "No follow-up scheduled for this placement.",
  "Compléter": "Complete",
  "Annuler ce suivi": "Cancel this follow-up",
  "Objectif du placement": "Placement objective",
  "Situation actuelle": "Current situation",
  "Signaux": "Signals",
  "Aucun signal d'alerte": "No alert signal",
  "Le suivi est à jour.": "Follow-up is up to date.",
  "Situation sportive": "Sporting situation",
  "Relation club": "Club relationship",
  "Relation avec le club": "Relationship with the club",
  "Satisfaction athlète": "Athlete satisfaction",
  "Satisfaction de l'athlète": "Athlete satisfaction",
  "Positive": "Positive",
  "Stable": "Stable",
  "Aucune action planifiée.": "No action scheduled.",
  "Modifier l'objectif": "Edit objective",
  "Objectif mis à jour": "Objective updated",
  "Comment évolue la situation ?": "How is the situation evolving?",
  "Date de l'action": "Action date",
  "Optionnel": "Optional",
  "Enregistrer le suivi": "Save follow-up",
  "Suivi enregistré": "Follow-up saved",
  "Suivi planifié": "Follow-up scheduled",
  "Planifier le suivi": "Schedule follow-up",
  "Le suivi est ajouté à la timeline et au calendrier.": "The follow-up is added to the timeline and calendar.",
  "Libellé": "Label",
  "Libellé et date requis": "Label and date required",
  "Ex. : Point avec le staff": "E.g.: Check-in with staff",
  "Clôturer le placement": "Close placement",
  "Le placement et son historique restent consultables dans l'onglet Historique.": "The placement and its history remain available in the History tab.",
  "Contrat terminé": "Contract ended",
  "Nouvelle opportunité": "New opportunity",
  "Placement clôturé": "Placement closed",
  "Clôture…": "Closing…",
  "Actions du placement": "Placement actions",
  "Ouvrir l'athlète": "Open athlete",
  "Ouvrir le contrat": "Open contract",
  "Contrat lié": "Linked contract",
  "Aucun contrat n'est lié à ce placement.": "No contract is linked to this placement.",
  "Phase actuelle": "Current stage",
  "Jours restants": "Days remaining",
  "Priorité": "Priority",
  "Voir dans Renouvellements": "View in Renewals",
  "Ce contrat n'est pas encore entré dans sa fenêtre de renouvellement.": "This contract has not yet entered its renewal window.",
  "Le renouvellement n'est pas encore ouvert.": "The renewal is not open yet.",
  "Temps restant": "Time remaining",
  "Objectifs de carrière actifs": "Active career goals",
  "Aucun objectif de carrière actif.": "No active career goal.",
  "Ouvrir le plan de carrière": "Open career plan",
  "Relation avec l'athlète": "Relationship with the athlete",
  "Interactions (90 jours)": "Interactions (90 days)",
  "Relances ouvertes": "Open reminders",
  "Ouvrir la fiche athlète": "Open athlete profile",
  "Statut de la relation": "Relationship status",
  "Voir le club": "View club",
  "Documents de l'athlète": "Athlete documents",
  "Aucun document.": "No document.",
  "Gérer les documents": "Manage documents",
  "Activité récente": "Recent activity",
  "Aucune activité enregistrée.": "No recorded activity.",
  "Placement confirmé": "Placement confirmed",
  "Placement modifié": "Placement updated",
  "Suivi réalisé": "Follow-up completed",
  "Suivi modifié": "Follow-up updated",
  "Changement de statut": "Status change",
  "Voir le placement": "View placement",
  "Un placement actif existe déjà pour cet athlète": "An active placement already exists for this athlete",
  "Impossible de confirmer le placement": "Unable to confirm the placement",
  "L'athlète passera au statut « Placé » et son suivi post-placement sera activé.": "The athlete will move to the “Placed” status and post-placement follow-up will be activated.",
  "Club / organisation": "Club / organization",
  "Contrat associé": "Linked contract",
  "Aucun contrat lié": "No linked contract",
  "Ex. : obtenir davantage de temps de jeu avant une montée de niveau.": "E.g.: get more playing time before stepping up a level.",
  "Générer le programme de suivi par défaut (J+7, J+30, J+60, J+90…)": "Generate the default follow-up schedule (D+7, D+30, D+60, D+90…)",
  "Confirmation…": "Confirming…",
  "Sélectionnez un athlète": "Select an athlete",
  "Sélectionner…": "Select…",
  "Impossible de charger les placements": "Unable to load placements",
  "Placement introuvable": "Placement not found",
  "Aucun placement dans cette vue.": "No placement in this view.",
  "Confirmez un placement depuis la Pipeline ou avec le bouton ci-dessus.": "Confirm a placement from the Pipeline or with the button above.",
  "Les placements correspondants apparaîtront ici.": "Matching placements will appear here.",
  "Sélectionnez un athlète placé pour ouvrir son espace de suivi.": "Select a placed athlete to open their follow-up workspace.",
  "Placements à suivre": "Placements to watch",
  "Suivez l'intégration de vos athlètes, leurs performances et les prochaines étapes de leur carrière.": "Track your athletes' integration, performances and the next steps of their career.",
  "Rechercher un athlète, un club, une compétition…": "Search an athlete, a club, a competition…",
  "Voir la Pipeline": "View Pipeline",
  "Les athlètes apparaîtront ici après confirmation de leur placement depuis la Pipeline.": "Athletes will appear here once their placement is confirmed from the Pipeline.",
  "Vue d'ensemble": "Overview",
  "Prochaines étapes": "Next steps",
  "Situation au club": "Club situation",
  "Compétition": "Competition",
  "Poste / discipline": "Position / discipline",
  "Aucune donnée de performance disponible.": "No performance data available.",
  "Ouvrir Performance": "Open Performance",
  "Contacts au club": "Club contacts",
  "Aucun contact club associé.": "No club contact linked.",
  "Appeler": "Call",
  "Envoyer un email": "Send an email",
  "Prochaine période clé": "Next key period",
  "Aucune échéance à venir.": "No upcoming deadline.",
  "Intégration": "Integration",
  "Aucun élément d'intégration suivi.": "No integration item tracked.",
  "À faire": "To do",
  "Bloqué": "Blocked",
  "Scolarité / formation": "Education / training",
  "Entourage": "Entourage",
  "Administratif": "Administrative",
  "Installation": "Relocation",
  "Ex. : Trouver un logement": "E.g.: Find housing",
  "Performance & bien-être": "Performance & well-being",
  "Disponibilité": "Availability",
  "Limité": "Limited",
  "Indisponible": "Unavailable",
  "En progression": "Improving",
  "En baisse": "Declining",
  "Temps de jeu": "Playing time",
  "Note (déclaratif, non médical)": "Note (declarative, non-medical)",
  "Aucune donnée enregistrée.": "No recorded data.",
  "Indicateurs de suivi": "Follow-up indicators",
  "Niveau d'attention": "Attention level",
  "Ajouter une interaction": "Add an interaction",
  "Interaction ajoutée": "Interaction added",
  "Note requise": "Note required",
  "Compte rendu, point d'étape…": "Report, check-in…",
  "Réunion": "Meeting",
  "Visio": "Video call",
  "Enregistré": "Saved",
  "Libellé requis": "Label required",
  "Suivi": "Follow-up",
  "Agenda": "Calendar",
  "Carrière": "Career",
  "Fin de contrat / renouvellement": "Contract end / renewal",

  // ===== Phase 2 =====
  "(contrats créés sur la période)": "(contracts created over the period)",
  "(triés par urgence)": "(sorted by urgency)",
  "+ Ajouter un contact": "+ Add a contact",
  "+ Ajouter un document": "+ Add a document",
  "+ Planifier une action": "+ Plan an action",
  "/ mois": "/ month",
  "/mois": "/month",
  "000000 ou XXXX-XXXX": "000000 or XXXX-XXXX",
  "2. Champs détectés": "2. Detected fields",
  "Actions & relances": "Actions & follow-ups",
  "Activez maintenant pour conserver l'accès après l'essai.": "Activate now to keep access after the trial.",
  "Activité contractuelle": "Contract activity",
  "Ajouter des athlètes": "Add athletes",
  "Ajouter des participants": "Add participants",
  "Ajouter l'objectif": "Add goal",
  "Ajouter le jalon": "Add milestone",
  "Ajouter un athlète": "Add an athlete",
  "Ajouter un contact": "Add a contact",
  "Ajouter un fait marquant…": "Add a highlight…",
  "Ajouter un événement": "Add an event",
  "Ajouter une activité": "Add an activity",
  "Ajouter une description ou des notes…": "Add a description or notes…",
  "Ajouter une note interne…": "Add an internal note…",
  "Ajoutez des athlètes, renseignez vos contrats et enregistrez vos échanges pour des analyses plus riches.": "Add athletes, fill in your contracts and record your exchanges for richer analyses.",
  "Ajoutez des contrats pour calculer l'indice de santé du portefeuille.": "Add contracts to compute the portfolio health index.",
  "Ajoutez des jalons et objectifs pour mesurer le rythme d'exécution.": "Add milestones and goals to measure execution pace.",
  "Ajoutez des jalons à l'étape 3.": "Add milestones in step 3.",
  "Ajoutez des objectifs à l'étape 4.": "Add goals in step 4.",
  "Ajoutez le premier document de votre portefeuille (contrat, CV, pièce d'identité…).": "Add the first document of your portfolio (contract, CV, ID…).",
  "Ajoutez un contact et quelques interactions pour permettre à Athlynx d'évaluer cette relation.": "Add a contact and a few interactions so Athlynx can assess this relationship.",
  "Ajoutez une étape à la feuille de route.": "Add a step to the roadmap.",
  "Alertes de sécurité": "Security alerts",
  "Analysez les transitions de votre pipeline sur la période.": "Analyze your pipeline transitions over the period.",
  "Annuler la signature": "Cancel signature",
  "Annuler le changement": "Cancel change",
  "Aperçu": "Overview",
  "Aperçu A4": "A4 preview",
  "Aperçu A4 en direct": "Live A4 preview",
  "Aperçu de la fiche de négociation": "Negotiation sheet preview",
  "Aperçu des joueurs": "Players preview",
  "Aperçu du logo": "Logo preview",
  "Aperçu du template": "Template preview",
  "Archiver ce contrat ?": "Archive this contract?",
  "Associer un athlète": "Link an athlete",
  "Athlète lié": "Linked athlete",
  "Athlète lié (optionnel)": "Linked athlete (optional)",
  "Athlètes concernés": "Athletes involved",
  "Athlètes liés": "Linked athletes",
  "Attention : vos codes de récupération actuels seront tous invalidés.": "Warning: all your current recovery codes will be invalidated.",
  "Aucun athlète disponible": "No athlete available",
  "Aucun athlète ne correspond à ces critères.": "No athlete matches these criteria.",
  "Aucun champ reconnu pour le moment. Importez un fichier pour commencer.": "No field recognized yet. Import a file to get started.",
  "Aucun club disponible": "No club available",
  "Aucun contact": "No contact",
  "Aucun contrat créé sur cette période.": "No contract created over this period.",
  "Aucun document dans le dossier.": "No document in the case file.",
  "Aucun document ne correspond à ces filtres.": "No document matches these filters.",
  "Aucun document pour l'instant": "No document yet",
  "Aucun document pour le moment.": "No document yet.",
  "Aucun document principal pour le moment": "No main document yet",
  "Aucun dossier en ralentissement — votre pipeline avance bien.": "No stalled case — your pipeline is moving well.",
  "Aucun doublon détecté": "No duplicate detected",
  "Aucun jalon défini.": "No milestone defined.",
  "Aucun jalon défini. Ajoutez la première étape de la feuille de route.": "No milestone defined. Add the first step of the roadmap.",
  "Aucun jalon — ajoutez la première étape de la feuille de route.": "No milestone — add the first step of the roadmap.",
  "Aucun jalon — vous pourrez en ajouter plus tard.": "No milestone — you can add some later.",
  "Aucun joueur.": "No player.",
  "Aucun moyen de paiement enregistré. Il sera ajouté de manière sécurisée via Stripe lors de l'activation.": "No payment method saved. It will be added securely via Stripe upon activation.",
  "Aucun objectif actif.": "No active goal.",
  "Aucun objectif défini. Ajoutez 3 à 5 objectifs de développement.": "No goal defined. Add 3 to 5 development goals.",
  "Aucun objectif — ajoutez les priorités des prochains mois.": "No goal — add the priorities for the coming months.",
  "Aucun objectif — vous pourrez en ajouter plus tard.": "No goal — you can add some later.",
  "Aucun plan de carrière": "No career plan",
  "Aucun point de vigilance actif. 👍": "No active watch point. 👍",
  "Aucun résultat": "No results",
  "Aucun signataire pour le moment. Ajoutez les parties devant signer ce contrat.": "No signer yet. Add the parties who must sign this contract.",
  "Aucun élément dans cette catégorie.": "No item in this category.",
  "Aucun événement ce jour-là": "No event that day",
  "Aucun événement pour ce filtre.": "No event for this filter.",
  "Aucun événement.": "No event.",
  "Aucune action en cours pour cet athlète.": "No ongoing action for this athlete.",
  "Aucune action planifiée. Planifiez une relance, un appel ou un envoi de profils.": "No planned action. Schedule a follow-up, a call or a profile send.",
  "Aucune action requise": "No action required",
  "Aucune action urgente.": "No urgent action.",
  "Aucune activité": "No activity",
  "Aucune activité dans cette catégorie.": "No activity in this category.",
  "Aucune activité planifiée.": "No planned activity.",
  "Aucune donnée pour la période sélectionnée.": "No data for the selected period.",
  "Aucune entrée pour le moment.": "No entry yet.",
  "Aucune expérience — ajoutez les saisons marquantes du parcours.": "No experience — add the key seasons of the career.",
  "Aucune facture pour le moment": "No invoice yet",
  "Aucune interaction.": "No interaction.",
  "Aucune opportunité enregistrée pour le moment.": "No opportunity recorded yet.",
  "Aucune version pour le moment. Ajoutez un document depuis l'onglet Documents.": "No version yet. Add a document from the Documents tab.",
  "Blocages carrière": "Career blockers",
  "Brief de réunion": "Meeting brief",
  "CSV ou Excel. Le mapping des colonnes est suggéré automatiquement par l'IA.": "CSV or Excel. Column mapping is automatically suggested by AI.",
  "Calcul en attente": "Pending computation",
  "Calculé automatiquement à partir des données disponibles — ajustez si nécessaire.": "Computed automatically from available data — adjust if needed.",
  "Carrière & faits marquants": "Career & highlights",
  "Ce contact semble déjà exister.": "This contact seems to already exist.",
  "Ce contrat n'existe pas ou a été supprimé.": "This contract does not exist or was deleted.",
  "Certification & intégrité": "Certification & integrity",
  "Cette action est définitive.": "This action is final.",
  "Champ du template": "Template field",
  "Changer de thème": "Switch theme",
  "Changer le statut": "Change status",
  "Chaque envoi crée une nouvelle version horodatée avec son empreinte SHA-256 — aucune version ne peut être modifiée ou supprimée.": "Each send creates a new timestamped version with its SHA-256 fingerprint — no version can be modified or deleted.",
  "Chaque partie devant signer le contrat.": "Each party who must sign the contract.",
  "Chaque événement est chaîné par empreinte SHA-256 : toute modification a posteriori serait détectable.": "Each event is chained by SHA-256 fingerprint: any later modification would be detectable.",
  "Chargement des données…": "Loading data…",
  "Choisir dans mes contacts": "Pick from my contacts",
  "Choisir l'offre à comparer": "Choose the offer to compare",
  "Choisir un fichier de statistiques": "Choose a statistics file",
  "Choisir une offre": "Choose a plan",
  "Choisissez l'offre qui correspond le mieux à vos besoins.": "Choose the plan that best fits your needs.",
  "Choisissez un modèle — la phrase se crée automatiquement, puis reste modifiable.": "Choose a template — the sentence is created automatically, then remains editable.",
  "Choisissez une action pour chaque joueur déjà présent dans votre base.": "Choose an action for each player already in your base.",
  "Chronologie des signatures": "Signature timeline",
  "Cliquez pour inclure — apparaissent dans l'encart rouge du PDF.": "Click to include — they appear in the red box of the PDF.",
  "Club lié": "Linked club",
  "Code d'authentification ou code de récupération": "Authentication code or recovery code",
  "Code de vérification": "Verification code",
  "Comparaison : début": "Comparison: start",
  "Comparaison côte à côte et résumé IA des différences entre versions — disponibles en Phase 2.": "Side-by-side comparison and AI summary of differences between versions — available in Phase 2.",
  "Comparez les fonctionnalités Athlynx et choisissez le niveau d'accompagnement qui correspond à vos besoins.": "Compare Athlynx features and choose the level of support that fits your needs.",
  "Compile la situation réelle du dossier : objectifs, jalons, points de vigilance et questions à aborder.": "Compiles the real state of the case: goals, milestones, watch points and questions to raise.",
  "Compléter manuellement": "Complete manually",
  "Configurez votre compte Athlynx en trois étapes.": "Set up your Athlynx account in three steps.",
  "Confirmation du changement d'offre": "Plan change confirmation",
  "Confirmer le plan d'action": "Confirm action plan",
  "Connexion en cours…": "Signing in…",
  "Conservez ces codes dans un endroit sûr. Ils permettent d'accéder à votre compte si vous perdez votre application d'authentification.": "Keep these codes in a safe place. They let you access your account if you lose your authenticator app.",
  "Contact joueur non renseigné": "Player contact not provided",
  "Contact lié": "Linked contact",
  "Contexte, spécialités, informations utiles…": "Context, specialties, useful information…",
  "Continuez à enregistrer vos interactions, changements de statut et résultats pour suivre votre progression dans le temps.": "Keep recording your interactions, status changes and results to track your progress over time.",
  "Contrat & marché": "Contract & market",
  "Contrat introuvable": "Contract not found",
  "Crée une relance dans votre système « À relancer ».": "Creates a follow-up in your Follow-ups system.",
  "Créer quand même": "Create anyway",
  "Créer un événement": "Create an event",
  "Créez une fiche club complète pour votre portefeuille.": "Create a complete club profile for your portfolio.",
  "Créez vos premiers contrats pour générer votre rapport d'intelligence contractuelle.": "Create your first contracts to generate your contract intelligence report.",
  "Dans votre fichier": "In your file",
  "Date d'échéance": "Due date",
  "Dernière interaction": "Last interaction",
  "Dernières activités": "Latest activities",
  "Destination du QR code imprimé sur le document.": "Destination of the QR code printed on the document.",
  "Dissocier cet athlète ?": "Unlink this athlete?",
  "Documents bientôt expirés": "Documents expiring soon",
  "Documents de l'athlète — gérés depuis la fiche joueur": "Athlete documents — managed from the player profile",
  "Documents du dossier": "Case documents",
  "Documents expirés": "Expired documents",
  "Documents récents": "Recent documents",
  "Données manquantes": "Missing data",
  "Décrivez la vision de carrière…": "Describe the career vision…",
  "Décrivez vos besoins, notre équipe revient vers vous rapidement.": "Describe your needs, our team will get back to you quickly.",
  "Définir": "Set",
  "Définir des objectifs": "Set goals",
  "Définissez un objectif de développement suivi dans le plan.": "Define a development goal tracked in the plan.",
  "Définissez une date d'expiration si applicable (carte d'identité, contrat, etc.).": "Set an expiration date if applicable (ID card, contract, etc.).",
  "Déjà dans Athlynx": "Already in Athlynx",
  "Déjà existant": "Already exists",
  "Département": "Department",
  "Désactiver la double authentification": "Disable two-factor authentication",
  "Détail de l'interaction…": "Interaction details…",
  "Détail de la journée — agenda, alertes et actions rapides.": "Day details — agenda, alerts and quick actions.",
  "Détail du jalon": "Milestone details",
  "Détaillez l'échange, la proposition ou la note…": "Detail the exchange, proposal or note…",
  "Détectés automatiquement à partir des données réelles du dossier (contrat, documents, objectifs, pipeline).": "Automatically detected from the real case data (contract, documents, goals, pipeline).",
  "Empreinte SHA-256 du document principal": "SHA-256 fingerprint of the main document",
  "Enregistrer l'activité": "Save activity",
  "Enregistrez une opportunité réelle identifiée pour cet athlète.": "Record a real opportunity identified for this athlete.",
  "Entrée pour ajouter · plusieurs participants possibles": "Enter to add · multiple participants possible",
  "Environnement recherché": "Target environment",
  "Essayez une autre catégorie ou élargissez votre recherche.": "Try another category or broaden your search.",
  "Ex : Contrat 2026-2028": "E.g.: Contract 2026-2028",
  "Ex. 32 matchs · 9 buts · 11 passes décisives": "E.g. 32 matches · 9 goals · 11 assists",
  "Ex. : Appeler le club pour ouvrir la discussion": "E.g.: Call the club to open the discussion",
  "Ex. Appel avec le directeur sportif": "E.g. Call with the sporting director",
  "Ex. Appeler le club pour le point mensuel": "E.g. Call the club for the monthly check-in",
  "Ex. Devenir titulaire en Ligue 2 d'ici 24 mois, puis viser la Ligue 1…": "E.g. Become a starter in Ligue 2 within 24 months, then aim for Ligue 1…",
  "Ex. Intérêt du RC Strasbourg": "E.g. Interest from RC Strasbourg",
  "Ex. Relancer le directeur sportif": "E.g. Follow up with the sporting director",
  "Ex. Rencontré au tournoi de Lyon…": "E.g. Met at the Lyon tournament…",
  "Ex. Signer dans un club de Ligue 2": "E.g. Sign with a Ligue 2 club",
  "Ex. Stade, bureau, visioconférence…": "E.g. Stadium, office, video call…",
  "Ex. Valider la stratégie mercato": "E.g. Validate the transfer-window strategy",
  "Ex. décideur, recrutement": "E.g. decision-maker, recruitment",
  "Exporter en CSV (KPI + évolution)": "Export to CSV (KPIs + trend)",
  "FICHE ATHLÈTE / NÉGOCIATION": "ATHLETE SHEET / NEGOTIATION",
  "Facturé mensuellement": "Billed monthly",
  "Facturé annuellement": "Billed annually",
  "Fermer l'aperçu": "Close preview",
  "Fiche générée avec succès": "Sheet generated successfully",
  "Fiche joueur": "Player profile",
  "Fin d'accès le": "Access ends on",
  "Fin de contrat actuelle (optionnel)": "Current contract end (optional)",
  "Fonctionnalité": "Feature",
  "Fonctionnalités débloquées :": "Unlocked features:",
  "Forces du profil": "Profile strengths",
  "Formats supportés": "Supported formats",
  "Formats supportés : CSV, XLSX, PDF — max 8 Mo": "Supported formats: CSV, XLSX, PDF — max 8 MB",
  "Français": "French",
  "Funnel de conversion du pipeline": "Pipeline conversion funnel",
  "Glissez un document ici, ou": "Drag a document here, or",
  "Glissez-déposez ou cliquez pour importer une image": "Drag and drop or click to import an image",
  "Glissez-déposez votre fichier ici": "Drag and drop your file here",
  "Génération…": "Generating…",
  "Générer la fiche de négociation": "Generate negotiation sheet",
  "Générer le PDF": "Generate PDF",
  "Générer le Plan de Carrière": "Generate the Career Plan",
  "Générez une feuille de route professionnelle prête à partager avec les athlètes, clubs et partenaires.": "Generate a professional roadmap ready to share with athletes, clubs and partners.",
  "Généré avec Athlynx": "Generated with Athlynx",
  "Gérer la double authentification": "Manage two-factor authentication",
  "Gérez votre abonnement, vos paiements et vos factures.": "Manage your subscription, payments and invoices.",
  "Heure de début": "Start time",
  "Historique de négociation": "Negotiation history",
  "Historique des factures": "Invoice history",
  "Historique des statuts": "Status history",
  "Historique du plan": "Plan history",
  "Horizon du plan": "Plan horizon",
  "Identité de l'athlète": "Athlete identity",
  "Ils ne seront plus affichés.": "They will no longer be displayed.",
  "Importer des données": "Import data",
  "Importer des joueurs": "Import players",
  "Importer le logo du club": "Import the club logo",
  "Importer les statistiques pour la fiche de négociation": "Import statistics for the negotiation sheet",
  "Importer un fichier de contacts (.vcf ou .csv)": "Import a contacts file (.vcf or .csv)",
  "Importez plusieurs joueurs en une seule opération": "Import several players in a single operation",
  "Impossible de charger ce dossier de renouvellement": "Unable to load this renewal case",
  "Impossible de charger cette équipe.": "Unable to load this organization.",
  "Impossible de charger l'activité.": "Unable to load activity.",
  "Impossible de charger le catalogue des offres.": "Unable to load the plan catalog.",
  "Impossible de charger le plan de carrière.": "Unable to load the career plan.",
  "Impossible de charger vos données de performance.": "Unable to load your performance data.",
  "Indicateur interne de pilotage — calculé sur la progression des objectifs et jalons.": "Internal steering indicator — computed from goal and milestone progress.",
  "Indicateur interne de préparation du dossier — ne prédit pas la réussite sportive.": "Internal case-readiness indicator — does not predict sporting success.",
  "Indicateurs d'aide à la décision — ne prédisent pas le succès sportif.": "Decision-support indicators — they do not predict sporting success.",
  "Indice de santé": "Health index",
  "Informations complémentaires sur le club…": "Additional information about the club…",
  "Instantané opérationnel : athlètes uniques dont la prochaine relance est due aujourd'hui ou en retard — identique au badge latéral, à la page À relancer et à Athlynx Intelligence. Indépendant de la période sélectionnée.": "Operational snapshot: unique athletes whose next follow-up is due today or overdue — identical to the sidebar badge, the Follow-ups page and Athlynx Intelligence. Independent of the selected period.",
  "J'ai enregistré mes codes de récupération": "I have saved my recovery codes",
  "Jalon clé": "Key milestone",
  "Jalons de la feuille de route": "Roadmap milestones",
  "Je comprends que certaines fonctionnalités deviendront accessibles en lecture seule.": "I understand that some features will become read-only.",
  "Joueurs concernés": "Players involved",
  "L'IA détecte et associe automatiquement vos colonnes au format Athlynx.": "AI automatically detects and maps your columns to the Athlynx format.",
  "L'historique apparaîtra ici au fil des actions sur le plan.": "History will appear here as actions happen on the plan.",
  "L'édition avancée modifie directement les données utilisées pour le document.": "Advanced editing directly modifies the data used for the document.",
  "Le PDF A4 sera créé avec les données vérifiées puis enregistré comme nouvelle version dans les Documents de l'athlète.": "The A4 PDF will be created with the verified data then saved as a new version in the athlete's Documents.",
  "Le brief est construit uniquement à partir des données enregistrées dans Athlynx — aucun fait n'est inventé.": "The brief is built only from data recorded in Athlynx — no fact is invented.",
  "Le contrat restera consultable, ses documents et son historique sont conservés de façon immuable.": "The contract will remain viewable; its documents and history are kept immutably.",
  "Le résumé apparaîtra ici — proposez-en un ou rédigez librement.": "The summary will appear here — suggest one or write freely.",
  "Les données de progression apparaîtront ici au fil du temps.": "Progress data will appear here over time.",
  "Les dossiers qui stagnent le plus dans le pipeline.": "The cases stalling the longest in the pipeline.",
  "Les informations pourront être modifiées ensuite.": "The information can be edited afterwards.",
  "Les modifications de cet événement seront perdues.": "Changes to this event will be lost.",
  "Liées au système « À relancer » — aucune donnée dupliquée": "Linked to the Follow-ups system — no duplicated data",
  "Logo du club": "Club logo",
  "Masque les heures et conserve uniquement la date.": "Hides times and keeps only the date.",
  "Mesurez l'efficacité, la fiabilité et l'évolution de votre activité contractuelle.": "Measure the efficiency, reliability and evolution of your contract activity.",
  "Mettez à jour votre moyen de paiement.": "Update your payment method.",
  "Mettre à jour": "Update",
  "Mettre à jour la dernière fiche": "Update the latest sheet",
  "Modifier l'expiration": "Edit expiration",
  "Modifier l'opportunité": "Edit opportunity",
  "Modifier l'équipe": "Edit organization",
  "Modifier la date d'expiration": "Edit expiration date",
  "Modifier la vision carrière": "Edit career vision",
  "Modèles de vision": "Vision templates",
  "Mois précédent": "Previous month",
  "Mois suivant": "Next month",
  "Média & dossier complet": "Media & full file",
  "Navigation des paramètres": "Settings navigation",
  "Nom de l'équipe": "Organization name",
  "Nom de l'événement": "Event name",
  "Nom du document": "Document name",
  "Notes privées — jamais incluses dans un PDF.": "Private notes — never included in a PDF.",
  "Nouveau": "New",
  "Nouveau club": "New club",
  "Nouveau jalon": "New milestone",
  "Nouveau rappel": "New reminder",
  "Nouveau tag…": "New tag…",
  "Nouvelle fiche": "New sheet",
  "Objectif de la réunion (optionnel)": "Meeting goal (optional)",
  "Objectifs de développement": "Development goals",
  "Objectifs en cours": "Ongoing goals",
  "Opportunités en cours": "Ongoing opportunities",
  "Options du document": "Document options",
  "Ou importer depuis une source rapide": "Or import from a quick source",
  "Ouvrir les contrats": "Open contracts",
  "PDF, DOCX, images… max 15 Mo. Chaque version est immuable et horodatée (SHA-256).": "PDF, DOCX, images… max 15 MB. Each version is immutable and timestamped (SHA-256).",
  "PLAN DE CARRIÈRE": "CAREER PLAN",
  "PROFIL IMPRIMABLE POUR NÉGOCIATIONS ET PRÉSENTATIONS": "PRINTABLE PROFILE FOR NEGOTIATIONS AND PRESENTATIONS",
  "Page précédente": "Previous page",
  "Page suivante": "Next page",
  "Paiement bientôt disponible": "Payment available soon",
  "Paiement reçu, activation en cours…": "Payment received, activation in progress…",
  "Par défaut": "Default",
  "Paramètres": "Settings",
  "Parcourir les fichiers": "Browse files",
  "Part des contrats envoyés qui ont été signés par toutes les parties.": "Share of sent contracts signed by all parties.",
  "Pas assez de données historiques": "Not enough historical data",
  "Pas encore de données contractuelles": "No contract data yet",
  "Passer à la génération": "Proceed to generation",
  "Passez à Athlynx Pro": "Upgrade to Athlynx Pro",
  "Pays, région ou division…": "Country, region or division…",
  "Performance de négociation": "Negotiation performance",
  "Performance des contrats": "Contract performance",
  "Plan d'action proposé": "Suggested action plan",
  "Planifier la prochaine action": "Plan the next action",
  "Planifier un rendez-vous": "Schedule a meeting",
  "Planifiez une relance, un rendez-vous club ou une deadline contrat.": "Schedule a follow-up, a club meeting or a contract deadline.",
  "Plein écran": "Fullscreen",
  "Postes selon le sport sélectionné": "Positions based on the selected sport",
  "Pourquoi ce score ?": "Why this score?",
  "Priorité déterministe calculée à partir de vos données réelles (échéance, actions, activité).": "Deterministic priority computed from your real data (deadline, actions, activity).",
  "Prochains rendez-vous": "Upcoming meetings",
  "Programmé": "Scheduled",
  "Progression, responsable et priorité — la couleur s'adapte automatiquement.": "Progress, owner and priority — the color adapts automatically.",
  "Protégez votre compte avec une application d'authentification (Google Authenticator, Authy, 1Password…).": "Protect your account with an authenticator app (Google Authenticator, Authy, 1Password…).",
  "Pré-rempli depuis le profil — cliquez sur une valeur pour la modifier.": "Pre-filled from the profile — click a value to edit it.",
  "Pré-rempli depuis votre compte — affiché sur la page finale du PDF.": "Pre-filled from your account — shown on the final page of the PDF.",
  "Préparation du dossier": "Case preparation",
  "Préparation…": "Preparing…",
  "Préparer une autre réunion": "Prepare another meeting",
  "Préparer une réunion": "Prepare a meeting",
  "Période": "Period",
  "Période actuelle": "Current period",
  "Période actuelle :": "Current period:",
  "Période personnalisée": "Custom period",
  "Période précédente :": "Previous period:",
  "Questions à poser": "Questions to ask",
  "Quitter sans enregistrer ?": "Leave without saving?",
  "Rechercher dans votre portefeuille…": "Search your portfolio…",
  "Rechercher un joueur, club, événement, document…": "Search a player, club, event, document…",
  "Rechercher une activité…": "Search an activity…",
  "Reformuler avec l'IA": "Rephrase with AI",
  "Relance en retard": "Overdue follow-up",
  "Relances en retard": "Overdue follow-ups",
  "Relation configurée": "Relationship configured",
  "Renouvellements · Performance financière · Score de conformité · Athlynx Intelligence (IA) · Export PDF — arrivent en Phase 2 de ce module.": "Renewals · Financial performance · Compliance score · Athlynx Intelligence (AI) · PDF export — coming in Phase 2 of this module.",
  "Renouvellements à préparer": "Renewals to prepare",
  "Repères de marché génériques (calendrier) — pas des opportunités vérifiées.": "Generic market reference points (calendar) — not verified opportunities.",
  "Retour au plan de carrière": "Back to career plan",
  "Retour au profil": "Back to profile",
  "Retour au profil de l'athlète": "Back to athlete profile",
  "Retour aux équipes": "Back to organizations",
  "Revenir à l'éditeur guidé": "Back to guided editor",
  "Revenir à la valeur automatique": "Back to automatic value",
  "Rythme d'exécution du plan": "Plan execution pace",
  "Récapitulatif": "Summary",
  "Récapitulatif du changement d'offre avant confirmation": "Plan change summary before confirmation",
  "Régénérez vos codes de récupération ou désactivez la double authentification.": "Regenerate your recovery codes or disable two-factor authentication.",
  "Réinitialiser les filtres": "Reset filters",
  "Réordonnez par glisser-déposer ou avec les flèches. La timeline du PDF se met à jour en direct.": "Reorder by drag-and-drop or with the arrows. The PDF timeline updates live.",
  "Répartition pipeline": "Pipeline breakdown",
  "Réseaux / Instagram": "Social / Instagram",
  "Résumé de la saison": "Season summary",
  "Rôle / Fonction": "Role / Function",
  "Sans engagement": "No commitment",
  "Santé de la relation": "Relationship health",
  "Santé de la relation :": "Relationship health:",
  "Santé du portefeuille": "Portfolio health",
  "Score déterministe calculé à partir de vos données réelles : transitions du pipeline (25%), relances (20%), résultats (20%), régularité (15%), temps de réponse (10%), qualité du portefeuille (10%).": "Deterministic score computed from your real data: pipeline transitions (25%), follow-ups (20%), results (20%), consistency (15%), response time (10%), portfolio quality (10%).",
  "Sections du contrat": "Contract sections",
  "Sections équipe": "Organization sections",
  "Seuls les champs non résolus sont affichés.": "Only unresolved fields are shown.",
  "Sous mandat": "Under mandate",
  "Statut actuel": "Current status",
  "Statut relation": "Relationship status",
  "Stripe nous confirme votre paiement. L'activation est généralement immédiate.": "Stripe is confirming your payment. Activation is usually immediate.",
  "Suggestions issues de l'historique — cliquez pour inclure.": "Suggestions from history — click to include.",
  "Suivez la progression de vos indicateurs clés dans le temps.": "Track the progress of your key indicators over time.",
  "Suivi manuel — la signature électronique certifiée arrive prochainement.": "Manual tracking — certified electronic signature coming soon.",
  "Supprimer ce contact ?": "Delete this contact?",
  "Supprimer cet événement ?": "Delete this event?",
  "Supprimer cette opportunité ?": "Delete this opportunity?",
  "Supprimer l'expérience": "Delete experience",
  "Supprimer l'objectif": "Delete goal",
  "Supprimer l'opportunité": "Delete opportunity",
  "Supprimer la note": "Delete note",
  "Supprimer le jalon": "Delete milestone",
  "Sécurisé": "Secure",
  "Sélection multiple.": "Multiple selection.",
  "Sélectionner un athlète": "Select an athlete",
  "Sélectionner un club": "Select a club",
  "Sélectionner un pays": "Select a country",
  "Sélectionner un sport": "Select a sport",
  "Sélectionnez des contacts existants et ajoutez uniquement ceux qui sont utiles à votre réseau Athlynx.": "Select existing contacts and add only those useful to your Athlynx network.",
  "Tags (séparés par des virgules)": "Tags (comma-separated)",
  "Taille de l'équipe": "Organization size",
  "Tape au moins 2 caractères pour rechercher.": "Type at least 2 characters to search.",
  "Taux de réussite": "Success rate",
  "Taux · Temps moyen dans l'étape": "Rate · Average time in stage",
  "Temps dans l'étape": "Time in stage",
  "Titre du jalon": "Milestone title",
  "Titre du jalon…": "Milestone title…",
  "Toujours activée": "Always on",
  "Tous les champs sont renseignés.": "All fields are filled in.",
  "Tous les niveaux": "All levels",
  "Tous les statuts": "All statuses",
  "Tous vos joueurs sont uniques. Vous pouvez continuer en toute sécurité.": "All your players are unique. You can safely continue.",
  "Tout est à jour": "Everything is up to date",
  "Tout sélectionner": "Select all",
  "Tout voir": "View all",
  "Toute la journée": "All day",
  "Toutes": "All",
  "Toutes les informations d'identité sont confirmées depuis le profil Athlynx. ✓": "All identity information is confirmed from the Athlynx profile. ✓",
  "Toutes les évolutions du Career Development Plan": "All changes to the Career Development Plan",
  "Trouvez l'offre adaptée à votre activité": "Find the plan suited to your activity",
  "Type (agence, club, fédération…)": "Type (agency, club, federation…)",
  "Type de réunion": "Meeting type",
  "Télécharger le document": "Download document",
  "URL invalide — elle doit commencer par http:// ou https://": "Invalid URL — it must start with http:// or https://",
  "Un relevé est enregistré automatiquement chaque mois — aucune donnée n'est simulée.": "A snapshot is recorded automatically each month — no data is simulated.",
  "VOTRE CONTACT AGENT": "YOUR AGENT CONTACT",
  "Valeur détectée": "Detected value",
  "Valeur estimée (€)": "Estimated value (€)",
  "Version interne (complète)": "Internal version (full)",
  "Version partageable (sans données sensibles)": "Shareable version (no sensitive data)",
  "Vidéo": "Video",
  "Vision carrière": "Career vision",
  "Voir la fiche complète": "View full profile",
  "Voir la journée complète": "View full day",
  "Voir le calendrier complet": "View full calendar",
  "Voir les détails": "View details",
  "Voir tous": "View all",
  "Voir tout l'historique": "View full history",
  "Vos besoins principaux…": "Your main needs…",
  "Vos données ont été importées avec succès. Vous pouvez maintenant les retrouver dans votre Pipeline.": "Your data has been imported successfully. You can now find it in your Pipeline.",
  "Vos données restent privées": "Your data remains private",
  "Vos factures apparaîtront ici après votre premier paiement.": "Your invoices will appear here after your first payment.",
  "Votre calendrier est vide": "Your calendar is empty",
  "Votre colonne": "Your column",
  "Votre compte n'exigera plus de code d'authentification à la connexion.": "Your account will no longer require an authentication code at sign-in.",
  "Votre compte utilise la connexion Google : cliquez sur Continuer pour démarrer la configuration.": "Your account uses Google sign-in: click Continue to start the setup.",
  "Votre dernier paiement a échoué. Mettez à jour votre carte pour éviter l'interruption.": "Your last payment failed. Update your card to avoid interruption.",
  "Votre essai est terminé. Réactivez pour continuer.": "Your trial has ended. Reactivate to continue.",
  "Votre estimation interne — modifiable à tout moment.": "Your internal estimate — editable at any time.",
  "Votre nouvelle offre prendra effet à la fin de la période en cours. Vos données ne seront pas supprimées, mais ces fonctionnalités deviendront indisponibles :": "Your new plan will take effect at the end of the current period. Your data will not be deleted, but these features will become unavailable:",
  "Votre portefeuille de contrats est à jour.": "Your contract portfolio is up to date.",
  "Vous pourrez ajouter ses coordonnées plus tard.": "You can add their contact details later.",
  "Vous pourrez modifier toutes les données détectées avant la génération du PDF.": "You can edit all detected data before generating the PDF.",
  "Vue détaillée": "Detailed view",
  "Vérifiez, ajustez puis confirmez — rien n'est créé sans votre validation.": "Review, adjust then confirm — nothing is created without your validation.",
  "Zone d'import de fichier — cliquez ou déposez un fichier": "File import zone — click or drop a file",
  "Zoom arrière": "Zoom out",
  "complète": "complete",
  "contrats signés vs abandonnés": "signed vs abandoned contracts",
  "en retard": "overdue",
  "ex: Marc Lefèvre": "e.g. Marc Lefèvre",
  "formation, région lyonnaise…": "youth academy, Lyon region…",
  "incomplète": "incomplete",
  "nouvelle@adresse.fr": "new@address.com",
  "· Importé :": "· Imported:",
  "· Vos vraies données sont préservées et coexistent avec les données de démonstration": "· Your real data is preserved and coexists with the demo data",
  "À faire aujourd'hui": "Due today",
  "À faire maintenant": "Do now",
  "Âge maximum": "Maximum age",
  "Âge minimum": "Minimum age",
  "Échéance (mois)": "Deadline (months)",
  "Échéance (optionnel)": "Deadline (optional)",
  "Échéance en mois": "Deadline in months",
  "Édition avancée": "Advanced editing",
  "Élargir la période": "Widen the period",
  "Étape précédente": "Previous step",
  "Étape suivante": "Next step",
  "Étapes de la fiche": "Sheet steps",
  "Étapes du builder": "Builder steps",
  "Évaluation de l'athlète": "Athlete evaluation",
  "Évaluer": "Evaluate",
  "Évolution de la performance": "Performance trend",
  "Évolution réelle des scores Career Velocity et Readiness (un relevé par mois)": "Real trend of Career Velocity and Readiness scores (one snapshot per month)",
  "Évolution sur 30 jours": "30-day trend",
  "évén.": "events",
  "★ Recommandé": "★ Recommended",
  "✓ Dossier complet (PDF)": "✓ Full file (PDF)",
  "12 derniers mois": "Last 12 months",
  "30 derniers jours": "Last 30 days",
  "6 derniers mois": "Last 6 months",
  "7 derniers jours": "Last 7 days",
  "90 derniers jours": "Last 90 days",
  "Abonnement réactivé": "Subscription reactivated",
  "Abonnement résilié": "Subscription cancelled",
  "Action impossible": "Action failed",
  "Action planifiée — visible dans le calendrier": "Action planned — visible in the calendar",
  "Action terminée": "Action completed",
  "Activité": "Activity",
  "Activité enregistrée": "Activity saved",
  "Activités": "Activities",
  "Ajouter un contact principal": "Add a main contact",
  "Ajouter une opportunité": "Add an opportunity",
  "Algérie": "Algeria",
  "Analyse vidéo": "Video analysis",
  "Annulés": "Cancelled",
  "Année en cours": "Current year",
  "Archivés": "Archived",
  "Athlète créé": "Athlete created",
  "Athlète dissocié de l'équipe": "Athlete unlinked from the organization",
  "Athlète introuvable": "Athlete not found",
  "Attente enregistrée": "Expectation saved",
  "Aucun champ reconnu dans ce fichier.": "No field recognized in this file.",
  "Aucun changement vs. veille": "No change vs. previous day",
  "Aucun contact lisible dans ce fichier": "No readable contact in this file",
  "Aucun contact renseigné pour ce joueur": "No contact provided for this player",
  "Aucune comparaison": "No comparison",
  "Besoin identifié": "Identified need",
  "Bloquée": "Blocked",
  "Bloquées": "Blocked",
  "Brief généré — l'agenda IA n'est pas disponible pour le moment.": "Brief generated — the AI agenda is unavailable right now.",
  "Brésil": "Brazil",
  "Calendrier mis à jour": "Calendar updated",
  "Calendrier à jour": "Calendar up to date",
  "Ce type de fichier n'est pas accepté. (PNG, JPG ou WEBP)": "This file type is not accepted. (PNG, JPG or WEBP)",
  "Cette semaine": "This week",
  "Changement programmé annulé — vous conservez votre offre actuelle.": "Scheduled change cancelled — you keep your current plan.",
  "Changement programmé à la fin de la période de facturation. Vos données sont conservées.": "Change scheduled at the end of the billing period. Your data is kept.",
  "Club créé": "Club created",
  "Club créé, mais l'envoi du logo a échoué.": "Club created, but the logo upload failed.",
  "Clubs ciblés": "Target clubs",
  "Comparaison personnalisée": "Custom comparison",
  "Conditions mises à jour": "Terms updated",
  "Consulté": "Viewed",
  "Contact supprimé": "Contact deleted",
  "Contacté": "Contacted",
  "Contexte retiré — retour à la vue portefeuille.": "Context removed — back to portfolio view.",
  "Contrat archivé": "Contract archived",
  "Contrat principal": "Main contract",
  "Conversation introuvable": "Conversation not found",
  "Copie impossible — sélectionnez le texte manuellement": "Copy failed — select the text manually",
  "Corée du Sud": "South Korea",
  "Créés": "Created",
  "Côte d'Ivoire": "Ivory Coast",
  "Date d'expiration mise à jour": "Expiration date updated",
  "Dissociation impossible": "Unlink failed",
  "Dossier introuvable": "Case not found",
  "Double authentification désactivée": "Two-factor authentication disabled",
  "Doublon ignoré": "Duplicate skipped",
  "Décision": "Decision",
  "Définir le statut de la relation": "Set relationship status",
  "Définir une priorité": "Set a priority",
  "Développement physique": "Physical development",
  "Développement technique": "Technical development",
  "Empreinte SHA-256 copiée": "SHA-256 fingerprint copied",
  "En accélération": "Accelerating",
  "En retard (> 30 j)": "Overdue (> 30 d)",
  "Entraînement": "Training",
  "Entraînements": "Training sessions",
  "Envoi dossier": "File sent",
  "Envoyés": "Sent",
  "Erreur chargement abonnement": "Error loading subscription",
  "Erreur lors de l'import": "Import error",
  "Erreur lors de la mise à jour": "Update error",
  "Export CSV téléchargé": "CSV export downloaded",
  "Fenêtre de marché": "Market window",
  "Fiche de négociation générée": "Negotiation sheet generated",
  "Fiche de négociation premium": "Premium negotiation sheet",
  "Finalisés": "Finalized",
  "Format non supporté. Utilisez un fichier CSV, XLSX ou PDF.": "Unsupported format. Use a CSV, XLSX or PDF file.",
  "Grèce": "Greece",
  "Génération PDF": "PDF generation",
  "Importer les données": "Import data",
  "Impossible d'actualiser le calendrier.": "Unable to refresh the calendar.",
  "Impossible d'ajouter la note": "Unable to add the note",
  "Impossible d'ajouter la piste": "Unable to add the lead",
  "Impossible d'archiver le contrat": "Unable to archive the contract",
  "Impossible d'exporter le plan en PDF": "Unable to export the plan to PDF",
  "Impossible de charger le joueur": "Unable to load the player",
  "Impossible de générer la fiche de négociation.": "Unable to generate the negotiation sheet.",
  "Impossible de générer les suggestions": "Unable to generate suggestions",
  "Impossible de mettre à jour l'objectif": "Unable to update the goal",
  "Impossible de mettre à jour l'opportunité": "Unable to update the opportunity",
  "Impossible de mettre à jour l'évaluation": "Unable to update the evaluation",
  "Impossible de mettre à jour l'événement.": "Unable to update the event.",
  "Impossible de mettre à jour la vision": "Unable to update the vision",
  "Impossible de mettre à jour le jalon": "Unable to update the milestone",
  "Impossible de mettre à jour le point de vigilance": "Unable to update the watch point",
  "Impossible de planifier l'action": "Unable to plan the action",
  "Impossible de proposer un résumé": "Unable to suggest a summary",
  "Impossible de préparer la fiche de négociation.": "Unable to prepare the negotiation sheet.",
  "Impossible de supprimer l'objectif": "Unable to delete the goal",
  "Impossible de supprimer l'opportunité": "Unable to delete the opportunity",
  "Impossible de supprimer l'événement.": "Unable to delete the event.",
  "Impossible de supprimer le jalon": "Unable to delete the milestone",
  "Impossible de terminer l'action": "Unable to complete the action",
  "Interaction enregistrée": "Interaction saved",
  "Intérêt club": "Club interest",
  "Jalon ajouté": "Milestone added",
  "Jalon mis à jour": "Milestone updated",
  "Jalon supprimé": "Milestone deleted",
  "Joueur introuvable": "Player not found",
  "Joueur supprimé": "Player deleted",
  "Jour": "Day",
  "L'essai continue — cette offre démarrera à l'activation de votre abonnement.": "The trial continues — this plan will start when your subscription is activated.",
  "La double authentification est maintenant activée.": "Two-factor authentication is now enabled.",
  "La vision carrière est requise": "The career vision is required",
  "Le changement prendra effet à la fin de votre période en cours.": "The change will take effect at the end of your current period.",
  "Le logo dépasse la taille maximale autorisée (5 Mo).": "The logo exceeds the maximum allowed size (5 MB).",
  "Le nom de l'équipe est requis": "The organization name is required",
  "Le profil de l'athlète est introuvable.": "The athlete profile could not be found.",
  "Mapping détecté automatiquement": "Mapping detected automatically",
  "Mise à jour impossible": "Update failed",
  "Mise à niveau immédiate après confirmation.": "Immediate upgrade after confirmation.",
  "Mode démo : aucune modification Stripe réelle.": "Demo mode: no real Stripe changes.",
  "Modifié manuellement": "Edited manually",
  "Mois en cours": "Current month",
  "Même période l'an dernier": "Same period last year",
  "Norvège": "Norway",
  "Note et échéance requises": "Note and due date required",
  "Notes enregistrées": "Notes saved",
  "Nouveaux codes générés — les anciens sont invalidés": "New codes generated — the old ones are invalidated",
  "Nouvelle-Zélande": "New Zealand",
  "Négociation sponsoring": "Sponsorship negotiation",
  "Objectif ajouté": "Goal added",
  "Objectif supprimé": "Goal deleted",
  "Objectif terminé": "Goal completed",
  "Offre enregistrée — elle démarrera à l'activation de votre abonnement.": "Plan saved — it will start when your subscription is activated.",
  "Opportunité ajoutée": "Opportunity added",
  "Opportunité supprimée": "Opportunity deleted",
  "Paiement annulé": "Payment cancelled",
  "Personnalisé": "Custom",
  "Piste ajoutée au suivi": "Lead added to tracking",
  "Placé": "Placed",
  "Placés": "Placed",
  "Plan de carrière créé": "Career plan created",
  "Plan exporté en PDF": "Plan exported to PDF",
  "Planifier une prochaine action": "Plan a next action",
  "Point d'attention enregistré": "Watch point saved",
  "Point de vigilance traité": "Watch point resolved",
  "Point traité": "Point resolved",
  "Premier échange": "First exchange",
  "Prochaine action planifiée": "Next action planned",
  "Proposition de représentation": "Representation proposal",
  "Prévu": "Planned",
  "Prêt": "Loan",
  "Prêt avec option d'achat": "Loan with option to buy",
  "Période précédente": "Previous period",
  "Qualité du portefeuille": "Portfolio quality",
  "Quotas IA étendus": "Extended AI quotas",
  "Rappel créé": "Reminder created",
  "Rapports avancés": "Advanced reports",
  "Reformulation IA indisponible — le résumé actuel est conservé": "AI rephrasing unavailable — the current summary is kept",
  "Rejeté": "Rejected",
  "Relance enregistrée": "Follow-up saved",
  "Relance planifiée": "Follow-up planned",
  "Relance terminée": "Follow-up completed",
  "Relances effectuées": "Follow-ups completed",
  "Rendez-vous ajouté au calendrier": "Meeting added to calendar",
  "Repéré": "Scouted",
  "En discussion": "In discussion",
  "Mandat signé": "Signed mandate",
  "Sous contrat": "Under contract",
  "Fin de contrat proche": "Contract ending soon",
  "Libre": "Free agent",
  "En prêt": "On loan",
  "Contrat amateur": "Amateur contract",
  "Sans contrat pro": "No pro contract",
  "Saisie manuelle": "Manual entry",
  "Agent libre": "Free agent",
  "Prolongation": "Extension",
  "Autre": "Other",
  "Réactiver": "Reactivate",
  "Régularité de l'activité": "Activity consistency",
  "Régularité des relances": "Follow-up consistency",
  "Réponse reçue": "Reply received",
  "Résultats & conversions": "Results & conversions",
  "Résumé reformulé — relisez et approuvez avant inclusion": "Summary rephrased — review and approve before including",
  "Résumés IA": "AI summaries",
  "Réunion préparée": "Meeting prepared",
  "Réunions": "Meetings",
  "Santé": "Health",
  "Semaine": "Week",
  "Session de paiement expirée": "Payment session expired",
  "Signataire retiré": "Signer removed",
  "Slovénie": "Slovenia",
  "Suède": "Sweden",
  "Sélection": "Selection",
  "Sélection nationale": "National team",
  "Sénégal": "Senegal",
  "Tchéquie": "Czechia",
  "Temps de jeu / activité": "Playing time / activity",
  "Temps de réponse": "Response time",
  "Toutes les catégories": "All categories",
  "Traiter la réponse": "Handle the reply",
  "Transfert définitif": "Permanent transfer",
  "Trimestre en cours": "Current quarter",
  "Tâche": "Task",
  "Tâches": "Tasks",
  "Téléchargement impossible": "Download failed",
  "Téléchargement impossible.": "Download failed.",
  "Visibilité": "Visibility",
  "Vision mise à jour": "Vision updated",
  "Vision reformulée par l'IA": "Vision rephrased by AI",
  "Voir le message envoyé": "View sent message",
  "Voir les documents": "View documents",
  "Voir les renouvellements": "View renewals",
  "Votre abonnement est maintenant actif !": "Your subscription is now active!",
  "Votre offre a été mise à niveau. La proration est gérée par Stripe.": "Your plan has been upgraded. Proration is handled by Stripe.",
  "Vous n'avez pas accès à cet athlète.": "You don't have access to this athlete.",
  "Vous êtes déjà sur cette offre.": "You are already on this plan.",
  "Vérification": "Verification",
  "relance": "follow-up",
  "rendez-vous club cette semaine": "club meetings this week",
  "À relancer (> 14 j)": "To follow up (> 14 d)",
  "Écrivez d'abord une vision à reformuler": "Write a vision to rephrase first",
  "Égypte": "Egypt",
  "Émirats arabes unis": "United Arab Emirates",
  "Équipe créée": "Organization created",
  "Équipe mise à jour": "Organization updated",
  "Évaluation mise à jour": "Evaluation updated",
  "Événement ajouté au calendrier": "Event added to calendar",
  "Événement supprimé": "Event deleted",
  "Élevé": "High",
  "États-Unis": "United States",
  "Acceptée": "Accepted",
  "Aucune relance en retard.": "No overdue follow-up.",
  "Aucune relance prévue aujourd'hui.": "No follow-up due today.",
  "Aucune relance terminée.": "No completed follow-up.",
  "Aucune relance à venir.": "No upcoming follow-up.",
  "Ciblé": "Targeted",
  "Compléter les champs vides": "Fill empty fields",
  "Complétez les profils et gardez les documents à jour.": "Complete profiles and keep documents up to date.",
  "Contactez les nouveaux athlètes dans les 24 heures.": "Contact new athletes within 24 hours.",
  "Coordonnées de l'agent": "Agent contact details",
  "DISPONIBILITÉ": "AVAILABILITY",
  "Données": "Data",
  "Enregistrez des interactions plusieurs jours par semaine.": "Record interactions several days a week.",
  "FIN DE CONTRAT": "CONTRACT END",
  "Faites avancer vos athlètes d'étape en étape.": "Move your athletes forward stage by stage.",
  "Garder la fiche existante": "Keep the existing record",
  "INDEMNITÉ / CLAUSE": "FEE / CLAUSE",
  "Interagissez avec chaque athlète actif au moins tous les 14 jours.": "Interact with every active athlete at least every 14 days.",
  "MARCHÉ PRÉFÉRÉ": "PREFERRED MARKET",
  "Marché préféré": "Preferred market",
  "Message envoyé ?": "Message sent?",
  "NATIONALITÉ": "NATIONALITY",
  "Nouveau contact": "New contact",
  "Négociations clubs": "Club negotiations",
  "Opportunité": "Opportunity",
  "PRÉTENTIONS SALARIALES": "SALARY EXPECTATIONS",
  "Profil athlète (points forts)": "Athlete profile (strengths)",
  "Proposé": "Proposed",
  "Prétentions": "Expectations",
  "Père": "Father",
  "QR code (accès sécurisé au dossier)": "QR code (secure access to the file)",
  "Refusée": "Rejected",
  "Représentation sportive": "Sports representation",
  "Retirée": "Withdrawn",
  "STATUT": "STATUS",
  "Sans réponse": "No reply",
  "Section contrat & marché": "Contract & market section",
  "Terminés": "Ended",
  "Transformez les discussions en mandats signés.": "Turn discussions into signed mandates.",
  "VALEUR MARCHANDE ESTIMÉE": "ESTIMATED MARKET VALUE",
  "Valeur de marché": "Market value",
  "Valeur enregistrée": "Value saved",
  "Vidéo (MP4)": "Video (MP4)",
  "Votre essai est expiré — abonnez-vous pour ajouter des documents.": "Your trial has expired — subscribe to add documents.",
  "Votre essai est expiré. Réactivez votre abonnement pour générer et enregistrer la fiche.": "Your trial has expired. Reactivate your subscription to generate and save the sheet.",
  "joueur contacté": "player contacted",
  "joueur placé": "player placed",
  "joueurs contactés": "players contacted",
  "joueurs placés": "players placed",
  "À lancer": "To launch",
  "À renouveler": "To renew",
  "ÂGE": "AGE",
  "Écraser avec les nouvelles données": "Overwrite with the new data",
  "Belgique": "Belgium", "Suisse": "Switzerland", "Allemagne": "Germany", "Espagne": "Spain",
  "Italie": "Italy", "Angleterre": "England", "Pays-Bas": "Netherlands",
  "Top 5 européen": "European top 5", "Moyen-Orient": "Middle East",
  "Saison 2024/2025": "Season 2024/2025", "Saison 2025/2026": "Season 2025/2026", "Saison 2026/2027": "Season 2026/2027",
  "Trophée": "Trophy", "Record personnel": "Personal record",
  "Premier contrat professionnel": "First professional contract", "Capitanat": "Captaincy", "Tournoi majeur": "Major tournament",
  "Au club": "At club", "Essai": "Trial", "Transfert": "Transfer", "Fin de contrat": "Contract end", "Ancien": "Former",
  "Prospect": "Prospect", "Contact établi": "Contact established", "Relation active": "Active relationship",
  "Partenaire": "Partner", "Inactif": "Inactive", "À réactiver": "To reactivate",
  "Faible": "Low", "Normale": "Normal", "Haute": "High", "Stratégique": "Strategic",
  "Recrutement jeunes": "Youth recruitment", "Suivi de joueurs": "Player monitoring",
  "Partenariat académie": "Academy partnership", "Échange de données": "Data exchange", "Événements / visibilité": "Events / visibility",
  "Prospection": "Prospecting", "Gagnée": "Won", "Perdue": "Lost",
  "Recrutement": "Recruitment", "Partenariat": "Partnership", "Sponsoring": "Sponsorship", "Formation": "Youth development",
  "Collaborations": "Collaborations", "Scouting": "Scouting",
  "Directeur sportif": "Sporting director", "Recruteur": "Recruiter", "Scout": "Scout", "Président": "President",
  "Entraîneur": "Coach", "Responsable recrutement": "Head of recruitment", "Responsable formation": "Head of youth development",
  "Juridique": "Legal", "Médical": "Medical", "Intermédiaire": "Intermediary",
  "Sponsoring / Partenariats": "Sponsorship / Partnerships", "Communication": "Communication",
  "Anglais": "English", "Espagnol": "Spanish", "Italien": "Italian", "Allemand": "German", "Portugais": "Portuguese", "Arabe": "Arabic",
  "automatique": "automatic",
  "+ Plus de détails": "+ More details",
  "1. Ouvrez votre application (Google Authenticator, Authy, 1Password…) · 2. Scannez le QR code · 3. Saisissez le code à 6 chiffres.": "1. Open your app (Google Authenticator, Authy, 1Password…) · 2. Scan the QR code · 3. Enter the 6-digit code.",
  "30 jours": "30 days",
  "Afficher les filtres": "Show filters",
  "Affinez votre pipeline": "Refine your pipeline",
  "Ajouter le contact": "Add contact",
  "Ajouter un": "Add a",
  "Ajouter une expiration": "Add an expiration",
  "Ajoutez des CV, vidéos, statistiques ou tout autre document lié au joueur.": "Add CVs, videos, statistics or any other document related to the player.",
  "Ajoutez une couche de sécurité supplémentaire à votre compte Athlynx. Cette protection est optionnelle et reste sous votre contrôle.": "Add an extra layer of security to your Athlynx account. This protection is optional and stays under your control.",
  "Ajoutez votre interlocuteur principal pour centraliser vos échanges avec cette équipe.": "Add your main contact to centralize your exchanges with this organization.",
  "Appel effectué ?": "Call made?",
  "Aucun athlète associé à cette équipe.": "No athlete linked to this organization.",
  "Aucun contact pour cette équipe. Identifiez vos interlocuteurs clés.": "No contact for this organization. Identify your key contacts.",
  "Aucun document": "No document",
  "Aucun document lié.": "No linked document.",
  "Aucun rendez-vous à venir.": "No upcoming meeting.",
  "Aucun événement ce jour-là.": "No event that day.",
  "Aucune opportunité active.": "No active opportunity.",
  "Cliquez sur une date pour voir les événements.": "Click a date to see events.",
  "Clé copiée": "Key copied",
  "Codes copiés": "Codes copied",
  "Codes de récupération": "Recovery codes",
  "Codes de récupération Athlynx": "Athlynx recovery codes",
  "Configuration de la relation :": "Relationship setup:",
  "Configurer une application d'authentification": "Set up an authenticator app",
  "Confirmez votre identité pour commencer.": "Confirm your identity to begin.",
  "Contact ajouté": "Contact added",
  "Contact modifié": "Contact updated",
  "Contrats créés / semaine": "Contracts created / week",
  "Contrats signés": "Signed contracts",
  "Créer une fiche de négociation pour": "Create a negotiation sheet for",
  "Créer une opportunité": "Create an opportunity",
  "Date d'expiration (YYYY-MM-DD) :": "Expiration date (YYYY-MM-DD):",
  "Dernière étape : enregistrez vos codes de récupération.": "Last step: save your recovery codes.",
  "Durée moyenne": "Average duration",
  "Délai moyen d'ouverture": "Average time to open",
  "Délai moyen de signature": "Average time to sign",
  "Désactivation…": "Disabling…",
  "Efficacité des relances": "Follow-up efficiency",
  "Email copié": "Email copied",
  "Envoyer un email à": "Send an email to",
  "Envoyer un message à": "Send a message to",
  "Expiration retirée": "Expiration removed",
  "Fait aujourd'hui à": "Done today at",
  "Fait le": "Done on",
  "Fusionnés": "Merged",
  "Glissez un fichier ici ou cliquez pour parcourir.": "Drag a file here or click to browse.",
  "Générer": "Generate",
    "Les alertes email seront envoyées dès que l'envoi d'emails sera configuré (clé Resend). Vos préférences sont enregistrées dès maintenant.": "Email alerts will be sent once email delivery is configured (Resend key). Your preferences are saved right away.",
  "Les interactions significatives apparaîtront ici.": "Meaningful interactions will appear here.",
  "Les interactions significatives avec cette équipe apparaîtront ici.": "Meaningful interactions with this organization will appear here.",
  "Masquer les détails": "Hide details",
  "Modifier la date d'expiration (YYYY-MM-DD) ou laisser vide pour retirer :": "Edit the expiration date (YYYY-MM-DD) or leave empty to remove:",
  "Modifier le contact": "Edit contact",
  "Mois le plus productif": "Most productive month",
  "Montant estimé aujourd'hui": "Estimated amount today",
  "Non configurée": "Not configured",
  "Nouvelle offre": "New offer",
  "Numéro copié": "Number copied",
  "Négociation la plus longue": "Longest negotiation",
  "Négociations abandonnées": "Abandoned negotiations",
  "Opportunités actives": "Active opportunities",
  "Ouvrir la fiche": "Open profile",
  "Ouvrir le plan de carrière de": "Open the career plan of",
  "Pic d'activité": "Activity peak",
  "Plus longue inactivité": "Longest inactivity",
  "Prochaine facture complète": "Next full invoice",
  "Prochaine relance :": "Next follow-up:",
  "Refusées": "Rejected",
  "Relance envoyée ?": "Follow-up sent?",
  "Relances envoyées": "Follow-ups sent",
  "Remplacés": "Replaced",
  "Réduire les filtres": "Collapse filters",
  "Révisions moy. / contrat": "Avg. revisions / contract",
  "Soyez notifié par email lors d'actions importantes sur votre compte.": "Get notified by email for important actions on your account.",
  "Tous les postes": "All positions",
  "Valeur estimée": "Estimated value",
  "Versions moy. / contrat": "Avg. versions / contract",
  "Vérification par email": "Email verification",
  "Vérifier et activer": "Verify and enable",
  "cette semaine": "this week",
  "contact(s) importé(s)": "contact(s) imported",
  "documents": "documents",
  "documents ajoutés": "documents added",
  "déjà existant(s)": "already existing",
  "filtre actif": "active filter",
  "filtres actifs": "active filters",
    "restant": "remaining",
  "restants": "remaining",
  "À propos de": "About",
  "Événements d'audit": "Audit events",
  "à": "at",
  "à l'instant": "just now",
  "étapes complétées sur": "steps completed out of",
  "− Moins de détails": "− Fewer details",
  "Qui donne l'avis ?": "Who gives the feedback?",
  "Qui donne l'avis ? (athlète, représentant légal, famille…)": "Who gives the feedback? (athlete, legal representative, family…)",
  "Validé par l'athlète": "Validated by the athlete",
  "Validé par l'athlète et son représentant légal": "Validated by the athlete and their legal representative",
  "Validé par": "Validated by",
  "Validé par (représentant légal / famille)": "Validated by (legal representative / family)",
  "Projet validé": "Project validated",
  "Projet enregistré": "Project saved",
  "Valider le projet": "Validate the project",
  "Offre enregistrée": "Offer saved",
  "Offre mise à jour": "Offer updated",
  "Modifier l'offre": "Edit offer",
  "Enregistrer une offre": "Record an offer",
  "Actions sur l'objectif": "Goal actions", "Actions sur l'opportunité": "Opportunity actions", "Actions sur le jalon": "Milestone actions",
  "Activer maintenant": "Activate now", "Activez maintenant pour ne pas perdre l'accès.": "Activate now to keep access.",
  "Actualiser le calendrier": "Refresh calendar", "Afficher la valeur marchande et le salaire": "Show market value and salary",
  "Ajouter aux favoris": "Add to favorites", "Ajout…": "Adding…", "Annulation programmée": "Cancellation scheduled",
  "Aucun athlète": "No athlete", "Aucun club": "No club", "Aucun rappel": "No reminder",
  "Aucun événement à venir.": "No upcoming event.", "Aucune compétition": "No competition", "Aucune relance": "No follow-up",
  "Bonne dynamique": "Good momentum", "Career readiness : données insuffisantes": "Career readiness: insufficient data",
  "Catégorie de l'objectif": "Goal category", "Changez d'offre à tout moment": "Change plans at any time",
  "Choisir": "Choose", "Choisir un fichier": "Choose a file", "Choisissez d'abord un sport": "Choose a sport first",
  "Confiance": "Confidence", "Créer l'événement": "Create event", "Créer le club": "Create club",
  "Créer le plan de carrière": "Create the career plan", "Créer quand même ?": "Create anyway?",
  "Demande envoyée à": "Request sent to", "Document complété à": "Document completed at",
  "Durée, itérations et taux de réussite de vos négociations.": "Duration, iterations and success rate of your negotiations.",
  "Dynamique régulière": "Steady momentum", "Dynamique à relancer": "Momentum to revive",
  "Déplier": "Expand", "Déplier l'activité": "Expand activity", "Détecté depuis :": "Detected from:",
  "Effacer le filtre": "Clear filter", "Enregistrer le plan": "Save plan", "Erreur lors de la création du club": "Error creating the club",
  "Essai en cours": "Trial in progress", "Expiré il y a": "Expired", 
  "Fais un brief synthétique de mon portefeuille : actions urgentes, contrats à renouveler, opportunités et relations à surveiller": "Give me a concise brief of my portfolio: urgent actions, contracts to renew, opportunities and relationships to watch",
  "Graphique :": "Chart:", "Génère un plan d'action opérationnel": "Generate an operational action plan", "Générée": "Generated",
  "Import CSV/Excel avec assistance IA": "CSV/Excel import with AI assistance",
  "Impossible d'ajouter l'objectif": "Unable to add the goal", "Impossible d'ajouter l'opportunité": "Unable to add the opportunity",
  "Impossible d'ajouter le jalon": "Unable to add the milestone", "Impossible de créer l'événement. Réessayez.": "Unable to create the event. Try again.",
  "Impossible de créer le plan d'action.": "Unable to create the action plan.", "Impossible de créer le plan de carrière": "Unable to create the career plan",
  "Impossible de générer le PDF": "Unable to generate the PDF", "Impossible de préparer la réunion": "Unable to prepare the meeting",
  "Jamais relancé": "Never followed up", "Joueurs illimités, pipeline complet": "Unlimited players, full pipeline",
  "L'espace de travail complet pour gérer vos athlètes, opportunités, documents et opérations.": "The complete workspace to manage your athletes, opportunities, documents and operations.",
  "La date de début est requise.": "The start date is required.", "La fin doit être postérieure au début.": "The end must be after the start.",
  "La ville est obligatoire.": "The city is required.", "Le contact principal est obligatoire.": "The main contact is required.",
  "Le contenu est requis": "Content is required", "Le nom du club est obligatoire.": "The club name is required.",
  "Le nom est requis": "Name is required", "Le titre est requis.": "Title is required.",
  "Mettez à jour votre paiement": "Update your payment", "Mises à jour continues, support prioritaire": "Continuous updates, priority support",
  "Modifier l'événement": "Edit event", "Mémoire club + historique d'interactions": "Club memory + interaction history",
  "Négociation renouvellement": "Renewal negotiation", "Objectif archivé": "Goal archived", "Objectif atteint": "Goal achieved",
  "Objectif terminé 🎉": "Goal completed 🎉", "Offre actuelle": "Current plan",
  "Opportunité concrétisée 🎉": "Opportunity realized 🎉", "Opportunité écartée": "Opportunity dismissed",
  "Ouvrir le menu": "Open menu", "PDF généré": "PDF generated", "Paiement sécurisé": "Secure payment",
  "Paiement échoué": "Payment failed", "Paiements traités par Stripe": "Payments processed by Stripe",
  "Partir des suggestions": "Start from suggestions", "Payée": "Paid", "Plan de carrière généré": "Career plan generated",
  "Plus que": "Only", "Point de vigilance ignoré": "Watch point dismissed", "Point de vigilance réactivé": "Watch point reopened",
  "Point de vigilance résolu": "Watch point resolved", "Potentiel": "Potential", "Priorise ces éléments :": "Prioritize these items:",
  "Prochains événements": "Upcoming events", "Prépare un rendez-vous :": "Prepare a meeting:",
  "Rechercher un pays…": "Search a country…", "Rechercher…": "Search…", "Replier": "Collapse", "Replier l'activité": "Collapse activity",
  "Responsable administratif": "Administrative manager", "Responsable juridique": "Legal manager",
  "Responsable médical": "Medical manager", "Responsable partenariats": "Partnerships manager",
  "Retirer des favoris": "Remove from favorites", "Rien à signaler": "Nothing to report",
  "Réactivez l'écriture et gardez vos données accessibles.": "Reactivate editing and keep your data accessible.",
  "Résilier votre abonnement ? Vous gardez l'accès jusqu'à la fin de la période en cours.": "Cancel your subscription? You keep access until the end of the current period.",
  "Résiliez à tout moment": "Cancel anytime", "Résolu": "Resolved", "Résume :": "Summarize:",
  "Stats renseignées": "Stats filled in",
  "Suggestions générées à partir des informations disponibles — vérifiez et modifiez avant d'enregistrer.": "Suggestions generated from the available information — review and edit before saving.",
  "Sélectionnez un pays.": "Select a country.", "Sélectionnez un sport": "Select a sport", "Sélectionnez un sport.": "Select a sport.",
  "Sélectionnez une compétition compatible avec ce sport.": "Select a competition compatible with this sport.",
  "Tarification personnalisée": "Custom pricing", "Toutes les fonctionnalités, 129 €/mois. Il vous reste": "All features, €129/month. You have",
  "Télécharger la facture": "Download invoice", "Un club similaire existe déjà :": "A similar club already exists:",
  "Votre essai est terminé": "Your trial has ended", "Vérifier et ajuster le plan suggéré": "Review and adjust the suggested plan",
  "actif": "active", "actifs": "active", "action créée": "action created", "actions créées": "actions created",
  "ajouté — empreinte SHA-256 calculée": "added — SHA-256 fingerprint computed",
  "champ détecté": "field detected", "champs détectés": "fields detected", "création → signature": "creation → signature",
  "dans le fichier": "in the file", "et ajouté aux Documents": "and added to Documents",
  "et ajouté aux documents de l'athlète": "and added to the athlete's documents",
  "ignorée": "skipped", "ignorées": "skipped", "jour d'essai": "trial day", "jour d'essai.": "trial day.",
  "jour sans relance": "day without follow-up", "jours d'essai": "trial days", "jours d'essai.": "trial days.",
  "jours pour essayer": "days to try", "jours sans relance": "days without follow-up",
  "point à traiter": "point to address", "points sur la période sélectionnée": "points over the selected period",
  "points à traiter": "points to address", "pour": "for", "signés vs période préc.": "signed vs prev. period",
  "terminé": "completed", "terminés": "completed", "Échouée": "Failed", "Échéance du jalon": "Milestone deadline",
  "État actuel de la qualité de vos contrats (indépendant de la période sélectionnée).": "Current state of your contract quality (independent of the selected period).",
  "Évolutif": "Scalable", "Événement modifié": "Event updated", "Événements": "Events",
  "événement": "event", "événements": "events",
  "Revue de carrière": "Career review", "Point contractuel": "Contract check-in", "Préparation mercato": "Transfer-window preparation",
  "Rendez-vous club": "Club meeting", "Point famille / entourage": "Family / entourage check-in",
  "Jan.": "Jan.", "Fév.": "Feb.", "Avr.": "Apr.", "Mai": "May", "Juin": "Jun.", "Juil.": "Jul.", "Août": "Aug.", "Déc.": "Dec.",
  "janv.": "Jan", "févr.": "Feb", "avr.": "Apr", "mai": "May", "juin": "Jun", "juil.": "Jul", "août": "Aug",
  "sept.": "Sep", "oct.": "Oct", "nov.": "Nov", "déc.": "Dec", "mars": "Mar",
  "Bonjour": "Hello",
  "Je me permets de vous contacter au sujet de": "I am reaching out regarding",
  "Je serais ravi d'échanger avec vous sur son projet sportif.": "I would be delighted to discuss their sporting project with you.",
  "Bien cordialement": "Best regards",
  "Au sujet de": "Regarding",
  "1 heure avant": "1 hour before", "15 minutes avant": "15 minutes before", "24 heures avant": "24 hours before",
  "Aperçu indisponible": "Preview unavailable", "Avancement": "Progress",
  "Avec quelle efficacité vos contrats deviennent-ils signés ?": "How efficiently do your contracts get signed?",
  "Basse": "Low", "Calculé par Stripe": "Calculated by Stripe", "Changer d'offre": "Change plan",
  "Conformité Stripe · TVA gérée automatiquement": "Stripe compliance · VAT handled automatically",
  "Deal le plus rapide": "Fastest deal", "Document principal": "Main document", "Essai jusqu'au": "Trial until",
  "Facturation": "Billing", "Fait": "Done", "Fin de la période en cours": "End of the current period",
  "Horodatage": "Timestamp", "Immédiate après confirmation": "Immediate after confirmation",
  "Impossible de démarrer le paiement": "Unable to start the payment", "Indicateur": "Indicator",
  "Journal d'audit actif": "Audit log active", "Journal d'audit immuable": "Immutable audit log",
  "Le changement n'a pas pu être appliqué": "The change could not be applied", "Lieu": "Location",
  "Mode démo : aucun paiement réel ne sera effectué.": "Demo mode: no real payment will be made.",
  "Mois": "Month", "Offres Athlynx": "Athlynx plans",
  "Pas encore assez d'historique pour calculer votre score.": "Not enough history yet to compute your score.",
  "Plan actuel": "Current plan", "Rappel": "Reminder", "Suivi manuel": "Manual tracking", "Sur devis": "On quote",
  "Tactique": "Tactical", "Urgente": "Urgent", "Valeur": "Value", "Variation": "Change",
  "Voir tous les dossiers": "View all cases", "Votre demande a bien été transmise.": "Your request has been sent.",
  "notre athlète": "our athlete", "par mois": "per month", "signés après ≥ 2 demandes": "signed after ≥ 2 requests",
  "sur devis": "on quote", "À l'activation de l'abonnement": "When the subscription is activated",
  "Évolution de votre production contractuelle sur la période.": "Trend of your contract production over the period.",
  "Dans": "In",
  "Pour commencer à structurer votre activité.": "To start structuring your activity.",
  "Gestion essentielle du portefeuille et exports standards.": "Essential portfolio management and standard exports.",
  "L'offre complète pour les agents sportifs.": "The complete plan for sports agents.",
  "IA, fiches professionnelles, plans de carrière et analyses avancées.": "AI, professional sheets, career plans and advanced analytics.",
  "Pour les agences établies et les usages avancés.": "For established agencies and advanced usage.",
  "Branding agence, volumes supérieurs, rapports et support prioritaire.": "Agency branding, higher volumes, reports and priority support.",
  "Une solution sur mesure pour les grandes structures.": "A tailor-made solution for large organizations.",
  "Sécurité, intégrations, migration et accompagnement dédié.": "Security, integrations, migration and dedicated support.",
  "Moyen de paiement": "Payment method", "Ajouter un moyen de paiement": "Add a payment method",
  "Paiements sécurisés par Stripe": "Payments secured by Stripe",
  "Comparer les offres en détail": "Compare plans in detail",
  "Un chiffre": "One digit", "Expire dans": "Expires in",
  "Création": "Creation", "Envoyé pour signature": "Sent for signature", "Signatures en cours": "Signatures in progress",
  "Signé par toutes les parties": "Signed by all parties", "Contrat actif": "Active contract",
  "Contrepartie": "Counterparty", "Montant": "Amount", "Fin": "End", "Titre": "Title",
  "Préparation du renouvellement dans": "Renewal preparation in", "Préparation dans": "Preparation in",
  "Mot de passe": "Password", "Mot de passe actuel": "Current password",
  "Fuseau horaire": "Time zone", "Langue": "Language", "Membre depuis": "Member since",
  "Essai gratuit": "Free trial", "Double authentification": "Two-factor authentication",
  "activée": "enabled", "non activée": "not enabled",
  // Phase 2 final sweep
  "Événement marqué comme terminé.": "Event marked as done.", "Événement réouvert.": "Event reopened.",
  "Sujets urgents": "Urgent topics", "Détail du jour": "Day detail", "Sélectionnez un jour": "Select a day",
  "Aucun événement": "No events", "événement": "event", "événements": "events",
  "Actions": "Actions", "Relation": "Relationship", "Saison": "Season", "Fichier": "File",
  "Afficher le mot de passe": "Show password", "Axes de collaboration": "Collaboration areas",
  "Ex. Atteindre 80% de temps de jeu": "E.g. Reach 80% playing time", "Heure de fin": "End time",
  "Monter": "Move up", "Notes libres sur le joueur…": "Free notes about the player…",
  "Performance de signature": "Signature performance", "Points de vigilance": "Watch points",
  "Progression mensuelle": "Monthly progress", "Style de jeu": "Playing style",
  "Titre de l'action": "Action title", "Titre de l'objectif": "Goal title", "Titre de l'objectif…": "Goal title…",
  "Afficher :": "Show:",
  "Mis à jour à": "Updated at", "Actualisation…": "Refreshing…", "Actualiser": "Refresh",
  "relance aujourd'hui": "follow-up today", "relances aujourd'hui": "follow-ups today",
  "deadline contrat demain": "contract deadline tomorrow", "deadlines contrat demain": "contract deadlines tomorrow",
  "jamais": "never", "Exporter l'audit (CSV)": "Export audit (CSV)",
  "Funnel de conversion": "Conversion funnel", "Voir le pipeline complet": "View full pipeline",
  "Taux de conversion": "Conversion rate", "Dossiers en ralentissement": "Stalled cases",
  "jours dans l'étape": "days in stage", "Dern. interaction :": "Last interaction:",
  "Relance appel": "Follow-up call", "Relance email": "Follow-up email",
  "Interactions (30 j)": "Interactions (30 d)", "Comprendre mon score": "Understand my score",
  "Rappels": "Reminders", "Renouvellement le": "Renews on", "Renouvellements": "Renewals",
  "Renouvellements critiques": "Critical renewals", "Pas de base de comparaison": "No comparison baseline",
  "Sans comparaison": "No comparison", "Comparer :": "Compare:",
  "Voir les": "View the", "relances en retard": "overdue follow-ups",
  // Billing Phase 1 — plans, quotas & usage
  "Mensuel": "Monthly", "Annuel": "Annual", "2 mois offerts": "2 months free",
  "Facturé": "Billed", "/ an": "/ yr", "Économisez": "Save",
  "Utilisation": "Usage", "Capacités consommées sur votre offre actuelle.": "Capacity used on your current plan.",
  "Joueurs actifs": "Active players", "Stockage documents": "Document storage", "Sièges utilisateurs": "User seats",
  "Illimité": "Unlimited", "Go": "GB", "Mo": "MB", "Ko": "KB",
  "Capacité atteinte — passez à une offre supérieure pour continuer d'ajouter.": "Capacity reached — upgrade your plan to keep adding.",
  "Vous approchez de la limite de votre offre.": "You are approaching your plan limit.",
  "Limite de joueurs atteinte pour votre offre": "Player limit reached for your plan",
  "Limite de stockage documents atteinte pour votre offre.": "Document storage limit reached for your plan.",
  "Limite d'utilisateurs atteinte pour votre offre.": "User limit reached for your plan.",
  "Passez à une offre supérieure pour augmenter vos capacités.": "Upgrade to a higher plan to increase your capacity.",
  "Cette fonctionnalité n'est pas incluse dans votre offre.": "This feature is not included in your plan.",
  "Abonnement inactif.": "Subscription inactive.",
  "Certaines lignes n'ont pas été importées.": "Some rows were not imported.",
  "Jusqu'à 15 joueurs actifs · 1 utilisateur": "Up to 15 active players · 1 user",
  "Suivi des contrats & alertes d'expiration": "Contract tracking & expiration alerts",
  "Documents — 2 Go de stockage": "Documents — 2 GB storage",
  "Athlynx Intelligence Essentiel": "Athlynx Intelligence Essential",
  "Jusqu'à 50 joueurs actifs": "Up to 50 active players",
  "Gestion avancée des contrats & négociation": "Advanced contract management & negotiation",
  "Athlynx Intelligence Avancé (résumés, analyses)": "Athlynx Intelligence Advanced (summaries, analyses)",
  "Documents — 10 Go de stockage": "Documents — 10 GB storage",
  "Jusqu'à 100 joueurs actifs": "Up to 100 active players",
  "3 comptes utilisateurs": "3 user accounts",
  "Documents — 30 Go de stockage": "Documents — 30 GB storage",
  "Onboarding accompagné · support premium": "Guided onboarding · premium support",
  "Capacités incluses": "Included capacities", "Comptes utilisateurs": "User accounts",
  "Sur mesure": "Custom",
  // Plan catalogue — features, comparison groups & cells
  "10 Go": "10 GB", "2 Go": "2 GB", "30 Go": "30 GB",
  "Branding complet de l'agence": "Full agency branding", "Branding de l'agence": "Agency branding",
  "Builder PDF complet": "Full PDF builder", "Builder premium & aperçu A4": "Premium builder & A4 preview",
  "Calendrier & rappels": "Calendar & reminders", "Calendrier, relances et rappels": "Calendar, follow-ups & reminders",
  "Career Readiness & Velocity": "Career Readiness & Velocity",
  "Clubs & contacts": "Clubs & contacts", "Clubs, calendrier & relances": "Clubs, calendar & follow-ups",
  "Collaboration d'agence": "Agency collaboration", "Collaboration d'agence — bientôt disponible": "Agency collaboration — coming soon",
  "Dashboard agent": "Agent dashboard", "Dashboard agent & pipeline complet": "Agent dashboard & full pipeline",
  "Destination QR configurable": "Configurable QR destination",
  "Documents & alertes d'expiration": "Documents & expiration alerts",
  "Dédié": "Dedicated", "Détection de doublons": "Duplicate detection",
  "Export PDF standard": "Standard PDF export", "Export PDF standard · support email": "Standard PDF export · email support",
  "Exports premium personnalisés": "Custom premium exports",
  "Fiche de négociation premium (aperçu A4)": "Premium negotiation sheet (A4 preview)",
  "Fiche de négociation standard": "Standard negotiation sheet",
  "Filtres & recherche avancée": "Advanced filters & search",
  "Gestion des athlètes": "Athlete management", "Gestion des athlètes, clubs et contacts": "Athletes, clubs & contacts management",
  "Historique de versions PDF": "PDF version history",
  "Import CSV / Excel": "CSV / Excel import", "Import intelligent": "Smart import", "Imports": "Imports",
  "Imports CSV / Excel standards": "Standard CSV / Excel imports",
  "Imports intelligents & détection de doublons": "Smart imports & duplicate detection",
  "Intelligence artificielle": "Artificial intelligence",
  "Intégrations & migration de données": "Integrations & data migration",
  "Migration accompagnée": "Assisted migration", "Onboarding": "Onboarding", "Onboarding dédié": "Dedicated onboarding",
  "Personnalisation complète": "Full customization",
  "Pipeline & opportunités": "Pipeline & opportunities", "Pipeline complet (Kanban)": "Full pipeline (Kanban)",
  "Plan de carrière basique": "Basic career plan", "Plan de carrière complet + Builder PDF": "Full career plan + PDF builder",
  "Premium": "Premium", "Prioritaire": "Priority", "Profils multi-sport": "Multi-sport profiles",
  "Quotas IA": "AI allowances", "Quotas IA & générations étendus": "Extended AI allowances & generations",
  "Rapports & statistiques avancés": "Advanced reports & statistics",
  "Rapports avancés · support prioritaire": "Advanced reports · priority support",
  "Rapports, branding & exports": "Reports, branding & exports",
  "Relances en 1 clic": "One-click follow-ups", "Résumés & reformulations IA": "AI summaries & rewrites",
  "SLA & support dédié": "SLA & dedicated support", "Standard": "Standard", "Support": "Support",
  "Support & accompagnement": "Support & guidance",
  "Sécurité & conformité avancées": "Advanced security & compliance", "Sécurité & intégrations": "Security & integrations",
  "Tout Agency, plus :": "Everything in Agency, plus:", "Tout Pro, plus :": "Everything in Pro, plus:",
  "Tout Starter, plus :": "Everything in Starter, plus:", "Étendus": "Extended",
  "Retour à la facturation": "Back to billing", "Nous contacter": "Contact us",
  "Essai": "Trial", "jours": "days",
  "Passage à": "Switching to", "Gérer l'abonnement": "Manage subscription", "le": "on",
  "Mettre à jour le paiement": "Update payment", "Réactiver l'abonnement": "Reactivate subscription",
  "Titre / besoin": "Title / need", "Enregistrement impossible": "Could not save",
  "Opportunité modifiée": "Opportunity updated", "Opportunité créée": "Opportunity created",
  "Créer l'opportunité": "Create opportunity", "Ex. Recherche ailier U19": "E.g. Looking for a U19 winger",
  "Valeur (€)": "Value (€)", "Contact responsable": "Contact in charge",
  "Aucun résultat pour ces filtres.": "No results for these filters.",
  "Aucune opportunité pour ce filtre.": "No opportunities for this filter.",
  "Aucune opportunité pour cette équipe.": "No opportunities for this team.",
  "Aucun document dans cette catégorie.": "No documents in this category.",
  "Aucun document lié à cette équipe.": "No documents linked to this team.",
  "Supprimer ce joueur définitivement ?": "Delete this player permanently?",
  "Impossible de démarrer l'abonnement": "Could not start the subscription",
  "Mode démo : aucun paiement réel ne sera effectué.": "Demo mode: no real payment will be made.",
  "Analyse de votre fichier…": "Analyzing your file…", "Déposer votre fichier ici": "Drop your file here",
  "L'IA détecte automatiquement vos colonnes": "AI automatically detects your columns",
  "ou cliquez pour parcourir · CSV, XLSX jusqu'à 10 Mo": "or click to browse · CSV, XLSX up to 10 MB",
  "Nom Prénom": "Full name", "Compression échouée": "Compression failed",
  "Votre essai est expiré — abonnez-vous pour réactiver l'écriture": "Your trial has expired — subscribe to re-enable editing",
  "Paiement en retard — mettez à jour votre carte": "Payment past due — update your card",
  "jours d'essai restants": "trial days left", "jour d'essai restant": "trial day left",
  "champ reconnu avec succès": "field recognized successfully", "champs reconnus avec succès": "fields recognized successfully",
  "champ nécessite votre attention": "field needs your attention", "champs nécessitent votre attention": "fields need your attention",
  "champ nécessite encore votre attention — vous pouvez tout de même générer la fiche.": "field still needs your attention — you can still generate the sheet.",
  "champs nécessitent encore votre attention — vous pouvez tout de même générer la fiche.": "fields still need your attention — you can still generate the sheet.",
  "traitée (concrétisée ou écartée)": "processed (completed or dismissed)", "traitées (concrétisées ou écartées)": "processed (completed or dismissed)",
  "événement enregistré": "event recorded", "événements enregistrés": "events recorded",
  "action ajoutée à vos relances et à votre agenda.": "action added to your follow-ups and calendar.",
  "actions ajoutées à vos relances et à votre agenda.": "actions added to your follow-ups and calendar.",
  "action sélectionnée": "action selected", "actions sélectionnées": "actions selected",
  "contrat actif évalué sur 5 facteurs pondérés.": "active contract scored across 5 weighted factors.",
  "contrats actifs évalués sur 5 facteurs pondérés.": "active contracts scored across 5 weighted factors.",
  "athlète lié": "athlete linked", "athlètes liés": "athletes linked",
  "Âge moyen :": "Avg age:", "fin(s) de contrat": "contract end(s)",
  "activité supplémentaire": "more activity", "activités supplémentaires": "more activities",
  "doublon détecté": "duplicate detected", "doublons détectés": "duplicates detected",
  "décision prise": "decision made", "décisions prises": "decisions made",
  "Une action sera demandée par défaut (Ignorer).": "A default action will be applied (Skip).",
  "colonne associée": "column mapped", "colonnes associées": "columns mapped",
  "joueur ajouté à votre pipeline": "player added to your pipeline", "joueurs ajoutés à votre pipeline": "players added to your pipeline",
  "joueur prêt à être importé": "player ready to import", "joueurs prêts à être importés": "players ready to import",
  "autre joueur non affiché": "other player not shown", "autres joueurs non affichés": "other players not shown",
  "Bonjour,\n\nProposons un créneau pour notre visio.\n\nCordialement.": "Hello,\n\nLet's find a slot for our video call.\n\nBest regards.",
  "SMS depuis fiche": "SMS from profile", "Email depuis fiche": "Email from profile", "WhatsApp depuis fiche": "WhatsApp from profile",
  "Appel rapide depuis calendrier": "Quick call from calendar", "Email rapide depuis calendrier": "Quick email from calendar",
  "Visio depuis calendrier": "Video call from calendar", "Appel rapide depuis activité": "Quick call from activity",
  "Email rapide depuis activité": "Quick email from activity", "WhatsApp rapide depuis activité": "Quick WhatsApp from activity",
  // Workspaces & collaboration (Phase 2/3)
  "Workspace": "Workspace",
  "Sièges": "Seats",
  "Plan, statut et quotas personnalisés (Enterprise).": "Plan, status and custom quotas (Enterprise).",
  "Laisser vide = limites du plan. Les quotas personnalisés priment sur le plan.": "Leave empty = plan limits. Custom quotas override the plan.",
  "Outil interne — plans, quotas personnalisés et statuts.": "Internal tool — plans, custom quotas and statuses.",
  "Équipe & Workspace": "Team & Workspace",
  "Changer de workspace": "Switch workspace",
  "Changement de workspace impossible": "Could not switch workspace",
  "Espace personnel": "Personal workspace",
  "Propriétaire": "Owner",
  "Administrateur": "Admin",
  "Membre": "Member",
  "Membres": "Members",
  "vous": "you",
  "Invitations en attente": "Pending invitations",
  "invitation(s) en attente": "pending invitation(s)",
  "Inviter un membre": "Invite a member",
  "Le collaborateur recevra un lien d'invitation valable 7 jours.": "The collaborator will receive an invitation link valid for 7 days.",
  "Email du collaborateur": "Collaborator email",
  "Envoyer l'invitation": "Send invitation",
  "Invitation créée — partagez le lien.": "Invitation created — share the link.",
  "Email d'invitation envoyé.": "Invitation email sent.",
  "Invitation impossible": "Could not send invitation",
  "Invitation révoquée": "Invitation revoked",
  "Renvoyer et copier le lien": "Resend and copy link",
  "Révoquer": "Revoke",
  "Copier le lien": "Copy link",
  "Lien copié": "Link copied",
  "Membre retiré": "Member removed",
  "Retirer du workspace": "Remove from workspace",
  "Quitter le workspace": "Leave workspace",
  "Workspace renommé": "Workspace renamed",
  "La collaboration multi-utilisateurs fait partie de l'offre Athlynx Agency.": "Multi-user collaboration is part of the Athlynx Agency plan.",
  "Passer à Agency": "Upgrade to Agency",
  "Seul le propriétaire du workspace gère l'abonnement.": "Only the workspace owner manages the subscription.",
  "Vous consultez la facturation du workspace": "You are viewing the billing of workspace",
  "Invitation introuvable": "Invitation not found",
  "Ce lien d'invitation est invalide. Demandez au propriétaire du workspace de vous renvoyer une invitation.": "This invitation link is invalid. Ask the workspace owner to send you a new invitation.",
  "vous invite à rejoindre ce workspace.": "invites you to join this workspace.",
  "Vous êtes invité à rejoindre ce workspace.": "You are invited to join this workspace.",
  "Cette invitation a expiré ou a été révoquée.": "This invitation has expired or was revoked.",
  "Cette invitation est destinée à": "This invitation is intended for",
  "Vous êtes connecté avec": "You are signed in as",
  "Accepter l'invitation": "Accept the invitation",
  "Acceptation impossible": "Could not accept the invitation",
  "Invitation acceptée — bienvenue !": "Invitation accepted — welcome!",
  "Plus tard": "Later",
  // Member Attribution (Team Activity)
  "Activité de l'équipe": "Team Activity",
  "Tous les membres": "All members",
  "Système Athlynx": "Athlynx System",
  "Membre inconnu": "Unknown member",
  "Relances terminées": "Completed follow-ups",
  "Athlètes sans activité récente": "Athletes with no recent activity",
  "actions": "actions",
  "Aucune activité pour ces filtres.": "No activity for these filters.",
  "Voir plus": "Show more",
  "Équipe": "Team",
  "Collaboration d'agence — 3 sièges utilisateurs": "Agency collaboration — 3 user seats",
};


// Module-level FR→EN translator for non-hook call sites (lib maps, handlers,
// shared primitives). Reads the persisted language at call time — same pattern
// as lib/utils formatters. Components with useLang() should prefer tf().
export function translateFr(s) {
  try { if (localStorage.getItem("athlynx_lang") === "en") return FR_EN[s] ?? s; } catch { /* ssr/private */ }
  return s;
}

export function LanguageProvider({ children }) {
  const auth = useAuth();
  const user = auth?.user || null;
  const [lang, setLangState] = useState(initialLanguage);
  const setLang = useCallback((l) => {
    const n = normalizeLanguage(l);
    if (!n) return;
    setLangState(n);
    let changed = false;
    try {
      changed = localStorage.getItem("athlynx_lang") !== n;
      localStorage.setItem("athlynx_lang", n);
    } catch { /* private mode */ }
    const done = user ? api.patch("/account/preferences", { language: n }).catch(() => {}) : Promise.resolve();
    // Full reload so every module-level label map re-evaluates in the new language.
    if (changed) done.then(() => { try { window.location.reload(); } catch { /* ssr */ } });
  }, [user]);
  const t = useCallback((key, vars) => {
    let s = MESSAGES[lang]?.[key] ?? MESSAGES.fr[key] ?? key;
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
    return s;
  }, [lang]);
  useEffect(() => {
    // Authenticated server preference is authoritative — hydrate from the already-loaded
    // /auth/me user (no extra request, no 401 on public routes such as Login).
    if (!user) return;
    const l = normalizeLanguage(user?.preferences?.language) || normalizeLanguage(user?.preferred_language);
    if (l) {
      setLangState(l);
      try { localStorage.setItem("athlynx_lang", l); } catch { /* private mode */ }
    }
  }, [user]);
  const locale = lang === "en" ? "en-GB" : "fr-FR";
  useEffect(() => {
    try {
      document.documentElement.lang = lang;
      localStorage.setItem("athlynx_lang", lang);
    } catch { /* ssr-safe */ }
  }, [lang]);
  const tf = useCallback((s) => (lang === "en" ? (FR_EN[s] ?? s) : s), [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tf, locale }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLang = () => useContext(LanguageContext);
